/* ============================================
   ROOM DESIGNER — Save/Load & Designs Module
   Asset file: custom-room-designer-storage.js

   Handles auto-save (session cache), file-based
   save/open, recovery prompt, migration from
   legacy localStorage slots, and the designs panel UI.
   Loaded before custom-room-designer.js via <script defer>.
   Instantiated by the RoomDesigner constructor.
   ============================================ */

// CUSTOM_GYMLAYOUT_BY_CLAUDE (Ryan 2026-10-10): a loaded design is fitted to the screen minus 10%.
const LOAD_FIT_INSET = 0.9;

class RoomDesignerStorage {
  constructor(rd) {
    this.rd = rd;
    this._lastAutoSaveTime = null;
    this._autoSaveDebounce = null;
    this._autoSaveInterval = null;
    this._autoSaveIndicatorInterval = null;
    this._isDirty = false;

    // Install methods on the RD instance so existing call sites
    // (this._doAutoSave(), this._serializeDesign(), etc.) keep working.
    // The overlay module wraps _serializeDesign / _loadDesignData — wrapping
    // these installed functions works correctly.
    this._installMethods();

    // Setup UI and start services
    this._setupDesignsPanel();
    /* CUSTOM_ROOMDESIGNER_BY_CLAUDE (Ryan 2026-05-21) — Highest-priority
       intercept: if ?designId= is in the URL, this is an explicit
       "open THIS specific design" request (Quote Dashboard LINKED FILES
       cards target /pages/gym-layout-designer?designId=<id>). Take the
       direct-load path and SKIP the recovery prompt + share-link checks
       entirely so a stale autosave from another tab (or any other edge
       case where _portalMode misreads) doesn't gate the rep behind a
       "You have an unsaved design from just now" dialog.

       Clear all autosave artefacts (including LKG + meta) so the prompt
       can't fire later from any other code path while this design is
       loading.

       Auth race fix (2026-05-21 follow-up): _loadFromPortal silently
       returns when sessionStorage.rep_portal_session isn't populated
       yet. On a fresh tab, RoomDesignerStorage runs before the rep
       portal auth has finished hydrating, so the immediate load would
       silent-no-op and leave the canvas blank. Wait for the
       `rep-portal:authenticated` window event before firing the load
       — matches the pattern Workstation Boot uses for the same race. */
    const _urlDesignId = new URLSearchParams(window.location.search).get('designId');
    /* CUSTOM_QUOTELAYOUTSWAP_BY_CLAUDE (Ryan 2026-10-10) — `embed=1`: the Quote Workstation runs this page
       hidden inside itself to update a quote version's layout. It must not touch the rep's own autosave
       (another tab may hold unsaved work), arm the leave guards, or autosave on a timer. */
    this._embed = new URLSearchParams(window.location.search).get('embed') === '1';
    if (_urlDesignId && /^[A-Za-z0-9]{8}$/.test(_urlDesignId)) {
      const _prefix = this._getRepPrefix();
      if (!this._embed) {
        localStorage.removeItem(_prefix + 'rd_autosave');
        localStorage.removeItem(_prefix + 'rd_autosave_time');
        localStorage.removeItem(_prefix + 'rd_autosave_lkg');
        localStorage.removeItem(_prefix + 'rd_autosave_lkg_time');
        localStorage.removeItem(_prefix + 'rd_autosave_meta');
      }
      rd.startNewRoom();
      if (!this._embed) {
        this._startAutoSaveTimer();
        this._setupPageLeaveGuard();
      }
      // Wait for BOTH session-in-sessionStorage AND window.RepPortal.Auth
      // to be ready before triggering the load. _loadFromPortal's
      // _authedFetch goes through RepPortal.Auth.getAccessToken() — if
      // RepPortal hasn't initialized yet, the fetch falls back to a raw
      // sessionStorage token read that doesn't handle refresh, which can
      // 401 silently on an expired-but-refreshable session. Poll every
      // 100ms for up to 5s; covers both event-missed and slow-script-
      // load scenarios.
      const _self = this;
      let _kicked = false;
      const _kick = function() {
        if (_kicked) return;
        _kicked = true;
        _self._loadFromPortal(_urlDesignId);
        // Also refresh the Designs side-panel list so Save/Load menu shows
        // the rep's full design list once auth is up. _loadDesignList is
        // called by _setupDesignsPanel synchronously in the constructor,
        // before auth hydrates — re-call it here to backfill.
        if (typeof _self._loadDesignList === 'function') {
          try { _self._loadDesignList(); } catch (e) {}
        }
      };
      const _isAuthReady = function() {
        return !!(_self._getPortalRepCode()
          && window.RepPortal
          && window.RepPortal.Auth
          && typeof window.RepPortal.Auth.getAccessToken === 'function');
      };
      if (_isAuthReady()) {
        _kick();
      } else {
        // Listen for the authenticated event AND poll as belt-and-suspenders.
        const _onAuth = function() {
          window.removeEventListener('rep-portal:authenticated', _onAuth);
          if (_isAuthReady()) _kick();
        };
        window.addEventListener('rep-portal:authenticated', _onAuth);
        let _pollCount = 0;
        const _poll = setInterval(function() {
          _pollCount++;
          if (_isAuthReady()) {
            clearInterval(_poll);
            window.removeEventListener('rep-portal:authenticated', _onAuth);
            _kick();
          } else if (_pollCount >= 50) {
            // 5s elapsed — give up waiting and try anyway. _loadFromPortal's
            // first guard will silent-return if still not authed, but at
            // least we tried instead of hanging forever.
            clearInterval(_poll);
            window.removeEventListener('rep-portal:authenticated', _onAuth);
            _kick();
          }
        }, 100);
      }
      return;
    }
    // Check for shared design link first (takes priority over auto-save)
    const hasShareLink = window.location.hash && window.location.hash.startsWith('#plan=');
    if (hasShareLink) {
      // Always start a new room so canvas is usable while share link loads
      rd.startNewRoom();
      this._loadFromShareLink().then(loaded => {
        if (!loaded) this._autoLoadDesign();
      });
    } else {
      // Always start a new room — recovery prompt will override if user clicks Resume
      rd.startNewRoom();
      // Portal mode: always open to a clean slate — users open saved designs from the panel
      if (rd._portalMode) {
        const prefix = this._getRepPrefix();
        localStorage.removeItem(prefix + 'rd_autosave');
        localStorage.removeItem(prefix + 'rd_autosave_meta');
        // Clear share/PDF dialog fields so they don't carry over between sessions
        localStorage.removeItem('rd_share_receiver');
        localStorage.removeItem('rd_share_space');
        localStorage.removeItem('rd_share_name');
      } else {
        this._autoLoadDesign();
      }
    }
    this._startAutoSaveTimer();
    this._setupPageLeaveGuard();
  }

  _untitledName() {
    const d = new Date();
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return 'Untitled Design - ' + yyyy + '-' + mm + '-' + dd;
  }

  _getRepPrefix() {
    if (!this.rd._repMode) return '';
    var repId = localStorage.getItem('rd_share_rep_id');
    return repId ? 'rd_rep_' + repId + '_' : '';
  }

  _installMethods() {
    const s = this;
    const rd = this.rd;
    rd._serializeDesign = () => s._serializeDesign();
    rd._loadDesignData = (data) => s._loadDesignData(data);
    rd._triggerAutoSave = () => s._triggerAutoSave();
    rd._doAutoSave = () => s._doAutoSave();
    rd._updateAutoSaveIndicator = () => s._updateAutoSaveIndicator();
    rd._startAutoSaveTimer = () => s._startAutoSaveTimer();
    rd._autoLoadDesign = () => s._autoLoadDesign();
    rd._newDesign = () => s._newDesign();
    rd._saveToFile = () => s._saveToFile();
    rd._openFromFile = (file) => s._openFromFile(file);
    rd._updateDesignNameUI = (name) => s._updateDesignNameUI(name);
    rd._showConfirm = (msg, ok, cb, opts) => s._showConfirm(msg, ok, cb, opts);
    rd._markDirty = () => { s._isDirty = true; };
    rd._setupDesignsPanel = () => s._setupDesignsPanel();
    rd._shareDesign = () => s._shareDesign();
    rd._buildShareUrl = (d, m) => s._buildShareUrl(d, m);
    rd._openShareDialog = (d) => s._openShareDialog(d);
    rd._showShareBanner = (n, m, r) => s._showShareBanner(n, m, r);
  }

  // ── Serialization ─────────────────────────

  _serializeDesign() {
    const rd = this.rd;
    return {
      rooms: JSON.parse(JSON.stringify(rd.rooms)),
      objects: JSON.parse(JSON.stringify(rd.objects)),
      currentRoom: rd.currentRoom ? JSON.parse(JSON.stringify(rd.currentRoom)) : null,
      wallSegmentGroups: JSON.parse(JSON.stringify(rd.wallSegmentGroups)),
      currentWallSegmentGroup: rd.currentWallSegmentGroup ? JSON.parse(JSON.stringify(rd.currentWallSegmentGroup)) : null,
      panX: rd.panX, panY: rd.panY, zoomLevel: rd.zoomLevel,
      measurements: rd._measurements.length > 0 ? JSON.parse(JSON.stringify(rd._measurements)) : undefined,
      measureNextId: rd._measurements.length > 0 ? rd._measureNextId : undefined,
      settings: {
        /* CUSTOM_ROOMDESIGNER_BY_CLAUDE — save the user's PREFERENCE, not the tab's
           version of it. ⚠️ These four are forced by whichever tab you are standing
           on: Draw shows no flooring, Equipment wants a clean view, Customize forces
           the material overlay on, and every non-Draw tab hides wall dimensions. The
           tab stashes what it overrode so it can hand it back — so a plain
           `rd.showSeamLines` here persists THE TAB'S ANSWER, and "I turned seams on,
           saved, and they came back off" is that and nothing more. It depends
           entirely on which tab you happened to be on when you hit Save, which is why
           it looked intermittent.

           The load side was already fixed for this (2a71e97b) in BOTH places that
           wiped the flags — but the save side kept writing the wrong value in, so
           there was nothing correct to restore. _prefFlag reads the stash. */
        showMeasurements: rd._prefFlag('_stashedMeasurements', rd.showMeasurements),
        showRoomLabels: rd.showRoomLabels,
        showGrid: rd.showGrid,
        hollowWalls: rd.hollowWalls,
        hideOverage: rd._prefFlag('_stashedHideOverage', rd.hideOverage),
        showSeamLines: rd._prefFlag('_stashedSeamLines', rd.showSeamLines),
        showMaterialOverlay: rd._prefFlag('_stashedMaterialOverlay', rd.showMaterialOverlay),
        // CUSTOM_ROOMDESIGNER_BY_CLAUDE — everything the Settings drawer can change
        // belongs here. Anything missing silently falls back to whatever the last
        // localStorage value on THIS browser was, which is why a design opened on a
        // different machine came back looking wrong for no visible reason.
        showEquipmentLabels: rd.showEquipmentLabels,
        glassLabels: rd.glassLabels,
        unit: rd.unit,
        // The PDF page picker's ticks. On the DESIGN rather than in localStorage:
        // a selection is room and zone INDICES, which only mean anything for one
        // drawing — a browser-wide memory would apply them to the next plan opened.
        pdfPages: rd._pdfPageSelection || undefined,
        darkMode: rd.darkMode,
        largeUI: rd.largeUI
      },
      // CUSTOM_ROOMDESIGNER_BY_CLAUDE — persist share/PDF dialog fields with the design so they re-populate on load (across devices). null = never saved with this feature; '' = user cleared and saved.
      _meta: {
        receiverName: localStorage.getItem('rd_share_receiver') || '',
        spaceName: localStorage.getItem('rd_share_space') || '',
        // CUSTOM_ROOMDESIGNER_BY_CLAUDE — which page-title mode the rep chose. The
        // NAME was already saved here; the mode never was, so a packet had no way to
        // tell "one name for every page" from "from the drawing" and always did the
        // latter. Rides along with "Use designer layout" — see project-routes.
        pdfNameMode: localStorage.getItem('rd_pdf_name_mode') === 'custom' ? 'custom' : 'drawing'
      }
      // END CUSTOM_ROOMDESIGNER_BY_CLAUDE
    };
  }

  _loadDesignData(data) {
    const rd = this.rd;
    // CUSTOM_ROOMDESIGNER_BY_CLAUDE — restore share/PDF dialog fields from saved design.
    // If the design was saved with this feature, treat _meta as authoritative (including empty strings = explicit clear).
    // If absent (older design pre-feature), wipe localStorage so stale values from a previously-loaded design don't carry over.
    if (data._meta && typeof data._meta === 'object') {
      if (typeof data._meta.receiverName === 'string') localStorage.setItem('rd_share_receiver', data._meta.receiverName);
      if (typeof data._meta.spaceName === 'string') localStorage.setItem('rd_share_space', data._meta.spaceName);
    } else {
      localStorage.removeItem('rd_share_receiver');
      localStorage.removeItem('rd_share_space');
    }
    // END CUSTOM_ROOMDESIGNER_BY_CLAUDE
    rd.rooms = data.rooms || [];
    rd.objects = data.objects || [];
    rd.currentRoom = data.currentRoom || null;
    rd.wallSegmentGroups = data.wallSegmentGroups || [];
    // Discard incomplete wall segment groups — they become ghost lines
    // (visible but not selectable) if restored across sessions
    rd.currentWallSegmentGroup = null;
    if (data.panX != null) rd.panX = data.panX;
    if (data.panY != null) rd.panY = data.panY;
    if (data.zoomLevel != null) rd.zoomLevel = data.zoomLevel;
    // Restore settings if present (backward compatible — older saves won't have this)
    if (data.settings) {
      const s = data.settings;
      if (s.showMeasurements != null) rd.showMeasurements = s.showMeasurements;
      if (s.showRoomLabels != null) rd.showRoomLabels = s.showRoomLabels;
      if (s.showGrid != null) rd.showGrid = s.showGrid;
      if (s.hollowWalls != null) rd.hollowWalls = s.hollowWalls;
      if (s.hideOverage != null) rd.hideOverage = s.hideOverage;
      if (s.showSeamLines != null) rd.showSeamLines = s.showSeamLines;
      if (s.showMaterialOverlay != null) rd.showMaterialOverlay = s.showMaterialOverlay;
      if (s.showEquipmentLabels != null) rd.showEquipmentLabels = s.showEquipmentLabels;
      if (s.glassLabels != null) rd.glassLabels = s.glassLabels;
      if (s.unit != null) rd.unit = s.unit;
      // Taken as a hint, not as truth — the dialog validates every index against the
      // drawing it actually opens over. See the note in _openPdfDialog.
      if (s.pdfPages != null) rd._pdfPageSelection = s.pdfPages;
      if (s.darkMode != null) rd.darkMode = s.darkMode;
      if (s.largeUI != null) rd.largeUI = s.largeUI;
      // ⚠️ _applySettingsUI() is deliberately NOT called here. It used to be, and the
      // Draw-tab adjustment below then changed the flags underneath it — leaving the
      // drawer showing "Floor Seams" lit while the flag was false. The buttons are
      // synced once, at the end of this function, after every flag has settled.
    }
    rd._relinkDoorWallRefs();
    for (const r of rd.rooms) { r._zoneCache = null; r._zoneLayouts = null; }
    rd.undoStack = [];
    rd.redoStack = [];
    rd._placementRedoStack = [];
    rd.selectedWalls = new Set();
    rd.selectedDoors = new Set();
    rd.selectedObjects = new Set();
    rd.currentWall = null;
    rd.hasPlacedFirstPoint = false;
    rd._resetRectState();
    rd._resetFreehandState();
    rd._measurements = data.measurements ? JSON.parse(JSON.stringify(data.measurements)) : [];
    rd._measureNextId = data.measureNextId || (rd._measurements.length > 0 ? Math.max(...rd._measurements.map(m => m.id)) + 1 : 1);
    for (const m of rd._measurements) { if (m.offset == null) m.offset = 0; }
    rd._selectedMeasurements = new Set(); rd._measureStart = null; rd._measureEnd = null;
    rd._measurePhase = 0; rd._measurePendingEnd = null; rd._measureDragging = null;
    // Auto-show measurement lines if the design has any (so they're visible without toggling wall dims)
    if (rd._measurements.length > 0) rd._measurementsVisible = true;
    if (rd._updateMeasureClearBtn) rd._updateMeasureClearBtn();
    // Ensure there's always a currentRoom ready for drawing
    if (!rd.currentRoom) rd.startNewRoom();
    // Pre-load SVGs for any placed equipment (if catalog already populated)
    if (rd.objects && rd.objects.length > 0 && rd._equipmentImages) {
      const placedIds = new Set();
      for (const obj of rd.objects) {
        if (obj.type === 'equipment' && obj.equipmentId) placedIds.add(obj.equipmentId);
      }
      for (const eqId of placedIds) {
        if (rd._equipmentImages[eqId] && !rd._equipmentImages[eqId].loaded) {
          rd._ensureEquipmentLoaded(eqId);
        }
      }
    }
    /* CUSTOM_ROOMDESIGNER_BY_CLAUDE — the Draw tab shows no flooring, and `showOverlay`
       is the switch that does it: _drawFlooringOverlay returns immediately when it is
       false, so nothing flooring-related — strips, seams, textures — can reach the
       canvas either way.

       ⚠️ This block used to ALSO set showSeamLines and showMaterialOverlay to false,
       and that is the whole of "I save with Floor Seams on and it doesn't remember".
       Those two are the user's saved preferences, not tab state; wiping them here beat
       the restore twenty lines above by a few microseconds. Worse, it ran AFTER the UI
       sync, so the drawer sat there showing Floor Seams lit while the flag behind it
       was false — and the tab machinery (_stashedSeamLines, _floorSeamUserOff) then
       made a decision based on the wrong value the moment you opened the Floor tab.

       Both flags are inert on the Draw tab anyway: seams are drawn inside the flooring
       overlay, and the only other thing showSeamLines gates — hiding 'line' dividers —
       is itself scoped to the floor, equipment and customize tabs. */
    const overlay = window.roomDesignerOverlay;
    if (!overlay || !overlay.currentStep || overlay.currentStep === 'draw') {
      if (overlay) overlay.showOverlay = false;
    }
    // Sync the drawer LAST, so a button can never disagree with the flag behind it.
    if (data.settings) this._applySettingsUI();
    rd.render();
    rd.updateMeasurements();
    this._isDirty = false;
    this._fitAfterLoad();
  }

  /* CUSTOM_GYMLAYOUT_BY_CLAUDE (Ryan 2026-10-10) — EVERY design load (a rep's own from All
     Layouts or ?design=, a shared one, an autosave restore, a file import, an edit link)
     lands fitted to the screen minus 10%, not at whatever zoom it was saved with. A frame
     later so the canvas has its size; a customer's view-only link keeps its own fit. A shared
     design fits again once its side panel has closed (overlay, finishing step). */
  _fitAfterLoad() {
    const rd = this.rd;
    requestAnimationFrame(() => {
      if (rd._viewOnly || typeof rd.fitToView !== 'function') return;
      rd.fitToView({ inset: LOAD_FIT_INSET });
    });
  }

  _applySettingsUI() {
    const rd = this.rd;
    const el = document.querySelector('.room-designer');
    // Sync toggle buttons
    document.getElementById('toggleMeasurements').classList.toggle('active', rd.showMeasurements);
    document.getElementById('toggleRoomLabels').classList.toggle('active', rd.showRoomLabels);
    document.getElementById('toggleGrid').classList.toggle('active', rd.showGrid);
    document.getElementById('toggleHollowWalls').classList.toggle('active', rd.hollowWalls);
    document.getElementById('toggleHideOverage').classList.toggle('active', !rd.hideOverage);
    document.getElementById('toggleSeamLines').classList.toggle('active', rd.showSeamLines);
    document.getElementById('toggleMaterialOverlay').classList.toggle('active', rd.showMaterialOverlay);
    const _eqLbl = document.getElementById('toggleEquipmentLabels');
    if (_eqLbl) _eqLbl.classList.toggle('active', rd.showEquipmentLabels);
    document.querySelectorAll('.unit-btn').forEach(b => b.classList.toggle('active', b.dataset.value === rd.unit));
    // Glass/solid lives in the Area Label popover, not the drawer
    document.querySelectorAll('#labelPopover .label-style-btn').forEach(b =>
      b.classList.toggle('active', b.dataset.value === (rd.glassLabels ? 'glass' : 'solid')));
    // Sync theme / size
    el.classList.toggle('dark-mode', rd.darkMode);
    el.classList.toggle('large-ui', rd.largeUI);
    document.querySelectorAll('.theme-btn').forEach(b => b.classList.toggle('active', (b.dataset.value === 'dark') === rd.darkMode));
    document.querySelectorAll('.size-btn').forEach(b => b.classList.toggle('active', (b.dataset.value === 'large') === rd.largeUI));
    // Persist to localStorage so page reload keeps these
    localStorage.setItem('rd_showMeasurements', rd.showMeasurements ? '1' : '0');
    localStorage.setItem('rd_showRoomLabels', rd.showRoomLabels ? '1' : '0');
    localStorage.setItem('rd_showGrid', rd.showGrid ? '1' : '0');
    localStorage.setItem('rd_hollowWalls', rd.hollowWalls ? '1' : '0');
    localStorage.setItem('rd_hideOverage', rd.hideOverage ? '1' : '0');
    localStorage.setItem('rd_showSeamLines', rd.showSeamLines ? '1' : '0');
    localStorage.setItem('rd_showMaterialOverlay', rd.showMaterialOverlay ? '1' : '0');
    localStorage.setItem('rd_showEquipmentLabels', rd.showEquipmentLabels ? '1' : '0');
    localStorage.setItem('rd_glassLabels', rd.glassLabels ? '1' : '0');
    localStorage.setItem('rd_unit', rd.unit);
    localStorage.setItem('rd_darkMode', rd.darkMode ? '1' : '0');
    localStorage.setItem('rd_largeUI', rd.largeUI ? '1' : '0');
  }

  // ── Auto-Save ─────────────────────────────

  _triggerAutoSave() {
    if (this._autoSaveDebounce) clearTimeout(this._autoSaveDebounce);
    this._autoSaveDebounce = setTimeout(() => this._doAutoSave(), 1000);
  }

  // CUSTOM_ROOMDESIGNER_BY_CLAUDE — used by defensive autosave + recovery
  _hasContent(d) {
    if (!d) return false;
    return (d.rooms && d.rooms.length > 0)
      || (d.objects && d.objects.length > 0)
      || (d.currentRoom && d.currentRoom.walls && d.currentRoom.walls.length > 0);
  }

  _doAutoSave() {
    if (this._embed) return;   // CUSTOM_QUOTELAYOUTSWAP_BY_CLAUDE — the hidden Workstation copy never autosaves
    const rd = this.rd;
    try {
      const data = rd._serializeDesign();
      const prefix = this._getRepPrefix();
      const newHasContent = this._hasContent(data);

      // CUSTOM_ROOMDESIGNER_BY_CLAUDE — defensive autosave: never overwrite a populated cache with an empty design.
      // Catches the destructive scenario where a logout/blank-canvas tick clobbers good work.
      // _newDesign explicitly clears the cache, so a legitimate "start fresh" still works.
      if (!newHasContent) {
        const existing = localStorage.getItem(prefix + 'rd_autosave');
        if (existing) {
          try {
            if (this._hasContent(JSON.parse(existing))) return; // skip — preserve existing
          } catch (_) { /* corrupt — let the write proceed and overwrite */ }
        }
      }
      // END CUSTOM_ROOMDESIGNER_BY_CLAUDE

      const json = JSON.stringify(data);
      localStorage.setItem(prefix + 'rd_autosave', json);
      localStorage.setItem(prefix + 'rd_autosave_time', String(Date.now()));

      // CUSTOM_ROOMDESIGNER_BY_CLAUDE — Last-Known-Good slot. Only updated when current state has content,
      // so it survives any future overwrite-with-empty bugs as a final safety net.
      if (newHasContent) {
        try {
          localStorage.setItem(prefix + 'rd_autosave_lkg', json);
          localStorage.setItem(prefix + 'rd_autosave_lkg_time', String(Date.now()));
        } catch (_) { /* quota — ignore, primary write already succeeded */ }
      }
      // END CUSTOM_ROOMDESIGNER_BY_CLAUDE

      // Persist portal design ID + name so recovery can restore the link
      if (rd._portalMode) {
        const nameInput = document.getElementById('designNameInput');
        const meta = {
          portalDesignId: this._portalDesignId || null,
          portalDesignRef: this._portalDesignRef || null,
          portalDesignChainId: this._portalDesignChainId || null,
          designName: (nameInput && nameInput.value) || '',
          savedClean: !this._isDirty,
        };
        localStorage.setItem(prefix + 'rd_autosave_meta', JSON.stringify(meta));
      }
      this._lastAutoSaveTime = Date.now();
      this._updateAutoSaveIndicator();
    } catch (e) {
      // localStorage full or unavailable — silent fail
    }
  }

  _updateAutoSaveIndicator() {
    // No-op — indicator removed in file-based save migration
  }

  _startAutoSaveTimer() {
    const rd = this.rd;
    // Periodic auto-save every 30 seconds (session cache for hard-refresh recovery)
    this._autoSaveInterval = setInterval(() => this._doAutoSave(), 30000);
    // Save on page unload — show native "Leave site?" dialog if dirty
    window.addEventListener('beforeunload', (e) => {
      this._doAutoSave();
      if (this._isDirty) {
        e.preventDefault();
        e.returnValue = '';
      }
    });
  }

  _setupPageLeaveGuard() {
    // Intercept navigation-link clicks when the design has unsaved changes.
    // Uses capture phase so it fires before theme/instant-page handlers.
    document.addEventListener('click', (e) => {
      if (!this._isDirty) return;

      const link = e.target.closest('a[href]');
      if (!link) return;

      const href = link.getAttribute('href');
      if (!href || href.startsWith('#') || href.startsWith('javascript:')
          || href.startsWith('tel:') || href.startsWith('mailto:')
          || href.startsWith('blob:')) return;

      // Skip download links (file saves) and new-tab links
      if (link.hasAttribute('download')) return;
      if (link.target === '_blank') return;
      if (e.ctrlKey || e.metaKey || e.shiftKey || e.button !== 0) return;

      // Skip links inside the room designer itself (internal UI)
      const rdEl = document.querySelector('.room-designer');
      if (rdEl && rdEl.contains(link)) return;

      e.preventDefault();
      e.stopPropagation();

      this._pendingLeaveDest = link.href;
      const isPortal = !!this.rd._portalMode;
      this._showConfirm(
        isPortal ? 'You have unsaved changes. Save your design before leaving?' : 'You have unsaved changes. Download your design before leaving?',
        'Save & Leave',
        () => {
          const nameInput = document.getElementById('designNameInput');
          if (!nameInput || !nameInput.value.trim()) {
            this._openDrawerForName();
            return;
          }
          if (isPortal) {
            this._saveToPortal().then(() => {
              const dest = this._pendingLeaveDest;
              setTimeout(() => { this._isDirty = false; window.location.href = dest; }, 500);
            });
          } else {
            this._saveToFile();
            const dest = this._pendingLeaveDest;
            setTimeout(() => { this._isDirty = false; window.location.href = dest; }, 500);
          }
        },
        {
          altText: 'Leave',
          onAlt: () => {
            this._isDirty = false;
            window.location.href = link.href;
          }
        }
      );
    }, true);
  }

  _autoLoadDesign() {
    const rd = this.rd;
    try {
      const prefix = this._getRepPrefix();
      let autosave = localStorage.getItem(prefix + 'rd_autosave');
      let data = null;
      let usedLkg = false;
      if (autosave) {
        try {
          const parsed = JSON.parse(autosave);
          if (this._hasContent(parsed)) data = parsed;
        } catch (_) { /* corrupt primary — fall through */ }
      }
      // CUSTOM_ROOMDESIGNER_BY_CLAUDE — fall back to Last-Known-Good if primary is missing/empty/corrupt
      if (!data) {
        const lkg = localStorage.getItem(prefix + 'rd_autosave_lkg');
        if (lkg) {
          try {
            const parsedLkg = JSON.parse(lkg);
            if (this._hasContent(parsedLkg)) { data = parsedLkg; usedLkg = true; }
          } catch (_) { /* both corrupt — give up */ }
        }
      }
      // END CUSTOM_ROOMDESIGNER_BY_CLAUDE
      if (!data) return false;

      // In portal mode, check if the autosave was already saved to portal (clean)
      // If so, silently restore it without a "Resume?" prompt
      if (rd._portalMode) {
        try {
          const metaStr = localStorage.getItem(prefix + 'rd_autosave_meta');
          if (metaStr) {
            const meta = JSON.parse(metaStr);
            if (meta.savedClean) {
              rd._loadDesignData(data);
              this._portalDesignId = meta.portalDesignId || null;
              this._portalDesignRef = meta.portalDesignRef || null;
              this._portalDesignChainId = meta.portalDesignChainId || null;
              this._updateRefBadge();
              if (meta.designName) this._updateDesignNameUI(meta.designName);
              this._isDirty = false;
              return true;
            }
          }
        } catch (e) { /* ignore corrupt meta */ }
      }

      // Show recovery prompt — use LKG timestamp when we fell back to it.
      const timeStr = localStorage.getItem(prefix + (usedLkg ? 'rd_autosave_lkg_time' : 'rd_autosave_time'));
      const ts = timeStr ? parseInt(timeStr, 10) : null;
      this._showRecoveryPrompt(data, ts);
      return true;
    } catch (e) { /* corrupt data — ignore */ }
    return false;
  }

  _showRecoveryPrompt(data, timestamp) {
    const rd = this.rd;
    const overlay = document.getElementById('rdRecoveryOverlay');
    if (!overlay) {
      // Fallback: no recovery overlay in DOM — just load silently
      rd._loadDesignData(data);
      return;
    }
    const timeEl = overlay.querySelector('.rd-recovery-time');
    if (timeEl && timestamp) {
      const d = new Date(timestamp);
      const now = new Date();
      const diff = now - d;
      let timeText;
      if (diff < 60000) timeText = 'just now';
      else if (diff < 3600000) timeText = Math.round(diff / 60000) + ' minutes ago';
      else if (diff < 86400000) timeText = Math.round(diff / 3600000) + ' hours ago';
      else {
        const month = d.toLocaleString('en', { month: 'short' });
        const day = d.getDate();
        const time = d.toLocaleString('en', { hour: 'numeric', minute: '2-digit' });
        timeText = month + ' ' + day + ' at ' + time;
      }
      timeEl.textContent = timeText;
    } else if (timeEl) {
      timeEl.textContent = 'a previous session';
    }
    overlay.classList.add('visible');
    const resumeBtn = document.getElementById('rdRecoveryResume');
    const freshBtn = document.getElementById('rdRecoveryFresh');
    const cleanup = () => {
      overlay.classList.remove('visible');
      resumeBtn.removeEventListener('click', handleResume);
      freshBtn.removeEventListener('click', handleFresh);
    };
    const handleResume = () => {
      cleanup();
      rd._loadDesignData(data);
      // Restore portal design link if available
      if (rd._portalMode) {
        try {
          const prefix = this._getRepPrefix();
          const metaStr = localStorage.getItem(prefix + 'rd_autosave_meta');
          if (metaStr) {
            const meta = JSON.parse(metaStr);
            this._portalDesignId = meta.portalDesignId || null;
            this._portalDesignRef = meta.portalDesignRef || null;
            this._portalDesignChainId = meta.portalDesignChainId || null;
            this._updateRefBadge();
            if (meta.designName) this._updateDesignNameUI(meta.designName);
          }
        } catch (e) { /* ignore */ }
      }
      rd.updateStatus('Design restored.');
    };
    const handleFresh = () => {
      cleanup();
      const prefix = this._getRepPrefix();
      localStorage.removeItem(prefix + 'rd_autosave');
      localStorage.removeItem(prefix + 'rd_autosave_time');
      localStorage.removeItem(prefix + 'rd_autosave_meta');
      rd.startNewRoom();
      rd.updateStatus('Starting fresh.');
    };
    resumeBtn.addEventListener('click', handleResume);
    freshBtn.addEventListener('click', handleFresh);
  }

  _openDrawerForName() {
    const rd = this.rd;
    const drawer = document.getElementById('designsDrawer');
    const toggle = document.getElementById('designsToggle');
    const nameInput = document.getElementById('designNameInput');
    const warning = document.getElementById('designSaveWarning');
    if (drawer) drawer.classList.add('open');
    if (toggle) toggle.classList.add('open');
    rd._pinnedPopover = 'designs';
    if (warning) warning.classList.add('visible');
    if (nameInput) setTimeout(() => nameInput.focus(), 100);
  }

  _flashSaveButton() {
    const btn = document.getElementById('designSaveBtn');
    if (!btn) return;
    clearTimeout(this._saveFlashTimer);
    btn.textContent = 'Saved';
    btn.classList.add('saved');
    this._saveFlashTimer = setTimeout(() => {
      btn.textContent = 'Save';
      btn.classList.remove('saved');
    }, 1500);
  }

  _newDesign() {
    const rd = this.rd;
    rd.rooms = [];
    rd.objects = [];
    rd.currentRoom = null;
    rd.wallSegmentGroups = [];
    rd.currentWallSegmentGroup = null;
    rd.undoStack = [];
    rd.redoStack = [];
    rd._placementRedoStack = [];
    rd.selectedWalls = new Set();
    rd.selectedDoors = new Set();
    rd.selectedObjects = new Set();
    rd.currentWall = null;
    rd.hasPlacedFirstPoint = false;
    rd._resetRectState();
    rd._resetFreehandState();
    rd._measurements = []; rd._measureNextId = 1;
    rd._selectedMeasurements = new Set(); rd._measureStart = null; rd._measureEnd = null;
    rd._measurePhase = 0; rd._measurePendingEnd = null; rd._measureDragging = null;
    if (rd._updateMeasureClearBtn) rd._updateMeasureClearBtn();
    rd._brandColors = {};
    var _prefix = this._getRepPrefix();
    localStorage.removeItem(_prefix + 'rd_autosave');
    localStorage.removeItem(_prefix + 'rd_autosave_time');
    localStorage.removeItem(_prefix + 'rd_autosave_meta');
    // CUSTOM_ROOMDESIGNER_BY_CLAUDE — clear Last-Known-Good slot too so explicit "New" actually starts fresh
    localStorage.removeItem(_prefix + 'rd_autosave_lkg');
    localStorage.removeItem(_prefix + 'rd_autosave_lkg_time');
    // END CUSTOM_ROOMDESIGNER_BY_CLAUDE
    this._portalDesignId = null; // clear portal design ID so next save creates new
    this._portalDesignRef = null;
    this._portalDesignChainId = null;
    this._portalDesignLinkedDeals = [];
    this._updateRefBadge();
    this._updateDesignNameUI('');
    // Clear share dialog fields so they don't carry over to the new design
    localStorage.removeItem('rd_share_receiver');
    localStorage.removeItem('rd_share_space');
    localStorage.removeItem('rd_share_name');
    rd.startNewRoom();
    // Reset to hand tool (same as clearAll)
    rd.activeTool = 'hand';
    rd.mode = 'hand';
    rd.canvas.style.cursor = 'grab';
    document.getElementById('selectToolBtn').classList.remove('active');
    document.getElementById('handToolBtn').classList.remove('active');
    document.getElementById('penToolBtn').classList.remove('active');
    document.getElementById('objectsToolBtn').classList.remove('active');
    document.getElementById('eraserBtn').classList.remove('active');
    rd._updateJoystickVisibility();
    rd._updateFloatingDelete();
    rd._updateFloatingCancel();
    rd._updateFloatingRepeat();
    // Reset to Draw Space tab and clear overlay state
    const overlay = window.roomDesignerOverlay;
    if (overlay) {
      if (overlay.currentStep !== 'draw') overlay._applyStepChange('draw');
      overlay._activeBrush = null;
      overlay._exteriorZoneFlooring = [];
      overlay.showOverlay = false;
    }
    rd.render();
    rd.updateMeasurements();
    rd.updateStatus('New blank design started.');
    this._isDirty = false;
  }

  // ── Save to File / Open from File ────────

  _saveToFile() {
    const rd = this.rd;
    const data = rd._serializeDesign();
    const input = document.getElementById('designNameInput');
    const designName = (input && input.value.trim()) || this._untitledName();
    const exportObj = {
      version: 1,
      name: designName,
      exportedAt: new Date().toISOString(),
      data: data
    };
    const blob = new Blob([JSON.stringify(exportObj, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = designName.replace(/[^a-z0-9_\- ]/gi, '').replace(/\s+/g, '_') + '.json';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    const _fbg = window.roomDesignerBackground;
    rd.updateStatus(_fbg && _fbg._bgImages.length > 0 ? 'Design saved to file. Blueprint image excluded.' : 'Design saved to file.');
    this._flashSaveButton();
    this._isDirty = false;
  }

  _openFromFile(file) {
    const rd = this.rd;
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const parsed = JSON.parse(e.target.result);
        if (!parsed.version || !parsed.data) {
          rd.updateStatus('Invalid design file.', { error: true });
          return;
        }
        rd._loadDesignData(parsed.data);
        const name = parsed.name || 'Opened Design';
        this._updateDesignNameUI(name);
        // Update session cache
        const _openPrefix = this._getRepPrefix();
        localStorage.setItem(_openPrefix + 'rd_autosave', JSON.stringify(parsed.data));
        localStorage.setItem(_openPrefix + 'rd_autosave_time', String(Date.now()));
        rd.updateStatus('Design "' + name + '" opened.');
      } catch (err) {
        rd.updateStatus('Error reading design file.', { error: true });
      }
    };
    reader.readAsText(file);
  }

  // ── Share Link ──────────────────────────

  async _shareDesign() {
    const rd = this.rd;
    const data = rd._serializeDesign();
    // Skip pre-check — compression is very effective, let _buildShareUrl handle the 8KB URL limit
    this._openShareDialog(data);
  }

  async _copyToClipboard(text) {
    // Try modern Clipboard API first, then fall back to execCommand
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch (e) { /* user gesture may have expired after async — fall through */ }
    try {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.style.cssText = 'position:fixed;left:-9999px;top:-9999px;opacity:0';
      document.body.appendChild(ta);
      ta.select();
      const ok = document.execCommand('copy');
      document.body.removeChild(ta);
      return ok;
    } catch (e) { return false; }
  }

  async _buildShareUrl(designData, meta) {
    const payload = meta ? { meta, design: designData } : { design: designData };

    // Generate OG preview image for social sharing cards
    let ogImageUrl = null;
    try {
      const ogDataUrl = await this.rd._generateOgImage();
      if (ogDataUrl) {
        const blob = await (await fetch(ogDataUrl)).blob();
        const form = new FormData();
        form.append('file', blob, 'og-preview.jpg');
        const uploadRes = await fetch('https://rfq-worker.ryan-29b.workers.dev/upload', { method: 'POST', body: form });
        if (uploadRes.ok) {
          const uploadData = await uploadRes.json();
          // Upload returns relative path — make absolute for OG tags
          ogImageUrl = uploadData.url.startsWith('/')
            ? 'https://rfq-worker.ryan-29b.workers.dev' + uploadData.url
            : uploadData.url;
        }
      }
    } catch (e) {
      console.warn('OG image generation/upload failed — sharing without preview:', e.message);
    }

    // Build OG metadata for social preview cards
    const spaceSummary = this._buildSpaceSummary(this.rd);
    const ogTitle = meta?.spaceName
      ? meta.spaceName + ' \u2014 Floor Plan'
      : meta?.senderName
        ? 'Gym Layout by ' + meta.senderName
        : 'Gym Layout Design \u2014 Blue Sky Fitness Supply';
    const ogDescription = spaceSummary.spaceSummary.length > 0
      ? spaceSummary.spaceSummary.length + ' space' + (spaceSummary.spaceSummary.length > 1 ? 's' : '') + ', ' + spaceSummary.totalArea.toLocaleString() + ' sq ft'
      : 'Interactive gym floor plan created with the Room Designer tool';

    if (ogImageUrl) payload.ogImageUrl = ogImageUrl;
    payload.ogTitle = ogTitle;
    payload.ogDescription = ogDescription;

    // Try server-side storage first (short, email-safe URLs)
    // Compress design field to stay under worker's 500KB limit
    try {
      const workerPayload = { ...payload };
      const designJson = JSON.stringify(workerPayload.design);
      try {
        const blob = new Blob([designJson]);
        const cs = new CompressionStream('deflate');
        const compressed = await new Response(blob.stream().pipeThrough(cs)).arrayBuffer();
        const bytes = new Uint8Array(compressed);
        let binary = '';
        for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
        workerPayload.design = null;
        workerPayload.designCompressed = btoa(binary);
      } catch (e) { /* compression unavailable — send uncompressed */ }
      const res = await fetch('https://rfq-worker.ryan-29b.workers.dev/share', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(workerPayload),
      });
      if (res.status === 429) {
        console.warn('Share rate limit hit — falling back to URL hash');
        // Fall through to hash fallback (graceful degradation)
      } else if (res.ok) {
        const data = await res.json();
        if (data.success && data.id) {
          return 'https://rfq-worker.ryan-29b.workers.dev/plan/' + data.id;
        }
      }
    } catch (e) {
      // Worker down — fall through to hash fallback
    }

    // Fallback: deflate-compress into URL hash (legacy approach)
    const json = JSON.stringify(payload);
    let encoded;
    try {
      const blob = new Blob([json]);
      const cs = new CompressionStream('deflate');
      const stream = blob.stream().pipeThrough(cs);
      const compressed = await new Response(stream).arrayBuffer();
      const bytes = new Uint8Array(compressed);
      let binary = '';
      for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
      encoded = btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
    } catch (e) {
      encoded = 'r.' + btoa(unescape(encodeURIComponent(json))).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
    }
    // Portal mode: always use public page URL so links work for non-portal users
    const _sharePath = this.rd._portalMode ? '/pages/gym-layout-designer' : window.location.pathname;
    const url = window.location.origin + _sharePath + '#plan=' + encoded;
    if (url.length > 32000) return null;
    return url;
  }

  _openShareDialog(designData) {
    const overlay = document.getElementById('rdShareOverlay');
    const nameInput = document.getElementById('rdShareNameInput');
    const receiverInput = document.getElementById('rdShareReceiverInput');
    const msgInput = document.getElementById('rdShareMessageInput');
    const closeBtn = document.getElementById('rdShareClose');
    const copyBtn = document.getElementById('rdShareCopy');
    const emailBtn = document.getElementById('rdShareEmail');
    if (!overlay) return;

    // Rep tools section — always reset to hidden on dialog open
    const repSection = document.getElementById('rdShareRepSection');
    const repStatus = document.getElementById('rdShareRepStatus');
    const planPdfBtn = document.getElementById('rdSharePlanPdf');
    const fullPacketBtn = document.getElementById('rdShareFullPacket');
    const seamPdfBtn = document.getElementById('rdShareSeamPdf');
    const viewOnlyBtn = document.getElementById('rdShareViewOnly');
    const viewOnlyOptions = document.getElementById('rdShareViewOnlyOptions');
    const viewOnlyGoBtn = document.getElementById('rdShareViewOnlyGo');
    const multiChecksEl = document.getElementById('rdShareMultiChecks');
    if (repSection) repSection.classList.remove('visible');
    if (repStatus) { repStatus.textContent = ''; repStatus.classList.remove('error'); }
    if (viewOnlyOptions) viewOnlyOptions.classList.remove('visible');

    // Gate multi-view checkboxes on actual content
    if (multiChecksEl) {
      const hasFlooring = this.rd.rooms.some(r => r.closed && r.type === 'add' && r.flooring)
          || this.rd.objects.some(o => o.type === 'turf');
      const hasEquipment = this.rd.objects.some(o => o.type === 'equipment');
      multiChecksEl.querySelectorAll('input[type="checkbox"]').forEach(cb => {
        if (cb.value === 'floor') {
          cb.disabled = !hasFlooring;
          cb.checked = hasFlooring;
        } else if (cb.value === 'equipment') {
          cb.disabled = !hasEquipment;
          cb.checked = hasEquipment;
        } else {
          cb.checked = true;
        }
      });
    }

    // Pre-fill fields from localStorage
    const savedName = localStorage.getItem('rd_share_name') || '';
    nameInput.value = savedName;
    if (receiverInput) receiverInput.value = localStorage.getItem('rd_share_receiver') || '';
    const spaceInput = document.getElementById('rdShareSpaceInput');
    if (spaceInput) {
      // CUSTOM_ROOMDESIGNER_BY_CLAUDE — prefer explicit saved value (set by previous edit or design load via _meta).
      // localStorage.getItem() returns null when never set; '' means user cleared+saved (explicit blank — honor it).
      const savedSpace = localStorage.getItem('rd_share_space');
      if (savedSpace !== null) {
        spaceInput.value = savedSpace;
      } else {
        // No saved value yet — derive from room names. Skip the "Space N" auto-defaults.
        const addRooms = this.rd.rooms.filter(r => r.closed && r.type === 'add');
        const customNames = addRooms.map(r => r.name || '').filter(n => n && !/^Space \d+$/.test(n));
        if (customNames.length === 1) {
          // Single room: use the name as-is. Do NOT strip trailing digits — they may be part of the name (e.g. "Lincoln 80205").
          spaceInput.value = customNames[0];
        } else if (customNames.length > 1) {
          // Multi-room: strip the auto-appended " 1" / " 2" suffix we add when broadcasting one base name.
          const base = customNames[0].replace(/\s+\d+$/, '');
          const allSameBase = customNames.every(n => n === base || n.replace(/\s+\d+$/, '') === base);
          spaceInput.value = allSameBase ? base : customNames.join(', ');
        } else {
          spaceInput.value = '';
        }
      }
      // END CUSTOM_ROOMDESIGNER_BY_CLAUDE
    }
    if (msgInput) msgInput.value = '';

    // Auto-detect current step tab
    const activeStepTab = document.querySelector('.rd-step-tab.active');
    const currentTab = activeStepTab ? activeStepTab.dataset.step : 'draw';

    // Reset copy button state
    copyBtn.classList.remove('copied');
    copyBtn.innerHTML = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg> Share Edit Link';

    // Disable action buttons until required fields filled
    const hasClosedRoom = this.rd.rooms.some(r => r.closed && r.type === 'add');
    const updateBtnState = () => {
      const ready = nameInput.value.trim().length > 0;
      const repReady = ready
        && (!receiverInput || receiverInput.value.trim().length > 0)
        && (!spaceInput || spaceInput.value.trim().length > 0);
      copyBtn.disabled = !ready;
      emailBtn.disabled = !ready;
      // Rep PDF buttons need all 3 fields + a closed room; view-only just needs all 3 fields
      if (planPdfBtn) planPdfBtn.disabled = !repReady || !hasClosedRoom;
      if (fullPacketBtn) fullPacketBtn.disabled = !repReady || !hasClosedRoom;
      // Seam PDF needs roll-type flooring
      if (seamPdfBtn) {
        const hasRollFlooring = this.rd.rooms.some(r => {
          if (!r.closed || r.type !== 'add') return false;
          const overlay = window.roomDesignerOverlay;
          if (!overlay) return false;
          // Check room.flooring (single-zone or synced from zones)
          if (r.flooring) {
            const prod = overlay._getProduct(r.flooring.productId);
            if (prod && prod.shape === 'roll') return true;
          }
          // Check zoneFlooring entries
          if (r.zoneFlooring) {
            for (const zf of r.zoneFlooring) {
              const prod = overlay._getProduct(zf.productId);
              if (prod && prod.shape === 'roll') return true;
            }
          }
          return false;
        });
        seamPdfBtn.disabled = !repReady || !hasClosedRoom || !hasRollFlooring;
      }
      if (viewOnlyBtn) viewOnlyBtn.disabled = !repReady;
      // Multi-view "Copy" button: also check that at least 1 checkbox is checked
      if (viewOnlyGoBtn) {
        const anyChecked = !multiChecksEl || multiChecksEl.querySelectorAll('input[type="checkbox"]:checked').length > 0;
        viewOnlyGoBtn.disabled = !ready || !anyChecked;
      }
    };
    updateBtnState();

    overlay.classList.add('visible');
    setTimeout(() => nameInput.focus(), 100);

    // Prevent keyboard shortcuts while typing in name/message fields
    const handleKeydown = (e) => {
      e.stopPropagation();
      if (e.key === 'Escape') cleanup();
    };
    const handleKeyup = (e) => e.stopPropagation();
    const handleInput = () => {
      // CUSTOM_ROOMDESIGNER_BY_CLAUDE — capture dialog values live so a later Save embeds them on the design (covers users who type then save without clicking Generate/Share).
      if (receiverInput) localStorage.setItem('rd_share_receiver', receiverInput.value);
      if (spaceInput) localStorage.setItem('rd_share_space', spaceInput.value);
      // END CUSTOM_ROOMDESIGNER_BY_CLAUDE
      updateBtnState();
    };

    // When space name changes, update room names on canvas
    const handleSpaceNameChange = () => {
      const val = spaceInput ? spaceInput.value.trim() : '';
      if (!val) return;
      const rd = this.rd;
      const addRooms = rd.rooms.filter(r => r.closed && r.type === 'add');
      if (addRooms.length === 0) return;
      if (addRooms.length === 1) {
        addRooms[0].name = val;
      } else {
        addRooms.forEach((r, i) => { r.name = val + ' ' + (i + 1); });
      }
      rd.render();
      rd._triggerAutoSave();
    };

    // Rep roster for dropdown
    const REP_ROSTER = [
      { id: 'john-warner', name: 'John Warner', email: 'john@blueskyfitsupply.com', phone: '(303) 264-9496', headshotFile: 'rep-headshot-john-warner.webp' },
      { id: 'ryan-walker', name: 'Ryan Walker', email: 'ryan@blueskyfitsupply.com', phone: '(720) 515-3585', headshotFile: 'rep-headshot-ryan-walker.webp' }
    ];

    // Name dropdown (hidden by default, shown when rep tools visible)
    const nameSelect = document.getElementById('rdShareNameSelect');
    let _repSelectHandler = null;

    const showRepDropdown = () => {
      if (!nameSelect) return;
      // Populate options from roster
      nameSelect.innerHTML = '<option value="">Select rep...</option>';
      REP_ROSTER.forEach(rep => {
        const opt = document.createElement('option');
        opt.value = rep.id;
        opt.textContent = rep.name;
        nameSelect.appendChild(opt);
      });
      // Pre-select from localStorage
      const savedRepId = localStorage.getItem('rd_share_rep_id') || '';
      if (savedRepId) {
        nameSelect.value = savedRepId;
        const rep = REP_ROSTER.find(r => r.id === savedRepId);
        if (rep) nameInput.value = rep.name;
      }
      nameInput.style.display = 'none';
      nameSelect.style.display = '';
      // On change, sync name + save
      _repSelectHandler = () => {
        const rep = REP_ROSTER.find(r => r.id === nameSelect.value);
        nameInput.value = rep ? rep.name : '';
        if (rep) localStorage.setItem('rd_share_rep_id', rep.id);
        else localStorage.removeItem('rd_share_rep_id');
        updateBtnState();
      };
      nameSelect.addEventListener('change', _repSelectHandler);
      updateBtnState();
    };

    const hideRepDropdown = () => {
      if (!nameSelect) return;
      nameSelect.style.display = 'none';
      nameInput.style.display = '';
      if (_repSelectHandler) {
        nameSelect.removeEventListener('change', _repSelectHandler);
        _repSelectHandler = null;
      }
    };

    // Rep mode: auto-show rep tools section on dialog open
    const titleEl = overlay.querySelector('.rd-share-title');
    const handleTitleClick = () => {}; // no-op — kept so cleanup removeEventListener doesn't error
    if (this.rd._portalMode) { try {
      // Portal mode: simplified share dialog — just links
      // Auto-fill sender name + rep ID from session
      try {
        const sess = JSON.parse(sessionStorage.getItem('rep_portal_session') || '{}');
        if (sess.name) nameInput.value = sess.name;
        if (sess.fullName) {
          const slug = sess.fullName.toLowerCase().replace(/\s+/g, '-');
          localStorage.setItem('rd_share_rep_id', slug);
        }
      } catch(e) {}
      // Hide "Your name" field
      const nameField = nameInput.closest('.rd-share-name-field');
      if (nameField) nameField.style.display = 'none';
      // Hide message textarea + label
      if (msgInput) {
        msgInput.style.display = 'none';
        var prevEl = msgInput.previousElementSibling;
        if (prevEl && prevEl.classList && prevEl.classList.contains('rd-share-field-label')) prevEl.style.display = 'none';
      }
      // Rename Copy Link → Copy Edit Link
      copyBtn.innerHTML = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg> Share Edit Link';
      // Repurpose Email button → Copy Presentation Link
      emailBtn.innerHTML = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg> Share Presentation Link';
      emailBtn._portalPresentationHandler = async () => {
        const meta = getMeta();
        meta.viewOnly = true;
        meta.sharedAt = new Date().toISOString();
        const MULTI_VIEW_DEFS_P = {
          draw:      { id: 'draw',      label: 'Floor Plan',        overrides: { showMeasurements: true, showFloorOverlay: false, showSeamLines: false, showMaterialOverlay: false, seamFaint: false, hideZoneDividers: false, equipmentMode: false, showRoomLabels: true } },
          floor:     { id: 'floor',     label: 'Flooring Layout',   overrides: { showMeasurements: false, showFloorOverlay: true, showSeamLines: true, showMaterialOverlay: true, seamFaint: true, hideZoneDividers: true, equipmentMode: false, showRoomLabels: true } },
          equipment: { id: 'equipment', label: 'Equipment Layout',  overrides: { showMeasurements: false, showFloorOverlay: false, showSeamLines: false, showMaterialOverlay: false, seamFaint: false, hideZoneDividers: false, equipmentMode: true, showRoomLabels: true } }
        };
        const views = ['draw', 'floor', 'equipment'].map(id => MULTI_VIEW_DEFS_P[id]);
        views.push({ id: 'combined', label: 'Full Layout', overrides: { showMeasurements: false, showFloorOverlay: true, showSeamLines: true, showMaterialOverlay: true, seamFaint: true, hideZoneDividers: true, equipmentMode: true, showRoomLabels: true }});
        meta.multiView = true;
        meta.views = views;
        emailBtn.disabled = true;
        emailBtn.innerHTML = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="rd-spin"><circle cx="12" cy="12" r="10" stroke-dasharray="31.4 31.4"/></svg> Generating\u2026';
        const url = await this._buildShareUrl(designData, meta);
        emailBtn.disabled = false;
        emailBtn.innerHTML = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg> Share Presentation Link';
        if (!url) { this.rd.updateStatus('Design is too large to share via link.', { error: true }); return; }
        const copied = await this._copyToClipboard(url);
        if (copied) {
          emailBtn.classList.add('copied');
          emailBtn.innerHTML = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg> Copied!';
          this.rd.updateStatus('Presentation link copied to clipboard!');
        }
      };
      emailBtn.addEventListener('click', emailBtn._portalPresentationHandler);
      // Add "Share Internally" button (only once; never for an external user)
      if (!this._isExternalUser() && !emailBtn.parentNode.querySelector('.rd-share-internal-btn')) {
      const shareInternalBtn = document.createElement('button');
      shareInternalBtn.className = 'rd-share-btn rd-share-internal-btn';
      shareInternalBtn.innerHTML = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8"/><polyline points="16 6 12 2 8 6"/><line x1="12" y1="2" x2="12" y2="15"/></svg> Share Internally';
      emailBtn.parentNode.insertBefore(shareInternalBtn, emailBtn.nextSibling);
      shareInternalBtn.addEventListener('click', () => {
        if (this._portalDesignId) {
          this._showShareInternalDialog(this._portalDesignId, document.getElementById('designNameInput')?.value?.trim() || 'this design');
        } else {
          this.rd.updateStatus('Save your design first before sharing internally.', { error: true });
        }
      });
      }
      // Update button state: only need receiver + space filled
      const portalUpdateBtnState = () => {
        const ready = receiverInput && receiverInput.value.trim().length > 0;
        copyBtn.disabled = !ready;
        emailBtn.disabled = !ready;
      };
      portalUpdateBtnState();
      if (receiverInput) receiverInput.addEventListener('input', portalUpdateBtnState);
      if (spaceInput) spaceInput.addEventListener('input', portalUpdateBtnState);
      // Change dialog title
      if (titleEl) titleEl.textContent = 'Share Design';
      // Focus receiver instead of name
      setTimeout(() => { if (receiverInput) receiverInput.focus(); }, 100);
    } catch(portalErr) { console.error('[RD] Portal share setup error:', portalErr); }
    } else if (this.rd._repMode && repSection) {
      repSection.classList.add('visible');
      showRepDropdown();
    }

    const cleanup = () => {
      overlay.classList.remove('visible');
      closeBtn.removeEventListener('click', handleClose);
      copyBtn.removeEventListener('click', handleCopy);
      emailBtn.removeEventListener('click', handleEmail);
      if (emailBtn._portalPresentationHandler) {
        emailBtn.removeEventListener('click', emailBtn._portalPresentationHandler);
        emailBtn._portalPresentationHandler = null;
      }
      nameInput.removeEventListener('keydown', handleKeydown);
      nameInput.removeEventListener('keyup', handleKeyup);
      nameInput.removeEventListener('input', handleInput);
      if (receiverInput) {
        receiverInput.removeEventListener('keydown', handleKeydown);
        receiverInput.removeEventListener('keyup', handleKeyup);
        receiverInput.removeEventListener('input', handleInput);
      }
      if (spaceInput) {
        spaceInput.removeEventListener('keydown', handleKeydown);
        spaceInput.removeEventListener('keyup', handleKeyup);
        spaceInput.removeEventListener('input', handleInput);
        spaceInput.removeEventListener('blur', handleSpaceNameChange);
      }
      if (msgInput) {
        msgInput.removeEventListener('keydown', handleKeydown);
        msgInput.removeEventListener('keyup', handleKeyup);
      }
      if (titleEl) titleEl.removeEventListener('click', handleTitleClick);
      hideRepDropdown();
      if (planPdfBtn) planPdfBtn.removeEventListener('click', handlePlanPdf);
      if (fullPacketBtn) fullPacketBtn.removeEventListener('click', handleFullPacket);
      if (seamPdfBtn) seamPdfBtn.removeEventListener('click', handleSeamPdf);
      if (viewOnlyBtn) viewOnlyBtn.removeEventListener('click', handleViewOnlyToggle);
      if (viewOnlyGoBtn) viewOnlyGoBtn.removeEventListener('click', handleViewOnlyGo);
      if (viewOnlyOptions) {
        viewOnlyOptions.querySelectorAll('input[type="checkbox"]').forEach(cb => cb.removeEventListener('change', updateBtnState));
      }
      copyBtn.disabled = false;
      emailBtn.disabled = false;
    };

    const getMeta = () => {
      const name = nameInput.value.trim();
      const receiver = receiverInput ? receiverInput.value.trim() : '';
      const spaceName = spaceInput ? spaceInput.value.trim() : '';
      const message = msgInput ? msgInput.value.trim() : '';
      if (name) localStorage.setItem('rd_share_name', name);
      if (receiver) localStorage.setItem('rd_share_receiver', receiver);
      if (spaceName) localStorage.setItem('rd_share_space', spaceName);
      // Ensure room names are synced before any share/PDF action
      handleSpaceNameChange();
      const meta = { senderName: name, landingTab: currentTab };
      if (receiver) meta.receiverName = receiver;
      if (spaceName) meta.spaceName = spaceName;
      if (message) meta.message = message;
      // Attach rep data — portal mode reads from session, public mode uses REP_ROSTER dropdown
      if (this.rd._portalMode) {
        try {
          const sess = JSON.parse(sessionStorage.getItem('rep_portal_session') || '{}');
          if (sess.fullName) {
            meta.repId = sess.fullName.toLowerCase().replace(/\s+/g, '-');
            meta.repEmail = sess.email || '';
            meta.repPhone = sess.phone || '';
            meta.repHeadshotUrl = sess.headshotUrl || '';
          }
        } catch(e) {}
      } else if (nameSelect && nameSelect.style.display !== 'none' && nameSelect.value) {
        const rep = REP_ROSTER.find(r => r.id === nameSelect.value);
        if (rep) {
          meta.repId = rep.id;
          meta.repEmail = rep.email;
          meta.repPhone = rep.phone;
          meta.repHeadshotFile = rep.headshotFile;
        }
      }
      return meta;
    };

    const setRepStatus = (text, isError) => {
      if (!repStatus) return;
      repStatus.textContent = text;
      repStatus.classList.toggle('error', !!isError);
    };

    const handleClose = () => cleanup();

    const handleCopy = async () => {
      const meta = getMeta();
      copyBtn.disabled = true;
      copyBtn.innerHTML = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="rd-spin"><circle cx="12" cy="12" r="10" stroke-dasharray="31.4 31.4"/></svg> Generating link\u2026';
      const url = await this._buildShareUrl(designData, meta);
      copyBtn.disabled = false;
      if (!url) {
        this.rd.updateStatus('Design is too large to share via link. Use Export instead.', { error: true });
        cleanup();
        return;
      }
      const copied = await this._copyToClipboard(url);
      if (copied) {
        copyBtn.classList.add('copied');
        copyBtn.innerHTML = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg> Copied!';
        this.rd.updateStatus('Share link copied to clipboard!');
      } else {
        copyBtn.disabled = false;
        copyBtn.textContent = 'Share Edit Link';
        this.rd.updateStatus('Could not copy automatically. Link is ready — try again.', { error: true });
      }
    };

    const handleEmail = async () => {
      const meta = getMeta();
      emailBtn.disabled = true;
      emailBtn.innerHTML = 'Generating link\u2026';
      const url = await this._buildShareUrl(designData, meta);
      emailBtn.disabled = false;
      emailBtn.innerHTML = 'Email';
      if (!url) {
        this.rd.updateStatus('Design is too large to share via link. Use Export instead.', { error: true });
        cleanup();
        return;
      }
      // Always copy to clipboard first
      await this._copyToClipboard(url);
      const senderName = meta.senderName || 'Someone';
      const subject = encodeURIComponent(senderName + ' shared a gym layout with you');
      const body = encodeURIComponent(senderName + ' shared a gym layout design with you.\n\nOpen this link to view it:\n' + url);
      window.open('mailto:?subject=' + subject + '&body=' + body, '_self');
      this.rd.updateStatus('Share link copied to clipboard!');
      cleanup();
    };

    // ── Rep tool handlers ──

    // Toggle the view-only options panel open/closed
    const handleViewOnlyToggle = () => {
      if (viewOnlyOptions) viewOnlyOptions.classList.toggle('visible');
    };

    // Multi-view override definitions
    const MULTI_VIEW_DEFS = {
      draw:      { id: 'draw',      label: 'Floor Plan',        overrides: { showMeasurements: true, showFloorOverlay: false, showSeamLines: false, showMaterialOverlay: false, seamFaint: false, hideZoneDividers: false, equipmentMode: false, showRoomLabels: true } },
      floor:     { id: 'floor',     label: 'Flooring Layout',   overrides: { showMeasurements: false, showFloorOverlay: true, showSeamLines: true, showMaterialOverlay: true, seamFaint: true, hideZoneDividers: true, equipmentMode: false, showRoomLabels: true } },
      equipment: { id: 'equipment', label: 'Equipment Layout',  overrides: { showMeasurements: false, showFloorOverlay: false, showSeamLines: false, showMaterialOverlay: false, seamFaint: false, hideZoneDividers: false, equipmentMode: true, showRoomLabels: true } }
    };

    // Generate and copy the view-only link
    const handleViewOnlyGo = async () => {
      const meta = getMeta();
      meta.viewOnly = true;
      meta.sharedAt = new Date().toISOString();
      const checked = multiChecksEl ? Array.from(multiChecksEl.querySelectorAll('input[type="checkbox"]:checked')).map(cb => cb.value) : [];
      if (checked.length === 0) return;
      meta.multiView = true;
      meta.views = checked.map(id => MULTI_VIEW_DEFS[id]);
      // Auto-append combined tab when both flooring and equipment are selected
      if (checked.includes('floor') && checked.includes('equipment')) {
        meta.views.push({ id: 'combined', label: 'Full Layout', overrides: {
          showMeasurements: false, showFloorOverlay: true, showSeamLines: true, showMaterialOverlay: true,
          seamFaint: true, hideZoneDividers: true, equipmentMode: true, showRoomLabels: true
        }});
      }
      setRepStatus('Generating presentation link...');
      const url = await this._buildShareUrl(designData, meta);
      if (!url) {
        setRepStatus('Design is too large to share via link.', true);
        return;
      }
      const copied = await this._copyToClipboard(url);
      if (copied) {
        setRepStatus('Presentation link copied!');
        this.rd.updateStatus('Presentation link copied to clipboard!');
      } else {
        setRepStatus('Could not copy automatically. Try again.', true);
      }
    };

    const handlePlanPdf = async () => {
      if (!hasClosedRoom) { setRepStatus('Draw a closed room first.', true); return; }
      setRepStatus('Generating floor plan PDF...');
      try {
        const meta = getMeta();
        const pdfUrl = await this._handleSharePlanPdf(designData, meta);
        if (pdfUrl) {
          setRepStatus('PDF ready!');
          window.open(pdfUrl, '_blank');
          setTimeout(() => cleanup(), 1200);
        }
      } catch (err) {
        console.error('[RD] Plan PDF error:', err);
        setRepStatus('Failed to generate PDF. Try again.', true);
      }
    };

    const handleFullPacket = async () => {
      if (!hasClosedRoom) { setRepStatus('Draw a closed room first.', true); return; }
      setRepStatus('Generating full packet PDF...');
      try {
        const meta = getMeta();
        const pdfUrl = await this._handleShareFullPacket(designData, meta);
        if (pdfUrl) {
          setRepStatus('Full packet ready!');
          window.open(pdfUrl, '_blank');
          setTimeout(() => cleanup(), 1200);
        }
      } catch (err) {
        console.error('[RD] Full packet error:', err);
        setRepStatus('Failed to generate PDF. Try again.', true);
      }
    };

    const handleSeamPdf = async () => {
      if (!hasClosedRoom) { setRepStatus('Draw a closed room first.', true); return; }
      setRepStatus('Generating seam plan PDF...');
      try {
        const meta = getMeta();
        const pdfUrl = await this._handleShareSeamPdf(designData, meta);
        if (pdfUrl) {
          setRepStatus('Seam plan ready!');
          window.open(pdfUrl, '_blank');
          setTimeout(() => cleanup(), 1200);
        }
      } catch (err) {
        console.error('[RD] Seam PDF error:', err);
        setRepStatus('Failed to generate seam plan. Try again.', true);
      }
    };

    closeBtn.addEventListener('click', handleClose);
    copyBtn.addEventListener('click', handleCopy);
    if (!this.rd._portalMode) emailBtn.addEventListener('click', handleEmail);
    nameInput.addEventListener('keydown', handleKeydown);
    nameInput.addEventListener('keyup', handleKeyup);
    nameInput.addEventListener('input', handleInput);
    if (receiverInput) {
      receiverInput.addEventListener('keydown', handleKeydown);
      receiverInput.addEventListener('keyup', handleKeyup);
      receiverInput.addEventListener('input', handleInput);
    }
    if (spaceInput) {
      spaceInput.addEventListener('keydown', handleKeydown);
      spaceInput.addEventListener('keyup', handleKeyup);
      spaceInput.addEventListener('input', handleInput);
      spaceInput.addEventListener('blur', handleSpaceNameChange);
    }
    if (msgInput) {
      msgInput.addEventListener('keydown', handleKeydown);
      msgInput.addEventListener('keyup', handleKeyup);
    }
    if (titleEl) titleEl.addEventListener('click', handleTitleClick);
    if (planPdfBtn) planPdfBtn.addEventListener('click', handlePlanPdf);
    if (fullPacketBtn) fullPacketBtn.addEventListener('click', handleFullPacket);
    if (seamPdfBtn) seamPdfBtn.addEventListener('click', handleSeamPdf);
    if (viewOnlyBtn) viewOnlyBtn.addEventListener('click', handleViewOnlyToggle);
    if (viewOnlyGoBtn) viewOnlyGoBtn.addEventListener('click', handleViewOnlyGo);
    if (viewOnlyOptions) {
      viewOnlyOptions.querySelectorAll('input[type="checkbox"]').forEach(cb => cb.addEventListener('change', updateBtnState));
    }
  }

  // ── Portal PDF Dialog ─────────────────────────
  async _openPdfDialog() {
    const rd = this.rd;
    const overlay = document.getElementById('rdPortalPdfOverlay');
    if (!overlay) return;

    const closeBtn = document.getElementById('rdPortalPdfClose');
    const spaceInput = document.getElementById('rdPdfSpaceInput');
    const spaceField = document.getElementById('rdPdfSpaceField');
    const modeWrap = document.getElementById('rdPdfNameMode');
    const planBtn = document.getElementById('rdPortalPlanPdf');
    const fullBtn = document.getElementById('rdPortalFullPacket');
    const seamBtn = document.getElementById('rdPortalSeamPdf');
    const statusEl = document.getElementById('rdPortalPdfStatus');

    // CUSTOM_ROOMDESIGNER_BY_CLAUDE — page titles: the drawing's own names, or one
    // name across the whole set. Both are already understood by the PDF worker; an
    // empty spaceName lets each page header fall back to that space's name, and a set
    // one overrides every header. Nothing on the CANVAS is renamed either way — the
    // old dialog wrote the entered name over room.name, so generating a PDF silently
    // renamed your spaces AND made the two modes indistinguishable.
    let nameMode = localStorage.getItem('rd_pdf_name_mode') === 'custom' ? 'custom' : 'drawing';
    const modeBtns = modeWrap ? Array.from(modeWrap.querySelectorAll('.unit-btn')) : [];
    const syncMode = () => {
      for (const b of modeBtns) b.classList.toggle('active', b.dataset.value === nameMode);
      if (spaceField) spaceField.style.display = nameMode === 'custom' ? '' : 'none';
    };

    if (spaceInput) {
      const savedSpace = localStorage.getItem('rd_share_space');
      spaceInput.value = savedSpace !== null ? savedSpace : this._deriveSpaceName(rd);
    }
    syncMode();
    // END CUSTOM_ROOMDESIGNER_BY_CLAUDE

    /* CUSTOM_ROOMDESIGNER_BY_CLAUDE — the page list.
       This replaced a checkbox tree, and the reason is worth keeping: a tick cannot
       say "this zone is on page 2 AND again on page 4", and it has nowhere to hang a
       per-page rotation or an ordering. So the picker IS the document now — an
       ordered list of pages you build, where any zone may appear on as many as you
       like and each row carries its own rotation.

       A page is one of three kinds:
         space  — the whole room, which is what every page used to be
         zones  — frame on these zones of that room, together
         rect   — frame on a rectangle dragged on the plan, for a close-up

       All three are the same thing to the generator: a set of points to frame on.
       The kinds exist here, in the UI, because they are what a person picks. */
    const pagesWrap = document.getElementById('rdPdfPages');
    const addBtn = document.getElementById('rdPdfAddPage');
    const addForm = document.getElementById('rdPdfAddForm');
    const previewWrap = document.getElementById('rdPdfPreview');
    const previewBtn = document.getElementById('rdPdfPreviewBtn');
    const printRooms = rd.rooms.filter(r => r.closed && r.type === 'add');

    const zonesOf = (room) => {
      const ov = window.roomDesignerOverlay;
      if (!ov || !ov._getZonesForRoom) return [];
      try { return ov._getZonesForRoom(room) || []; } catch (e) { return []; }
    };
    const roomLabel = (ri) => (printRooms[ri] && printRooms[ri].name) || ('Space ' + (ri + 1));
    const zoneLabel = (ri, zi) => {
      const room = printRooms[ri];
      return (room && room.zoneNames && room.zoneNames[zi]) || ('Zone ' + (zi + 1));
    };

    /* Restored, then validated — never trusted. Indices go stale the moment a space
       or a divider is deleted, so anything pointing past the end is dropped and a
       zones page whose zones have all gone is dropped with them. A rect needs none
       of this: it is stored in world feet and has no index to rot. */
    const defaultPages = () => printRooms.map((_, ri) => ({ kind: 'space', roomIndex: ri, zoneIdxs: [], rect: null, rotateTurns: null }));
    let pages;
    {
      const saved = rd._pdfPageSelection && rd._pdfPageSelection.pages;
      pages = Array.isArray(saved) ? saved.reduce((acc, p) => {
        const ri = p.roomIndex;
        if (typeof ri !== 'number' || ri < 0 || ri >= printRooms.length) return acc;
        const turns = (p.rotateTurns == null) ? null : (((p.rotateTurns % 4) + 4) % 4);
        if (p.kind === 'rect' && p.rect && p.rect.maxX > p.rect.minX && p.rect.maxY > p.rect.minY) {
          acc.push({ kind: 'rect', roomIndex: ri, zoneIdxs: [], rect: p.rect, rotateTurns: turns });
        } else if (p.kind === 'zones') {
          const n = zonesOf(printRooms[ri]).length;
          const zs = (p.zoneIdxs || []).filter(zi => zi >= 0 && zi < n);
          if (zs.length) acc.push({ kind: 'zones', roomIndex: ri, zoneIdxs: zs, rect: null, rotateTurns: turns });
        } else {
          acc.push({ kind: 'space', roomIndex: ri, zoneIdxs: [], rect: null, rotateTurns: turns });
        }
        return acc;
      }, []) : defaultPages();
      // A remembered list that validated down to nothing becomes the default
      // document rather than an empty picker with no obvious reason why.
      if (!pages.length) pages = defaultPages();
    }

    const pageTitle = (p) => {
      if (p.kind === 'rect') return roomLabel(p.roomIndex) + ' — close-up';
      if (p.kind === 'zones') return roomLabel(p.roomIndex) + ' — ' + p.zoneIdxs.map(zi => zoneLabel(p.roomIndex, zi)).join(' + ');
      return roomLabel(p.roomIndex) + ' — whole space';
    };
    const TURN_LABEL = ['0°', '90°', '180°', '270°'];
    const turnLabel = (t) => (t == null ? 'Auto' : TURN_LABEL[t]);

    const rememberSelection = () => {
      rd._pdfPageSelection = { pages: pages.map(p => ({
        kind: p.kind, roomIndex: p.roomIndex, zoneIdxs: p.zoneIdxs.slice(),
        rect: p.rect ? { ...p.rect } : null, rotateTurns: p.rotateTurns
      })) };
    };

    // Shared with post-save variant generation — see rd._pagePlanFromSelection.
    const buildPagePlan = () => rd._pagePlanFromSelection({ pages }) || [];

    const iconBtn = (glyph, title, onClick, disabled) => {
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'rd-pdf-row-btn';
      b.textContent = glyph;
      b.title = title;
      b.disabled = !!disabled;
      b.addEventListener('click', onClick);
      return b;
    };

    const renderPages = () => {
      if (!pagesWrap) return;
      pagesWrap.innerHTML = '';
      pages.forEach((p, i) => {
        const row = document.createElement('div');
        row.className = 'rd-pdf-page-row';
        const num = document.createElement('span');
        num.className = 'rd-pdf-page-num';
        num.textContent = (i + 1);
        const name = document.createElement('span');
        name.className = 'rd-pdf-page-name';
        name.textContent = pageTitle(p);
        row.appendChild(num); row.appendChild(name);

        // Auto -> 0 -> 90 -> 180 -> 270 -> Auto. Explicit 0 is in the cycle on
        // purpose: "the fit rotated this and I don't want it to" is the whole
        // reason the override exists, and Auto cannot express it.
        const rot = iconBtn('↻', 'Rotate this page', () => {
          p.rotateTurns = (p.rotateTurns == null) ? 0 : (p.rotateTurns >= 3 ? null : p.rotateTurns + 1);
          rememberSelection(); renderPages(); schedulePreview();
        });
        const rotLbl = document.createElement('span');
        rotLbl.className = 'rd-pdf-rot-label';
        rotLbl.textContent = turnLabel(p.rotateTurns);
        row.appendChild(rot); row.appendChild(rotLbl);

        row.appendChild(iconBtn('↑', 'Move up', () => {
          if (i === 0) return;
          pages.splice(i - 1, 0, pages.splice(i, 1)[0]);
          rememberSelection(); renderPages(); schedulePreview();
        }, i === 0));
        row.appendChild(iconBtn('↓', 'Move down', () => {
          if (i === pages.length - 1) return;
          pages.splice(i + 1, 0, pages.splice(i, 1)[0]);
          rememberSelection(); renderPages(); schedulePreview();
        }, i === pages.length - 1));
        row.appendChild(iconBtn('✕', 'Remove this page', () => {
          pages.splice(i, 1);
          rememberSelection(); renderPages(); updateState(); schedulePreview();
        }));
        pagesWrap.appendChild(row);
      });
      if (!pages.length) {
        const empty = document.createElement('div');
        empty.className = 'rd-pdf-empty';
        empty.textContent = 'No pages yet — add one.';
        pagesWrap.appendChild(empty);
      }
    };

    // ── Add page ──
    let addOpen = false;
    const closeAddForm = () => { addOpen = false; if (addForm) { addForm.style.display = 'none'; addForm.innerHTML = ''; } };
    const renderAddForm = () => {
      if (!addForm) return;
      addForm.innerHTML = '';
      addForm.style.display = '';
      const sel = document.createElement('select');
      sel.className = 'rd-pdf-add-select';
      printRooms.forEach((r, ri) => {
        const o = document.createElement('option');
        o.value = String(ri); o.textContent = roomLabel(ri);
        sel.appendChild(o);
      });
      addForm.appendChild(sel);

      const zoneBox = document.createElement('div');
      zoneBox.className = 'rd-pdf-add-zones';
      const renderZoneBox = () => {
        zoneBox.innerHTML = '';
        const ri = parseInt(sel.value, 10);
        const zs = zonesOf(printRooms[ri]);
        if (zs.length <= 1) {
          // A room with no dividers reports one zone covering itself, so "zones"
          // and "whole space" would be the same page.
          zoneBox.textContent = 'This space has no zones to choose from.';
          zoneBox.classList.add('rd-pdf-add-note');
          return;
        }
        zoneBox.classList.remove('rd-pdf-add-note');
        zs.forEach((_, zi) => {
          const lab = document.createElement('label');
          lab.className = 'rd-pdf-add-zone';
          const cb = document.createElement('input');
          cb.type = 'checkbox'; cb.className = 'rd-pdf-page-cb'; cb.dataset.zi = String(zi);
          const sp = document.createElement('span');
          sp.textContent = zoneLabel(ri, zi);
          lab.appendChild(cb); lab.appendChild(sp);
          zoneBox.appendChild(lab);
        });
      };
      sel.addEventListener('change', renderZoneBox);
      renderZoneBox();
      addForm.appendChild(zoneBox);

      const actions = document.createElement('div');
      actions.className = 'rd-pdf-add-actions';
      const mk = (text, cls, fn) => {
        const b = document.createElement('button');
        b.type = 'button'; b.className = cls; b.textContent = text;
        b.addEventListener('click', fn);
        return b;
      };
      const addPage = (page) => {
        pages.push(page);
        rememberSelection(); closeAddForm(); renderPages(); updateState(); schedulePreview();
      };
      actions.appendChild(mk('Whole space', 'rd-pdf-add-btn', () => {
        addPage({ kind: 'space', roomIndex: parseInt(sel.value, 10), zoneIdxs: [], rect: null, rotateTurns: null });
      }));
      actions.appendChild(mk('Selected zones', 'rd-pdf-add-btn', () => {
        const ri = parseInt(sel.value, 10);
        const zs = Array.from(zoneBox.querySelectorAll('input:checked')).map(c => parseInt(c.dataset.zi, 10));
        if (!zs.length) { setStatus('Tick at least one zone.', true); return; }
        addPage({ kind: 'zones', roomIndex: ri, zoneIdxs: zs, rect: null, rotateTurns: null });
      }));
      actions.appendChild(mk('Draw a region…', 'rd-pdf-add-btn', () => {
        const ri = parseInt(sel.value, 10);
        closeAddForm();
        // Step the dialog aside so the whole plan is reachable, then hand the canvas
        // over. It comes back with a world rect, or null if Esc'd or the drag was a
        // click. The dialog reopens either way — vanishing on cancel would read as a
        // crash.
        overlay.classList.remove('visible');
        rd.beginRegionPick((rect) => {
          overlay.classList.add('visible');
          if (rect) {
            // Attribute the region to whichever space actually contains it, falling
            // back to the one chosen in the form. The room decides what else renders
            // around the close-up, so guessing wrong shows the wrong neighbours.
            const cx = (rect.minX + rect.maxX) / 2, cy = (rect.minY + rect.maxY) / 2;
            let hit = ri;
            printRooms.forEach((room, idx) => {
              const geo = rd._getRoomGeoCache(room);
              if (geo && geo.roomPolygon && rd.pointInPolygon(cx, cy, geo.roomPolygon)) hit = idx;
            });
            pages.push({ kind: 'rect', roomIndex: hit, zoneIdxs: [], rect, rotateTurns: null });
            rememberSelection();
          }
          renderPages(); updateState(); schedulePreview();
        });
      }));
      actions.appendChild(mk('Cancel', 'rd-pdf-add-cancel', closeAddForm));
      addForm.appendChild(actions);
    };
    const handleAdd = () => { addOpen = !addOpen; if (addOpen) renderAddForm(); else closeAddForm(); };

    // ── Preview ──
    /* CUSTOM_ROOMDESIGNER_BY_CLAUDE — the preview is OPT-IN, and that is the whole
       point of it. A thumbnail is a full print render per page: the same
       _generatePerSpacePrintCanvases the PDF itself runs, on a 3600x2000 canvas,
       once per page. On a large plan with several pages that is seconds of blocking
       main-thread canvas work — and it was firing on a 400ms debounce after every
       add, remove, reorder and rotate, plus once on open. Opening the dialog to
       reorder two pages paid for a full render pass you never asked to look at, and
       could hang the tab.

       ⚠️ So rendering now happens ONLY on an explicit click. Nothing schedules it:
       an edit marks what is on screen as out of date, it does not queue a new one.
       That is the guarantee — there is no debounce left to be unlucky with. */
    let previewTimer = null;   // kept only so the cleanup path stays harmless
    let previewRun = 0;
    let previewLoaded = false;
    let previewStale = false;
    /* ⚠️ Not the same thing as the run token. The token decides which RESULT wins;
       this stops a second render STARTING. They are not interchangeable: a render
       swaps rd.ctx / rd.canvas / panX / zoomLevel for a print canvas and puts them
       back in a finally, so two overlapping runs interleave those save/restores and
       can leave the live canvas pointing at a discarded offscreen one. Reachable
       because the texture preload awaits up to 5s, which is a window where clicks
       are still processed — and marking the preview stale re-enables the button. */
    let previewBusy = false;
    const setPreviewIdle = (msg) => {
      if (!previewWrap) return;
      previewWrap.classList.remove('stale');
      previewWrap.innerHTML = '<div class="rd-pdf-empty">' + msg + '</div>';
    };
    /* One place decides what the section looks like, so the button label and the
       thumbnails cannot disagree about whether what you are looking at is current. */
    const updatePreviewChrome = () => {
      if (previewBtn) {
        previewBtn.textContent = previewLoaded ? 'Refresh' : 'Load preview';
        previewBtn.disabled = !pages.length || previewBusy;
      }
      if (!previewWrap) return;
      if (!previewLoaded) {
        setPreviewIdle(pages.length ? 'Preview is off — click Load preview.' : 'Add a page to preview it.');
        return;
      }
      // ⚠️ Stale thumbnails are DIMMED, never silently left looking current — a
      // preview that does not match the page list beside it is worse than none.
      previewWrap.classList.toggle('stale', previewStale);
    };
    const renderPreview = async () => {
      if (!previewWrap || !pages.length || previewBusy) { updatePreviewChrome(); return; }
      const run = ++previewRun;
      previewBusy = true;
      previewWrap.classList.remove('stale');
      previewWrap.innerHTML = '<div class="rd-pdf-empty">Rendering…</div>';
      if (previewBtn) previewBtn.disabled = true;
      let imgs = null;
      rd._printPagePlan = buildPagePlan();
      try {
        // Quality 1 — these are thumbnails, and a full-resolution render per page
        // is a lot of work to look at something 120px wide.
        imgs = await rd._generatePerSpacePrintCanvases(1);
      } catch (e) { imgs = null; }
      finally { rd._printPagePlan = null; previewBusy = false; }
      // A newer request started while this one rendered; its result is the one that counts.
      if (run !== previewRun) { updatePreviewChrome(); return; }
      previewLoaded = true;
      previewStale = false;
      previewWrap.innerHTML = '';
      if (!imgs || !imgs.length) {
        previewWrap.innerHTML = '<div class="rd-pdf-empty">Nothing to preview.</div>';
        updatePreviewChrome();
        return;
      }
      imgs.forEach((img, i) => {
        const cell = document.createElement('div');
        cell.className = 'rd-pdf-thumb';
        const im = document.createElement('img');
        im.src = img.dataUrl; im.alt = img.spaceName || ('Page ' + (i + 1));
        const cap = document.createElement('span');
        cap.className = 'rd-pdf-thumb-cap';
        cap.textContent = (i + 1) + '. ' + (img.spaceName || '');
        cell.appendChild(im); cell.appendChild(cap);
        previewWrap.appendChild(cell);
      });
      updatePreviewChrome();
    };
    /* Was schedulePreview() — every caller that changes the page list still calls
       it, so nothing at the call sites had to change; it just no longer starts a
       render. An in-flight one is orphaned, because its result describes a page
       list that no longer exists. */
    const schedulePreview = () => {
      clearTimeout(previewTimer);
      previewRun++;
      if (previewLoaded) previewStale = true;
      updatePreviewChrome();
    };

    const hasClosedRoom = rd.rooms.some(r => r.closed && r.type === 'add');
    const setStatus = (text, isError) => {
      if (!statusEl) return;
      statusEl.textContent = text;
      statusEl.classList.toggle('error', !!isError);
    };
    setStatus('');

    // Button state
    const updateState = () => {
      // CUSTOM_ROOMDESIGNER_BY_CLAUDE — capture dialog values live so a later Save embeds them on the design.
      if (spaceInput) localStorage.setItem('rd_share_space', spaceInput.value);
      localStorage.setItem('rd_pdf_name_mode', nameMode);
      // Only the "one name" mode needs anything typed — taking the names off the
      // drawing needs nothing at all, so the buttons stay live.
      const ready = nameMode === 'drawing'
        || (spaceInput && spaceInput.value.trim().length > 0);
      // A PDF of nothing is not a PDF. An empty picker disables the buttons rather
      // than quietly producing a zero-page document.
      const hasPages = buildPagePlan().length > 0;
      // END CUSTOM_ROOMDESIGNER_BY_CLAUDE
      if (planBtn) planBtn.disabled = !ready || !hasClosedRoom || !hasPages;
      if (fullBtn) fullBtn.disabled = !ready || !hasClosedRoom || !hasPages;
      if (seamBtn) {
        const hasRoll = rd.rooms.some(r => {
          if (!r.closed || r.type !== 'add') return false;
          const ov = window.roomDesignerOverlay;
          if (!ov) return false;
          if (r.flooring) { const p = ov._getProduct(r.flooring.productId); if (p && p.shape === 'roll') return true; }
          if (r.zoneFlooring) { for (const zf of r.zoneFlooring) { const p = ov._getProduct(zf.productId); if (p && p.shape === 'roll') return true; } }
          return false;
        });
        seamBtn.disabled = !ready || !hasClosedRoom || !hasRoll || !hasPages;
      }
    };
    rememberSelection();
    renderPages();
    updateState();
    schedulePreview();

    overlay.classList.add('visible');
    setTimeout(() => { if (nameMode === 'custom' && spaceInput) spaceInput.focus(); }, 100);

    // Build metadata from fields
    const getMeta = () => {
      // CUSTOM_ROOMDESIGNER_BY_CLAUDE — a space name is sent ONLY in "one name" mode.
      // Left out, every page header falls back to that space's own name, which is
      // what "from the drawing" means. Room names are never written to — see the note
      // at the top of this dialog.
      const spaceName = nameMode === 'custom' && spaceInput ? spaceInput.value.trim() : '';
      if (spaceName) localStorage.setItem('rd_share_space', spaceName);
      // Get rep info — portal mode reads session directly, public mode uses REP_ROSTER
      let senderName = '';
      let repId = '', repEmail = '', repPhone = '', repHeadshotFile = '', repHeadshotUrl = '';
      try {
        const sess = JSON.parse(sessionStorage.getItem('rep_portal_session') || '{}');
        senderName = sess.name || '';
        if (rd._portalMode && sess.fullName) {
          repId = sess.fullName.toLowerCase().replace(/\s+/g, '-');
          repEmail = sess.email || '';
          repPhone = sess.phone || '';
          repHeadshotUrl = sess.headshotUrl || '';
        } else {
          repId = localStorage.getItem('rd_share_rep_id') || '';
          const REP_ROSTER = [
            { id: 'john-warner', name: 'John Warner', email: 'john@blueskyfitsupply.com', phone: '(303) 264-9496', headshotFile: 'rep-headshot-john-warner.webp' },
            { id: 'ryan-walker', name: 'Ryan Walker', email: 'ryan@blueskyfitsupply.com', phone: '(720) 515-3585', headshotFile: 'rep-headshot-ryan-walker.webp' }
          ];
          const rep = REP_ROSTER.find(r => r.id === repId);
          if (rep) { repEmail = rep.email; repPhone = rep.phone; repHeadshotFile = rep.headshotFile; }
        }
      } catch(e) {}
      const meta = { senderName };
      if (spaceName) meta.spaceName = spaceName;
      if (repId) {
        meta.repId = repId; meta.repEmail = repEmail; meta.repPhone = repPhone;
        if (repHeadshotUrl) meta.repHeadshotUrl = repHeadshotUrl;
        if (repHeadshotFile) meta.repHeadshotFile = repHeadshotFile;
      }
      return meta;
    };

    const designData = rd._serializeDesign();

    const handleMode = (e) => {
      const btn = e.target.closest('.unit-btn');
      if (!btn) return;
      nameMode = btn.dataset.value === 'custom' ? 'custom' : 'drawing';
      syncMode();
      updateState();
      if (nameMode === 'custom' && spaceInput) spaceInput.focus();
    };

    const cleanup = () => {
      overlay.classList.remove('visible');
      if (closeBtn) closeBtn.removeEventListener('click', handleClose);
      if (planBtn) planBtn.removeEventListener('click', handlePlan);
      if (fullBtn) fullBtn.removeEventListener('click', handleFull);
      if (seamBtn) seamBtn.removeEventListener('click', handleSeam);
      if (modeWrap) modeWrap.removeEventListener('click', handleMode);
      if (addBtn) addBtn.removeEventListener('click', handleAdd);
      clearTimeout(previewTimer);
      previewRun++;              // orphan any preview still rendering
      rd._printPagePlan = null;
      if (spaceInput) { spaceInput.removeEventListener('input', updateState); spaceInput.removeEventListener('keydown', stopProp); spaceInput.removeEventListener('keyup', stopProp); }
    };
    const stopProp = (e) => { e.stopPropagation(); if (e.key === 'Escape') cleanup(); };
    const handleClose = () => cleanup();

    /* CUSTOM_ROOMDESIGNER_BY_CLAUDE — _printPagePlan is set for the duration of one
       generate and cleared in the finally, never left on the instance. The same
       per-space image builder feeds share links, the quote handoff and the Path-B
       variants; a plan left behind would silently re-frame all of them. */
    const withPagePlan = async (work) => {
      rd._printPagePlan = buildPagePlan();
      try { return await work(); } finally { rd._printPagePlan = null; }
    };

    /* CUSTOM_ROOMDESIGNER_BY_CLAUDE — say WHY it failed.
       ⚠️ All three of these caught the error, threw it away, and showed one fixed
       string. That is the same shape as the preview banner earlier today, and it
       costs the same thing: a round trip through Ryan with a console open before
       anyone can even name the cause. The reason is in hand at the moment it
       happens; keep it. Short in the status line, full text on hover, and the first
       stack frame in the console because a message alone rarely says WHERE. */
    const pdfFailed = (label, e) => {
      const msg = (e && e.message) || String(e || 'unknown error');
      const frame = (e && e.stack) ? String(e.stack).split(/\r?\n/)[1] : '';
      try { console.warn('[GLD PDF] ' + label + ' failed: ' + msg + (frame ? ' @ ' + frame.trim() : '')); } catch (_e) {}
      setStatus(label + ' failed: ' + msg.slice(0, 90), true);
      try { if (statusEl) statusEl.title = msg + (frame ? ' | ' + frame.trim() : ''); } catch (_e2) {}
    };

    const handlePlan = async () => {
      if (!hasClosedRoom) { setStatus('Draw a closed room first.', true); return; }
      setStatus('Generating floor plan PDF...');
      try {
        const meta = getMeta();
        const url = await withPagePlan(() => this._handleSharePlanPdf(designData, meta));
        if (url) { setStatus('PDF ready!'); window.open(url, '_blank'); setTimeout(cleanup, 1200); }
      } catch(e) { pdfFailed('Floor plan PDF', e); }
    };
    const handleFull = async () => {
      if (!hasClosedRoom) { setStatus('Draw a closed room first.', true); return; }
      setStatus('Generating full packet PDF...');
      try {
        const meta = getMeta();
        const url = await withPagePlan(() => this._handleShareFullPacket(designData, meta));
        if (url) { setStatus('Full packet ready!'); window.open(url, '_blank'); setTimeout(cleanup, 1200); }
      } catch(e) { pdfFailed('Full packet PDF', e); }
    };
    const handleSeam = async () => {
      if (!hasClosedRoom) { setStatus('Draw a closed room first.', true); return; }
      setStatus('Generating seam plan PDF...');
      try {
        const meta = getMeta();
        const url = await withPagePlan(() => this._handleShareSeamPdf(designData, meta));
        if (url) { setStatus('Seam plan ready!'); window.open(url, '_blank'); setTimeout(cleanup, 1200); }
      } catch(e) { pdfFailed('Seam plan PDF', e); }
    };

    if (closeBtn) closeBtn.addEventListener('click', handleClose);
    if (planBtn) planBtn.addEventListener('click', handlePlan);
    if (fullBtn) fullBtn.addEventListener('click', handleFull);
    if (seamBtn) seamBtn.addEventListener('click', handleSeam);
    if (modeWrap) modeWrap.addEventListener('click', handleMode);
    if (addBtn) addBtn.addEventListener('click', handleAdd);
    if (previewBtn) previewBtn.addEventListener('click', () => renderPreview());
    if (spaceInput) { spaceInput.addEventListener('input', updateState); spaceInput.addEventListener('keydown', stopProp); spaceInput.addEventListener('keyup', stopProp); }
  }

  /**
   * A sensible default space name from the rooms the user has actually named.
   * Shared by the share dialog's prefill and the PDF dialog — it was duplicated,
   * and a second copy is how the two drift apart.
   */
  _deriveSpaceName(rd) {
    const addRooms = rd.rooms.filter(r => r.closed && r.type === 'add');
    const named = addRooms.map(r => r.name || '').filter(n => n && !/^Space \d+$/.test(n));
    if (named.length === 0) return '';
    if (named.length === 1) return named[0];
    const base = named[0].replace(/\s+\d+$/, '');
    const allSameBase = named.every(n => n === base || n.replace(/\s+\d+$/, '') === base);
    return allSameBase ? base : named.join(', ');
  }

  async _loadFromShareLink() {
    const hash = window.location.hash;
    if (!hash || !hash.startsWith('#plan=')) return false;
    /* CUSTOM_PROJECTASSEMBLY_BY_CLAUDE (Ryan 2026-09-23) — `#plan=s.XXXXXXXX&edit` is the
       presentation's "Try the free Gym Layout Designer" door: the SAME share, opened as an
       editable copy in the ordinary designer (header and footer back), the way a non-view-only
       share link has always loaded. The flag is stripped before the id is read. */
    const editWanted = /[&;]edit\b/.test(hash);
    const encoded = hash.slice(6).replace(/[&;]edit\b.*$/, '');
    const _presentRoot = document.documentElement;
    try {
      let parsed;

      if (encoded.startsWith('s.')) {
        // Server-side share link: fetch from worker KV
        const shareId = encoded.slice(2);
        this.rd._viewOnlyShareId = shareId;
        const res = await fetch('https://rfq-worker.ryan-29b.workers.dev/share/' + shareId);
        if (!res.ok) {
          const rd = this.rd;
          const errMsg = res.status === 404
            ? 'This share link has expired or is invalid.'
            : 'Failed to load shared design. Please try again.';
          rd.updateStatus(errMsg, { error: true });
          const errRoot = rd.canvas.closest('.room-designer');
          if (errRoot) errRoot.classList.remove('view-only-pending');
          _presentRoot.classList.remove('rd-present');
          history.replaceState(null, '', window.location.pathname + window.location.search);
          return false;
        }
        const result = await res.json();
        parsed = result.data;
      } else {
        // Legacy hash-encoded link (deflate or raw base64)
        let json;
        if (encoded.startsWith('r.')) {
          json = decodeURIComponent(escape(atob(encoded.slice(2).replace(/-/g, '+').replace(/_/g, '/'))));
        } else {
          const b64 = encoded.replace(/-/g, '+').replace(/_/g, '/');
          const binary = atob(b64);
          const bytes = new Uint8Array(binary.length);
          for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
          const ds = new DecompressionStream('deflate');
          const stream = new Blob([bytes]).stream().pipeThrough(ds);
          json = await new Response(stream).text();
        }
        parsed = JSON.parse(json);
      }

      const rd = this.rd;

      // Decompress design if it was compressed for server storage
      if (parsed.designCompressed && !parsed.design) {
        try {
          const b64 = parsed.designCompressed;
          const binary = atob(b64);
          const bytes = new Uint8Array(binary.length);
          for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
          const ds = new DecompressionStream('deflate');
          const json = await new Response(new Blob([bytes]).stream().pipeThrough(ds)).text();
          parsed.design = JSON.parse(json);
          delete parsed.designCompressed;
        } catch (e) {
          console.error('Failed to decompress shared design:', e);
          this.rd.updateStatus('Failed to load shared design.', { error: true });
          return false;
        }
      }

      // New format: { meta: { senderName, landingTab }, design: {...} }
      // Legacy format: bare design data (no meta property)
      let designData, meta;
      if (parsed.meta && parsed.design) {
        meta = parsed.meta;
        designData = parsed.design;
      } else {
        meta = null;
        designData = parsed;
      }

      rd._loadDesignData(designData);

      // View-only mode: skip normal post-load flow
      if (meta && meta.viewOnly && !editWanted) {
        this._activateViewOnlyMode(meta);
        return true;
      }

      // Not view-only — remove the pre-hide class (and the presentation's chrome-hide, if the
      // liquid pre-flag set it for a legacy link that turned out not to be view-only).
      const pendingRoot = rd.canvas.closest('.room-designer');
      if (pendingRoot) pendingRoot.classList.remove('view-only-pending');
      _presentRoot.classList.remove('rd-present');
      if (editWanted) {
        // An editable copy is theirs; a reply to the rep's share is not a thing here.
        rd._viewOnlyShareId = null;
        history.replaceState(null, '', window.location.pathname + window.location.search);
      }

      const senderName = meta && meta.senderName ? meta.senderName : '';
      const name = senderName ? 'Shared from ' + senderName : 'Shared Design';
      const _sharePrefix = this._getRepPrefix();
      localStorage.setItem(_sharePrefix + 'rd_autosave', JSON.stringify(designData));
      localStorage.setItem(_sharePrefix + 'rd_autosave_time', String(Date.now()));
      this._updateDesignNameUI(name);
      rd.updateStatus('Shared design loaded!');

      // Switch to landing tab if specified
      if (meta && meta.landingTab) {
        const overlay = window.roomDesignerOverlay;
        if (overlay) {
          const tab = document.querySelector('.rd-step-tab[data-step="' + meta.landingTab + '"]');
          // Fall back to draw if landing tab is disabled
          const targetStep = (tab && !tab.classList.contains('disabled')) ? meta.landingTab : 'draw';
          overlay._applyStepChange(targetStep);
          // Sync tab UI
          document.querySelectorAll('.rd-step-tab').forEach(t => t.classList.remove('active'));
          const activeTab = document.querySelector('.rd-step-tab[data-step="' + targetStep + '"]');
          if (activeTab) activeTab.classList.add('active');
        }
      }

      // Show welcome modal
      const shareMessage = meta && meta.message ? meta.message : '';
      const receiverName = meta && meta.receiverName ? meta.receiverName : '';
      if (senderName || shareMessage || receiverName) {
        this._showShareBanner(senderName, shareMessage, receiverName);
      }

      return true;
    } catch (e) {
      console.warn('[RD] Failed to load shared design:', e);
      const failRoot = this.rd.canvas.closest('.room-designer');
      if (failRoot) failRoot.classList.remove('view-only-pending');
      return false;
    }
  }

  _showShareBanner(senderName, message, receiverName) {
    const overlay = document.getElementById('rdShareWelcomeOverlay');
    const title = document.getElementById('rdShareWelcomeTitle');
    const msgEl = document.getElementById('rdShareWelcomeMessage');
    const btn = document.getElementById('rdShareWelcomeBtn');
    if (!overlay || !title || !btn) return;

    let titleText;
    if (receiverName && senderName) {
      titleText = 'Hi ' + receiverName + ', ' + senderName + ' shared a gym layout with you';
    } else if (senderName) {
      titleText = senderName + ' shared a gym layout with you';
    } else {
      titleText = 'Someone shared a gym layout with you';
    }
    title.textContent = titleText;

    if (message && msgEl) {
      msgEl.textContent = message;
      msgEl.style.display = '';
    } else if (msgEl) {
      msgEl.style.display = 'none';
    }

    overlay.classList.add('visible');

    const dismiss = () => {
      overlay.classList.remove('visible');
      btn.removeEventListener('click', dismiss);
    };
    btn.addEventListener('click', dismiss);
  }

  // ── View-Only Mode ──────────────────────

  _activateViewOnlyMode(meta) {
    const rd = this.rd;
    rd._viewOnly = true;
    rd._viewOnlyMeta = meta;
    rd._viewOnlyReplySent = false;

    // Snapshot customizable state for diff later
    rd._viewOnlyOriginalBrandColors = JSON.parse(JSON.stringify(rd._brandColors || {}));
    rd._viewOnlyOriginalTurf = JSON.parse(JSON.stringify(
      (rd.objects || []).filter(o => o.type === 'turf').map(o => ({
        id: o.id, turfColorId: o.turfColorId, turfColorHex: o.turfColorHex,
        turfLines: o.turfLines, turfHashes: o.turfHashes,
        turfBorderLine: o.turfBorderLine, turfBorderLineEdges: o.turfBorderLineEdges,
        turfLineColor: o.turfLineColor, turfMarkingsRotated: o.turfMarkingsRotated,
        turfLineDirection: o.turfLineDirection,
        turfYardNumbers: o.turfYardNumbers, turfHalfwayLine: o.turfHalfwayLine, turfLineOrigin: o.turfLineOrigin,
        turfCenterHashes: o.turfCenterHashes, turfHashSides: o.turfHashSides,
        turfHashSpacing: o.turfHashSpacing
      }))
    ));

    // Add view-only class to root element
    const root = rd.canvas.closest('.room-designer');
    if (root) root.classList.add('view-only');
    /* CUSTOM_PROJECTASSEMBLY_BY_CLAUDE (Ryan 2026-09-23) — PRESENTATION IS FULL-SCREEN, like the
       Weight Designer's: *"you also don't see all the extra fluff on the page, no scrolling"*. The
       html class (normally set by the section's pre-flag before first paint; set here too for a
       legacy hash link) hides the theme header, footer, overlay group, chat bubble and reviews
       bubble, pins the designer to the viewport and locks scroll — custom-room-designer.css,
       "html.rd-present". The page's own H1/subtitle are hidden with it, so the old "shared this
       gym layout with you" subtitle is not written any more. */
    document.documentElement.classList.add('rd-present');
    // Remove pending class now that the presentation chrome is in place
    if (root) root.classList.remove('view-only-pending');

    // Hide sibling sections below the designer (KB, etc.)
    const sectionEl = root ? root.closest('.shopify-section') : null;
    if (sectionEl) {
      let sibling = sectionEl.nextElementSibling;
      while (sibling) {
        sibling.style.display = 'none';
        sibling = sibling.nextElementSibling;
      }
    }
    /* CUSTOM_PROJECTASSEMBLY_BY_CLAUDE (Ryan 2026-09-23) — ONE SUBTLE DOOR, and nothing else
       under the plan. The greeting ("Hi there! Bookmark this page…"), the sign-off, the rep card
       and the "Looks Good!" reply button that used to sit below the canvas are gone from the
       presentation: *"The one thing I say we keep is the link which says 'Try the free Gym Layout
       Designer' which can be positioned under the window off to the right, keep it subtle. then
       when that's clicked, it opens up the same GLD plan but in edit mode on the main site with
       the header and footer visible again."* So the link carries THIS share's id plus `&edit`,
       which _loadFromShareLink reads as "load the same plan as an editable copy". A legacy
       hash-encoded link has no id to carry and opens the blank designer, as before. */
    {
      const _designerPath = this.rd._portalMode ? '/pages/gym-layout-designer' : window.location.pathname;
      const designerUrl = window.location.origin + _designerPath +
        (rd._viewOnlyShareId ? ('#plan=s.' + rd._viewOnlyShareId + '&edit') : '');
      // The pencil circle at the bottom of the floating column (Ryan, same day: a circular button
      // with the others, "a symbol that makes sense"). The element lives in the liquid.
      const tryBtn = document.getElementById('rdViewOnlyTryBtn');
      if (tryBtn) { tryBtn.href = designerUrl; tryBtn.style.display = ''; }
    }

    // Force hand tool (pan only)
    rd.setActiveTool('hand');

    // Show floating controls
    const controls = document.getElementById('rdViewOnlyControls');
    if (controls) {
      controls.style.display = '';
      const zoomIn = document.getElementById('rdViewOnlyZoomIn');
      const zoomOut = document.getElementById('rdViewOnlyZoomOut');
      const fit = document.getElementById('rdViewOnlyFit');
      const dimToggle = document.getElementById('rdViewOnlyDimToggle');
      const darkToggle = document.getElementById('rdViewOnlyDarkToggle');
      const _vib = () => { if (navigator.vibrate) navigator.vibrate(50); };
      const _flash = (btn) => { btn.classList.add('tap-flash'); setTimeout(() => btn.classList.remove('tap-flash'), 180); };
      if (zoomIn) zoomIn.addEventListener('click', () => { _vib(); _flash(zoomIn); rd.zoom(1.3); });
      if (zoomOut) zoomOut.addEventListener('click', () => { _vib(); _flash(zoomOut); rd.zoom(1 / 1.3); });
      if (fit) fit.addEventListener('click', () => { _vib(); _flash(fit); rd.fitToView(); });
      if (dimToggle) {
        dimToggle.classList.toggle('active', rd.showMeasurements);
        dimToggle.addEventListener('click', () => {
          _vib();
          rd.showMeasurements = !rd.showMeasurements;
          dimToggle.classList.toggle('active', rd.showMeasurements);
          rd.render();
        });
      }
      const sunIcon = '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="5"/><line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/><line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/><line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/><line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/></svg>';
      const moonIcon = '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/></svg>';
      const updateToggleIcon = () => { if (darkToggle) darkToggle.innerHTML = rd.darkMode ? sunIcon : moonIcon; };
      updateToggleIcon();
      if (darkToggle) darkToggle.addEventListener('click', () => {
        _vib();
        rd.darkMode = !rd.darkMode;
        localStorage.setItem('rd_darkMode', rd.darkMode ? '1' : '0');
        const rootEl = rd.canvas.closest('.room-designer');
        if (rootEl) rootEl.classList.toggle('dark-mode', rd.darkMode);
        if (rd._rebuildEquipmentTheme) rd._rebuildEquipmentTheme();
        if (rd._hoverCalloutEl) rd._dismissHoverCallout();
        updateToggleIcon();
        rd.render();
      });
    }

    // Download PDF button
    const pdfBtn = document.getElementById('rdViewOnlyPdfBtn');
    if (pdfBtn) {
      pdfBtn.addEventListener('click', async () => {
        if (pdfBtn.disabled) return;
        if (navigator.vibrate) navigator.vibrate(50);
        pdfBtn.disabled = true;
        pdfBtn.classList.add('generating');
        rd.updateStatus('Generating PDF...');
        try {
          const images = await this._uploadSpaceImages(rd);
          if (!images) throw new Error('No images generated');
          const { spaceSummary, totalArea } = this._buildSpaceSummary(rd);
          // Build a NEW presentation URL reflecting current customizations (colors, turf, etc.)
          const designData = rd._serializeDesign();
          const presUrl = await this._buildPresentationUrl(designData, meta) || window.location.href;
          const repName = meta.senderName || '';
          const repEmail = meta.repEmail || '';
          const repPhone = meta.repPhone || '';
          const customerName = meta.receiverName || '';
          // Collect flooring + equipment items via overlay helper
          const overlay = window.roomDesignerOverlay;
          let items = [];
          if (overlay && overlay._collectHandoffItems) {
            const collected = overlay._collectHandoffItems();
            items = [...collected.flooringItems, ...collected.equipmentItems, ...(collected.turfItems || []), ...(collected.platformItems || [])];
          }
          // Build per-vendor brand color summary
          const brandColorSummary = [];
          if (rd._getEquipColorNames && rd.objects) {
            const seenVendors = new Set();
            for (const obj of rd.objects) {
              if (obj.type !== 'equipment') continue;
              const info = rd._equipmentImages && rd._equipmentImages[obj.equipmentId];
              if (!info || !info.vendor || seenVendors.has(info.vendor)) continue;
              seenVendors.add(info.vendor);
              const colors = rd._getEquipColorNames(info.vendor, obj.equipmentId);
              brandColorSummary.push({
                vendor: info.vendor,
                frame: colors.frame || '-',
                pad: colors.pad || null
              });
            }
          }
          const res = await fetch('https://rfq-worker.ryan-29b.workers.dev/share/pdf', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              images,
              items,
              spaceSummary,
              totalArea,
              brandColorSummary,
              meta: { senderName: repName, receiverName: customerName, repName, repEmail, repPhone, customerName, spaceName: meta.spaceName || '', presentationUrl: presUrl },
              type: 'full',
            }),
          });
          if (!res.ok) throw new Error('PDF generation failed: ' + res.status);
          const data = await res.json();
          if (data.url) {
            window.open('https://rfq-worker.ryan-29b.workers.dev' + data.url, '_blank');
            rd.updateStatus('PDF ready!');
          }
        } catch (err) {
          console.error('View-only PDF error:', err);
          rd.updateStatus('PDF generation failed. Please try again.', { error: true });
        } finally {
          pdfBtn.disabled = false;
          pdfBtn.classList.remove('generating');
        }
      });
    }

    // Multi-view: build tab switcher, apply first view (skip tabs if only one view)
    if (meta.multiView && meta.views && meta.views.length > 1) {
      rd._viewOnlyViews = meta.views;
      this._buildMultiViewTabs(meta.views);
      this._viewOnlyInitialLoad = true;
      // Mark the last (default landing) tab as active — desktop only (mobile starts with no tab highlighted)
      if (!rd._isMobile) {
        const lastBtn = document.querySelector(`.rd-multiview-tab[data-view-id="${meta.views[meta.views.length - 1].id}"]`);
        if (lastBtn) lastBtn.classList.add('active');
      }
      this._applyViewOnlyView(meta.views[meta.views.length - 1]);
      // Hide ruler button if Floor Plan tab is included (redundant — dims always on for that tab)
      if (meta.views.some(v => v.id === 'draw')) {
        const dimBtn = document.getElementById('rdViewOnlyDimToggle');
        if (dimBtn) dimBtn.style.display = 'none';
      }
    } else if (meta.multiView && meta.views && meta.views.length === 1) {
      this._applyViewOnlyView(meta.views[0]);
    } else if (meta.currentViewOverrides) {
      // Current-view share: apply the exact visibility the rep had when sharing
      this._applyViewOnlyView({ id: meta.landingTab || 'current', label: 'Current View', overrides: meta.currentViewOverrides });
    }

    // Show space name banner at top (hide canvas room name labels)
    rd.showRoomLabels = false;
    const spaceLabelEl = document.getElementById('rdViewOnlySpaceLabel');
    if (spaceLabelEl && rd.rooms) {
      const names = rd.rooms
        .filter(r => r.closed && r.name && !/^Space\s*\d*$/i.test(r.name.trim()))
        .map(r => r.name);
      if (names.length > 0) {
        spaceLabelEl.textContent = names.join('  ·  ');
        spaceLabelEl.style.display = '';
      }
    }

    /* CUSTOM_PROJECTASSEMBLY_BY_CLAUDE (Ryan 2026-09-23) — open in the PDF's orientation. The
       packet's share carries `meta.canvasRotation` (0-3 quarter turns, already mapped from the
       page's print turns by the worker — lib/packet-share.js), applied before the fit so the plan
       lands rotated the way the printed page shows it. The R key still turns it from there. */
    if (meta.canvasRotation != null && isFinite(meta.canvasRotation)) {
      const _r = ((Math.round(meta.canvasRotation) % 4) + 4) % 4;
      rd.canvasRotation = _r;
      rd._canvasRotationRad = _r * Math.PI / 2;
    }
    // Fit to view and render
    rd.fitToView();
    rd.render();

    // Desktop: stash landing view info for delayed panel auto-open (triggered after UI entrance)
    if (!rd._isMobile && meta.multiView && meta.views && meta.views.length > 1) {
      const landingView = meta.views[meta.views.length - 1];
      const lvo = landingView.overrides || {};
      const lvid = landingView.id;
      const lvIsFloor = lvid === 'floor' || lvid === 'combined' || lvid === 'finishing';
      const lvIsEquip = lvo.equipmentMode && !lvo.showFloorOverlay;
      if (lvIsEquip || (lvIsFloor && lvid !== 'combined')) {
        this._pendingLandingPanelOpen = { lvIsEquip, lvIsFloor };
      }
    }

    // Show view-only welcome modal
    const senderName = meta.senderName || '';
    const receiverName = meta.receiverName || '';
    const message = meta.message || '';
    this._showViewOnlyWelcome(senderName, message, receiverName);
    // Fallback: if no welcome modal shown (no sender/message), trigger entrance immediately
    const woOverlay = document.getElementById('rdViewOnlyWelcomeOverlay');
    if (!woOverlay || !woOverlay.classList.contains('visible')) {
      const root2 = rd.canvas.closest('.room-designer');
      if (root2) {
        setTimeout(() => root2.classList.add('rd-vo-ui-ready'), 1000);
        setTimeout(() => root2.classList.add('rd-vo-entered'), 1500);
      }
      // Auto-open landing panel 1s after UI entrance (2s total)
      setTimeout(() => this._autoOpenLandingPanel(), 2000);
    }
  }

  _autoOpenLandingPanel() {
    const pending = this._pendingLandingPanelOpen;
    if (!pending) return;
    this._pendingLandingPanelOpen = null;
    const rd = this.rd;
    const overlay = window.roomDesignerOverlay;
    if (!overlay) return;
    if (pending.lvIsEquip && overlay._openViewOnlyColorPanel) {
      const config = overlay._getBrandColorConfig ? overlay._getBrandColorConfig() : {};
      let firstEquipId = null, fallbackEquipId = null;
      if (rd.objects) {
        for (const obj of rd.objects) {
          if (obj.type === 'equipment' && obj.equipmentId) {
            const info = rd._equipmentImages[obj.equipmentId];
            if (info && info.vendor && config[info.vendor]) {
              if (!fallbackEquipId) fallbackEquipId = obj.equipmentId;
              if (info.hasUpholstery) { firstEquipId = obj.equipmentId; break; }
            }
          }
        }
      }
      if (!firstEquipId) firstEquipId = fallbackEquipId;
      if (firstEquipId) overlay._openViewOnlyColorPanel(firstEquipId);
    } else if (pending.lvIsFloor && overlay._openViewOnlyTurfPanel) {
      const opened = !!(overlay._openViewOnlyFloorPanel && overlay._openViewOnlyFloorPanel());
      if (!opened) {
        const firstTurf = (rd.objects || []).find(obj => obj.type === 'turf' && obj.turfProductId);
        if (firstTurf) overlay._openViewOnlyTurfPanel(firstTurf);
      }
    }
  }

  _buildMultiViewTabs(views) {
    const container = document.getElementById('rdMultiViewTabs');
    if (!container) return;
    container.innerHTML = '';

    // Migrate old share link labels
    for (const v of views) {
      if (v.id === 'combined' && v.label === 'Complete Layout') v.label = 'Full Layout';
    }

    const isMobileVO = this.rd._isMobile && this.rd._viewOnly;
    if (isMobileVO) {
      this._buildFolderTabs(container, views);
    } else {
      views.forEach((view, i) => {
        const btn = document.createElement('button');
        btn.className = 'rd-multiview-tab';
        btn.textContent = view.label;
        btn.dataset.viewId = view.id;
        btn.addEventListener('click', () => {
          if (navigator.vibrate) navigator.vibrate(50);
          container.querySelectorAll('.rd-multiview-tab').forEach(b => b.classList.remove('active'));
          btn.classList.add('active');
          this._applyViewOnlyView(view);
        });
        container.appendChild(btn);
      });
    }
    container.style.display = '';

    // Overflow hint deferred until welcome modal is dismissed (see _showViewOnlyWelcome)
  }

  _buildFolderTabs(container, views) {
    container.classList.add('folder-mode');

    const SHORT_LABELS = {
      'Floor Plan': 'Plan',
      'Flooring Layout': 'Floor',
      'Equipment Layout': 'Equip',
      'Full Layout': 'All'
    };

    const tabRow = document.createElement('div');
    tabRow.className = 'rd-folder-tab-row';

    const buttons = [];

    views.forEach((view, i) => {
      const btn = document.createElement('button');
      btn.className = 'rd-multiview-tab';
      btn.dataset.viewId = view.id;

      const fullSpan = document.createElement('span');
      fullSpan.className = 'tab-label-full';
      fullSpan.textContent = view.label;

      const shortSpan = document.createElement('span');
      shortSpan.className = 'tab-label-short';
      shortSpan.textContent = SHORT_LABELS[view.label] || view.label;

      btn.appendChild(fullSpan);
      btn.appendChild(shortSpan);

      btn.addEventListener('click', () => {
        if (navigator.vibrate) navigator.vibrate(50);
        buttons.forEach(b => b.classList.remove('active'));
        btn.classList.add('active');
        this._applyViewOnlyView(view);
      });

      tabRow.appendChild(btn);
      buttons.push(btn);
    });

    const shelf = document.createElement('div');
    shelf.className = 'rd-tab-shelf';

    container.appendChild(tabRow);
    container.appendChild(shelf);
  }

  _initMultiViewOverflowHint() {
    const tabs = document.getElementById('rdMultiViewTabs');
    if (!tabs) return;
    const check = () => {
      if (tabs.scrollWidth > tabs.clientWidth + 4) {
        tabs.classList.add('has-overflow');
        // Check if already scrolled to end
        if (tabs.scrollLeft + tabs.clientWidth >= tabs.scrollWidth - 4) {
          tabs.classList.add('scrolled-end');
        } else {
          tabs.classList.remove('scrolled-end');
        }
      } else {
        tabs.classList.remove('has-overflow', 'scrolled-end');
      }
    };
    check();
    tabs.addEventListener('scroll', check, { passive: true });
  }

  _applyViewOnlyView(viewConfig) {
    const rd = this.rd;
    const overlay = window.roomDesignerOverlay;
    const o = viewConfig.overrides || {};

    // Dismiss hover callout from previous view
    if (rd._hoverCalloutEl) rd._dismissHoverCallout();

    // Floor overlay visibility (always force on for floor-type views in view-only)
    const vid = viewConfig.id;
    const isFloorView = vid === 'floor' || vid === 'combined' || vid === 'finishing';
    const showFloor = (rd._viewOnly && isFloorView) ? true : !!o.showFloorOverlay;
    if (overlay) {
      overlay.showOverlay = showFloor;
      overlay.currentStep = showFloor ? 'floor' : (o.equipmentMode ? 'equipment' : 'draw');
    }
    // Seam lines + material overlay (always hide overage in view-only — customers don't need it)
    rd.showSeamLines = (rd._viewOnly && isFloorView) ? true : (rd._viewOnly ? false : !!o.showSeamLines);
    rd.showMaterialOverlay = (rd._viewOnly && isFloorView) ? true : !!o.showMaterialOverlay;
    if (rd._viewOnly) rd.hideOverage = true;

    // View-only rendering flags
    rd._viewOnlySeamFaint = (rd._viewOnly && isFloorView) ? true : !!o.seamFaint;
    rd._viewOnlyHideZoneDividers = !!o.hideZoneDividers;
    rd._viewOnlyHideEquipment = !o.equipmentMode;
    rd._viewOnlyShowFloor = showFloor;
    rd._equipmentMode = !!o.equipmentMode;

    // Measurements and room/zone labels (only override if explicitly set)
    // In view-only Draw Space, always force measurements on so wall dimensions are visible
    const isDrawView = !showFloor && !o.equipmentMode;
    if (rd._viewOnly && isDrawView) {
      rd.showMeasurements = true;
    } else if (o.showMeasurements !== undefined) {
      rd.showMeasurements = o.showMeasurements;
    }
    const dimToggle = document.getElementById('rdViewOnlyDimToggle');
    if (dimToggle) dimToggle.classList.toggle('active', rd.showMeasurements);
    if (rd._viewOnly) rd.showRoomLabels = false;
    else if (o.showRoomLabels !== undefined) rd.showRoomLabels = o.showRoomLabels;

    // Invalidate floor layout caches so overlay re-renders with correct flags
    for (const room of rd.rooms) {
      room._flooringLayout = null;
      room._zoneLayouts = null;
    }

    rd.render();

    // Hover hint pulse on equipment-only view (not combined Full Layout)
    if (o.equipmentMode && !o.showFloorOverlay && rd._startEquipPulse) {
      rd._startEquipPulse();
    } else if (rd._stopEquipPulse) {
      rd._stopEquipPulse();
    }

    // Auto-open panels for dedicated tabs (not Full Layout / combined, not initial load)
    if (!this._viewOnlyInitialLoad && vid !== 'combined') {
      // Equipment tab: toggle color panel if any vendor equipment exists
      if (o.equipmentMode && !o.showFloorOverlay && overlay && overlay._openViewOnlyColorPanel) {
        if (overlay._viewOnlyPanelOpen && overlay._viewOnlyPanelVendor) {
          overlay._closeViewOnlyPanel();
        } else {
          const config = overlay._getBrandColorConfig ? overlay._getBrandColorConfig() : {};
          let firstEquipId = null;
          let fallbackEquipId = null;
          if (rd.objects) {
            for (const obj of rd.objects) {
              if (obj.type === 'equipment' && obj.equipmentId) {
                const info = rd._equipmentImages[obj.equipmentId];
                if (info && info.vendor && config[info.vendor]) {
                  if (!fallbackEquipId) fallbackEquipId = obj.equipmentId;
                  // Prefer equipment with upholstery (frame + pad color options)
                  if (info.hasUpholstery) { firstEquipId = obj.equipmentId; break; }
                }
              }
            }
          }
          if (!firstEquipId) firstEquipId = fallbackEquipId;
          if (firstEquipId) {
            overlay._openViewOnlyColorPanel(firstEquipId);
            // Pan to the selected equipment piece
            const panObj = rd.objects.find(o2 => o2.type === 'equipment' && o2.equipmentId === firstEquipId);
            if (panObj) setTimeout(() => this._smoothPanToObject(panObj), 100);
          }
        }
      // Flooring tab (Ryan 2026-09-24): slide out the flooring-colour panel — every floored space
      // and zone with its ECORE colour swatches, turf too — and zoom out to centre the plan like
      // the Floor Plan tab does. A second click on the tab closes the panel and fits again.
      } else if (isFloorView && rd._viewOnly && overlay) {
        if (overlay._viewOnlyPanelOpen && (overlay._viewOnlyPanelFloor || overlay._viewOnlyPanelTurfObj)) {
          overlay._closeViewOnlyPanel(true);
          setTimeout(() => this._smoothFitToView(), 100);
        } else {
          const opened = !!(overlay._openViewOnlyFloorPanel && overlay._openViewOnlyFloorPanel());
          if (!opened && (overlay._viewOnlyPanelOpen || overlay._viewOnlyPanelVendor)) overlay._closeViewOnlyPanel(true);
          setTimeout(() => this._smoothFitToView(), opened ? 380 : 100);
        }
      // Floor Plan tab: close any lingering panel and fit to view
      } else if (vid === 'draw' && rd._viewOnly && overlay && (overlay._viewOnlyPanelOpen || overlay._viewOnlyPanelVendor || overlay._viewOnlyPanelTurfObj || overlay._viewOnlyPanelFloor)) {
        overlay._closeViewOnlyPanel(true);
        setTimeout(() => this._smoothFitToView(), 100);
      }
    } else if (overlay && (overlay._viewOnlyPanelOpen || overlay._viewOnlyPanelVendor || overlay._viewOnlyPanelTurfObj || overlay._viewOnlyPanelFloor)) {
      // Full Layout: close any open panel and smoothly fit entire space into view
      overlay._closeViewOnlyPanel(true);
      if (rd._viewOnly) setTimeout(() => this._smoothFitToView(), 100);
    }
    this._viewOnlyInitialLoad = false;
  }

  // Smoothly pan/zoom so a target object is centered in the visible area above the bottom panel
  _smoothPanToObject(obj) {
    const rd = this.rd;
    if (!obj || !rd._viewOnly) return;

    // Object bounding box in world coords
    const hw = (obj.width || 0) / 2, hd = (obj.depth || 0) / 2;
    const rot = obj.rotation || 0;
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    const corners = [[-hw, -hd], [hw, -hd], [hw, hd], [-hw, hd]];
    for (const [lx, ly] of corners) {
      const p = rd._rotatePoint(obj.x + lx, obj.y + ly, obj.x, obj.y, rot);
      minX = Math.min(minX, p.x); minY = Math.min(minY, p.y);
      maxX = Math.max(maxX, p.x); maxY = Math.max(maxY, p.y);
    }
    const objW = maxX - minX, objH = maxY - minY;
    const objCX = (minX + maxX) / 2, objCY = (minY + maxY) / 2;

    // Available viewport — account for side panel on desktop, bottom panel on mobile
    const panel = document.querySelector('.rd-side-panel');
    let availW = rd._cssWidth, availH = rd._cssHeight, viewOffsetX = 0;
    if (!rd._isMobile) {
      // Desktop: side panel slides in from the left
      if (panel && panel.classList.contains('open')) {
        const pw = panel.offsetWidth;
        viewOffsetX = pw;
        availW = rd._cssWidth - pw;
      }
    } else {
      // Mobile: bottom sheet panel + tab bar
      const panelH = panel ? Math.min(panel.offsetHeight, rd._cssHeight * 0.35) : rd._cssHeight * 0.3;
      const tabsH = 62;
      availH = rd._cssHeight - panelH - tabsH;
    }
    const viewCX = viewOffsetX + availW / 2;
    const viewCY = availH / 2;

    // Compute zoom to fit object with padding in the available area
    // Swap width/height when canvas is rotated 90° or 270°
    const rotStep = rd.canvasRotation || 0;
    const swapped = (rotStep === 1 || rotStep === 3);
    const fitW = swapped ? objH : objW, fitH = swapped ? objW : objH;
    const pad = 3; // ft padding
    const scaleX = availW / ((fitW + pad * 2) * rd.basePixelsPerFoot);
    const scaleY = availH / ((fitH + pad * 2) * rd.basePixelsPerFoot);
    const targetZoom = Math.max(rd.minZoom, Math.min(rd.maxZoom, Math.min(scaleX, scaleY)));

    // Target pan to center the object — use worldToScreen with rotation
    const scale = rd.basePixelsPerFoot * targetZoom;
    const a = rd._canvasRotationRad || 0;
    const cos = Math.cos(a), sin = Math.sin(a);
    const targetPanX = viewCX - (objCX * scale * cos - objCY * scale * sin);
    const targetPanY = viewCY - (objCX * scale * sin + objCY * scale * cos);

    // Animate from current to target
    const startPanX = rd.panX, startPanY = rd.panY, startZoom = rd.zoomLevel;
    const dur = 500;
    const startTime = performance.now();
    const ease = (t) => t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;
    const step = (now) => {
      const p = Math.min((now - startTime) / dur, 1);
      const e = ease(p);
      rd.panX = startPanX + (targetPanX - startPanX) * e;
      rd.panY = startPanY + (targetPanY - startPanY) * e;
      rd.zoomLevel = startZoom + (targetZoom - startZoom) * e;
      rd.render();
      if (p < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }

  // Smoothly pan/zoom to fit the entire space (all rooms + objects) in the visible area
  _smoothFitToView() {
    const rd = this.rd;
    if (!rd._viewOnly) return;

    // Collect all points — same logic as fitToView()
    const allPoints = [];
    [...rd.rooms, rd.currentRoom].forEach(room => {
      if (!room) return;
      room.walls.forEach(wall => { allPoints.push(wall.start); allPoints.push(wall.end); });
    });
    for (const obj of rd.objects) {
      if (obj.type === 'rectangle' || obj.type === 'equipment' || obj.type === 'turf') {
        const hw = obj.width / 2, hd = obj.depth / 2;
        const rot = obj.rotation || 0;
        const corners = [[-hw, -hd], [hw, -hd], [hw, hd], [-hw, hd]];
        for (const [lx, ly] of corners) {
          const p = rd._rotatePoint(obj.x + lx, obj.y + ly, obj.x, obj.y, rot);
          allPoints.push(p);
        }
      } else if (obj.type === 'circle') {
        const r = obj.diameter / 2;
        allPoints.push({ x: obj.x - r, y: obj.y - r });
        allPoints.push({ x: obj.x + r, y: obj.y + r });
      }
    }
    if (allPoints.length === 0) return;

    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    allPoints.forEach(p => { minX = Math.min(minX, p.x); minY = Math.min(minY, p.y); maxX = Math.max(maxX, p.x); maxY = Math.max(maxY, p.y); });
    const padding = 3;
    minX -= padding; minY -= padding; maxX += padding; maxY += padding;
    const contentWidth = maxX - minX, contentHeight = maxY - minY;
    if (contentWidth <= 0 || contentHeight <= 0) return;

    // Available viewport — account for side panel on desktop, bottom panel on mobile
    let visibleLeft = 0, visibleWidth = rd._cssWidth, visibleHeight = rd._cssHeight, visibleTop = 0;
    if (!rd._isMobile) {
      const panel = document.querySelector('.rd-side-panel');
      if (panel && (panel.classList.contains('open') || panel.classList.contains('minimized'))) {
        visibleLeft = panel.offsetWidth;
        visibleWidth = rd._cssWidth - visibleLeft;
      }
    } else {
      const panel = document.querySelector('.rd-side-panel');
      if (panel && panel.classList.contains('open')) {
        const panelH = Math.min(panel.offsetHeight, rd._cssHeight * 0.35);
        const tabsH = 62;
        visibleHeight = rd._cssHeight - panelH - tabsH;
      }
      // else: panel closed (Full Layout) — use full canvas height
    }

    // Swap width/height when canvas is rotated 90° or 270°
    const rotStep = rd.canvasRotation || 0;
    const swapped = (rotStep === 1 || rotStep === 3);
    const fitW = swapped ? contentHeight : contentWidth;
    const fitH = swapped ? contentWidth : contentHeight;
    const scaleX = visibleWidth / (fitW * rd.basePixelsPerFoot);
    const scaleY = visibleHeight / (fitH * rd.basePixelsPerFoot);
    let targetZoom = Math.min(scaleX, scaleY) * 0.80;
    targetZoom = Math.max(rd.minZoom, Math.min(rd.maxZoom, targetZoom));

    const centerX = (minX + maxX) / 2, centerY = (minY + maxY) / 2;
    // Use rotation-aware pan calculation (matches worldToScreen transform)
    const scale = rd.basePixelsPerFoot * targetZoom;
    const a = rd._canvasRotationRad || 0;
    const cos = Math.cos(a), sin = Math.sin(a);
    const targetPanX = visibleLeft + visibleWidth / 2 - (centerX * scale * cos - centerY * scale * sin);
    const targetPanY = visibleHeight / 2 - (centerX * scale * sin + centerY * scale * cos);

    // Animate from current to target
    const startPanX = rd.panX, startPanY = rd.panY, startZoom = rd.zoomLevel;
    const dur = 500;
    const startTime = performance.now();
    const ease = (t) => t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;
    const step = (now) => {
      const p = Math.min((now - startTime) / dur, 1);
      const e = ease(p);
      rd.panX = startPanX + (targetPanX - startPanX) * e;
      rd.panY = startPanY + (targetPanY - startPanY) * e;
      rd.zoomLevel = startZoom + (targetZoom - startZoom) * e;
      rd.render();
      if (p < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }

  _showViewOnlyWelcome(senderName, message, receiverName) {
    const overlay = document.getElementById('rdViewOnlyWelcomeOverlay');
    const title = document.getElementById('rdViewOnlyWelcomeTitle');
    const msgEl = document.getElementById('rdViewOnlyWelcomeMessage');
    const btn = document.getElementById('rdViewOnlyWelcomeBtn');
    if (!overlay || !title || !btn) return;

    // First name only (Ryan 2026-09-23): "Ryan shared a gym layout", not "Ryan Walker shared".
    const senderFirst = String(senderName || '').trim().split(' ')[0];
    let titleText;
    if (receiverName && senderFirst) {
      titleText = 'Hi ' + receiverName + ', ' + senderFirst + ' shared a layout with you';
    } else if (senderFirst) {
      titleText = senderFirst + ' shared a layout with you';
    } else {
      titleText = 'Someone shared a layout with you';
    }
    title.textContent = titleText;

    if (message && msgEl) {
      msgEl.textContent = message;
      msgEl.style.display = '';
    } else if (msgEl) {
      msgEl.style.display = 'none';
    }

    overlay.classList.add('visible');
    const dismiss = () => {
      overlay.classList.remove('visible');
      btn.removeEventListener('click', dismiss);
      // Entrance animations 1s after welcome dismiss
      const root = this.rd.canvas.closest('.room-designer');
      if (root) {
        setTimeout(() => root.classList.add('rd-vo-ui-ready'), 1000);
        // After entrance animation finishes (1s delay + 0.5s anim), remove animation lock so CSS transform works
        setTimeout(() => root.classList.add('rd-vo-entered'), 1500);
      }
      // Auto-open landing panel 1s after UI entrance (2s after dismiss)
      setTimeout(() => this._autoOpenLandingPanel(), 2000);
      // Show scroll overflow hint on desktop (folder tabs on mobile need no hint — all visible)
      if (!this.rd._isMobile) {
        this._initMultiViewOverflowHint();
      } else {
        this._playFolderTabWave();
      }
    };
    btn.addEventListener('click', dismiss);
  }

  _playFolderTabWave() {
    const tabs = document.querySelectorAll('.folder-mode .rd-multiview-tab');
    if (!tabs.length) return;
    // Stagger each tab's "pop" animation left-to-right
    tabs.forEach((tab, i) => {
      setTimeout(() => tab.classList.add('tab-wave'), 300 + i * 150);
      setTimeout(() => tab.classList.remove('tab-wave'), 300 + i * 150 + 400);
    });
  }

  // ── Rep PDF Handlers ──────────────────────

  /* @param opts.noFallback — never substitute the whole-design single-page render.
     ⚠️ This matters more than it looks. When the per-space images blow the 5MB
     ceiling, this function quietly swaps in _generatePrintCanvas() — ONE page of the
     ENTIRE design, named "Full Design". For a share link that is the right call:
     something beats nothing. For a proposal VARIANT it is a lie — it gets stored
     under o1_w0 or d1_o0_w0 and the packet later renders it believing it is that
     combo, so the rep ticks "Use designer layout" and gets a single whole-design page
     with no indication anything went wrong. Ryan hit exactly that.

     A designer page list makes it far likelier — more pages per combo, same ceiling.
     Variant generation passes noFallback and skips the combo instead, leaving the
     packet to fall back through its own chain to the plain variant: a page that is at
     least honestly what it claims to be.

     @param opts.note — an object this fills with `reason` when it returns nothing.
     ⚠️ "No images" has three completely different causes wanting three different
     fixes — the size ceiling, a design with no printable pages, and an upload the
     worker refused — and the caller can only see `null`. Reporting them as one
     string ("size ceiling or an upload error") is how a diagnosis costs a second
     round trip through Ryan. Each one names itself now. */
  async _uploadSpaceImages(rd, opts) {
    const note = (opts && opts.note) || {};
    // ONE definition of "the best quality that fits" — shared with the overlay's
    // quote handoff, which had its own drifting copy of the same rule.
    let spaceImages = await rd._renderPrintImagesWithinBudget(note);
    if (!spaceImages || spaceImages.length === 0) {
      if (opts && opts.noFallback) {
        try { console.warn('[VariantGen] combo SKIPPED rather than storing a whole-design page under a variant key — ' + note.reason); } catch (_e) {}
        return null;
      }
      const fallback = await rd._generatePrintCanvas();
      if (fallback) spaceImages = [{ spaceName: 'Full Design', dataUrl: fallback, spaceIndex: 0 }];
    }
    if (!spaceImages) return null;

    // Upload each image to worker and return R2 URLs
    const uploaded = [];
    for (const img of spaceImages) {
      const blob = await (await fetch(img.dataUrl)).blob();
      const form = new FormData();
      form.append('file', blob, `space-${img.spaceIndex}.jpg`);
      const res = await fetch('https://rfq-worker.ryan-29b.workers.dev/upload', { method: 'POST', body: form });
      // The status and body are the whole diagnosis here — a 413 is the image being
      // too big, a 403 is CORS/auth, a 500 is R2. "Image upload failed" is none of them.
      if (!res.ok) {
        let body = '';
        try { body = (await res.text()).slice(0, 120); } catch (_eB) {}
        throw new Error('image upload failed (HTTP ' + res.status + ') ' + body);
      }
      const data = await res.json();
      // rotateTurns: the quarter turns the page printed with (see _renderPrintImagesWithinBudget).
      uploaded.push(Object.assign({ url: data.url, spaceName: img.spaceName, spaceIndex: img.spaceIndex },
        (img.rotateTurns != null ? { rotateTurns: img.rotateTurns } : {})));
    }
    return uploaded;
  }

  // CUSTOM_PROJECTASSEMBLY_BY_CLAUDE — Path B: post-save variant generation.
  // Renders the floor plan in 4 toggle combos (overage on/off × walls on/off) and
  // PATCHes spaceImagesVariants onto the design record. Fire-and-forget — never
  // awaited by the save path. Tolerates partial failure: any combo that uploads
  // gets stored; missed combos retry on the next save. The Project Assembly
  // worker's /generate-packet then picks the matching variant when the rep
  // ticks the project's overage / wall-dim toggles.
  async _kickoffVariantGeneration(rd, designId) {
    if (!designId) return;
    if (this._variantGenInFlight) return; // de-dup back-to-back saves
    this._variantGenInFlight = true;

    // Snapshot toggle state so we can restore even if rendering throws
    const orig = {
      showMeasurements: rd.showMeasurements,
      hideOverage: rd.hideOverage,
    };

    const variants = {};
    /* CUSTOM_ROOMDESIGNER_BY_CLAUDE — why it failed, carried to the banner.
       "Preview generation incomplete — resave the plan to retry" said nothing about
       the cause, so diagnosing it meant asking the rep to open a console. Twice.
       The reason is right here at the moment it happens; put it where they can see
       it. Kept short in the banner, full text in the title attribute. */
    const failures = [];
    let patchDetail = '';
    let comboCount = 0;
    const t0 = Date.now();
    try { console.log('[VariantGen] start designId=' + designId); } catch (_e) {}

    // Show top banner + arm beforeunload guard so the rep can't accidentally close
    // the tab mid-generation (which would leave partial spaceImagesVariants on the
    // design record and silently fall back to the legacy render at packet time).
    const banner = this._showVariantGenBanner();
    const beforeUnloadHandler = function (e) {
      e.preventDefault();
      e.returnValue = 'Proposal previews are still generating. Leave anyway?';
      return e.returnValue;
    };
    try { window.addEventListener('beforeunload', beforeUnloadHandler); } catch (_eBU) {}

    try {
      // Loop o0_w0, o0_w1, o1_w0, o1_w1
      /* CUSTOM_ROOMDESIGNER_BY_CLAUDE — "Use designer layout".
         The rep's page list (zones, close-ups, order, per-page rotation) is
         orthogonal to the overage and wall-dimension toggles, so it doubles the
         grid rather than extending it: the same four combos rendered a second time
         with the page plan applied, keyed d1_o*_w*. That is what lets the packet
         checkbox compose with the other two instead of overriding them.

         ⚠️ The second pass only runs when the design HAS a page list that differs
         from the default (rd._hasDesignerLayout). A design without one would render
         four byte-identical images and double every rep's save for nothing — and it
         is the same condition the portal greys the checkbox on, deliberately, so the
         two can never disagree about when the feature exists. */
      const hasDesigner = rd._hasDesignerLayout && rd._hasDesignerLayout();
      const passes = hasDesigner ? [null, rd._pagePlanFromSelection(rd._pdfPageSelection)] : [null];
      const totalCombos = passes.length * 4;
      for (const plan of passes) {
        const dPrefix = plan ? 'd1_' : '';
        for (const oFlag of [0, 1]) {
          for (const wFlag of [0, 1]) {
            const variantKey = dPrefix + 'o' + oFlag + '_w' + wFlag;
            // hideOverage=true means overage is HIDDEN; flipped semantics
            rd.hideOverage = (oFlag === 0);
            // showMeasurements=true means walls ARE shown
            rd.showMeasurements = (wFlag === 1);
            try { rd.updateStatus('Generating proposal previews ' + (comboCount + 1) + '/' + totalCombos + '...'); } catch (_eS) {}
            this._updateVariantGenBanner(banner, comboCount + 1, totalCombos);
            rd._printPagePlan = plan;
            try {
              const tCombo = Date.now();
              const note = {};
              const images = await this._uploadSpaceImages(rd, { noFallback: true, note: note });
              if (images && images.length > 0) {
                variants[variantKey] = images;
                try { console.log('[VariantGen] ' + variantKey + ' uploaded ' + images.length + ' images in ' + (Date.now() - tCombo) + 'ms'); } catch (_e) {}
              } else {
                failures.push(variantKey + ': ' + (note.reason || 'no images, no reason captured'));
                try { console.warn('[VariantGen] ' + variantKey + ' produced no images — ' + (note.reason || '?')); } catch (_e) {}
              }
            } catch (eCombo) {
              // The message alone rarely identifies WHERE — carry the first stack frame.
              const _stk = (eCombo && eCombo.stack) ? String(eCombo.stack).split(/\r?\n/)[1] : '';
              failures.push(variantKey + ': ' + ((eCombo && eCombo.message) || eCombo) +
                (_stk ? ' @ ' + _stk.trim().slice(0, 90) : ''));
              try { console.warn('[VariantGen] ' + variantKey + ' failed: ' + (eCombo && eCombo.message || eCombo)); } catch (_e) {}
            } finally {
              // Never leave a plan on the instance — the same image builder feeds
              // share links and the quote handoff.
              rd._printPagePlan = null;
            }
            comboCount++;
          }
        }
      }

      let patchOk = false;
      if (Object.keys(variants).length === 0) {
        patchDetail = 'no previews rendered — nothing to save';
      }
      if (Object.keys(variants).length > 0) {
        try {
          const res = await this._authedFetch('https://rfq-worker.ryan-29b.workers.dev/design-update-variants', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            // hasDesignerLayout rides along so the portal can grey the packet
            // checkbox without having to infer it from the variant key names.
            body: JSON.stringify({ designId: designId, spaceImagesVariants: variants, hasDesignerLayout: !!hasDesigner }),
          });
          const txt = await res.text();
          try { console.log('[VariantGen] PATCH status=' + res.status + ' body=' + txt); } catch (_e) {}
          if (res.ok) {
            patchOk = true;
            try { rd.updateStatus('Proposal previews ready (' + Object.keys(variants).length + '/4) — ' + ((Date.now() - t0) / 1000).toFixed(1) + 's'); } catch (_eS) {}
            // Clear the status bar text after 5s so the success message doesn't linger.
            // The top banner already auto-fades on its own after ~2.5s.
            try { setTimeout(function () { try { rd.updateStatus(''); } catch (_eC2) {} }, 5000); } catch (_eC) {}
          } else {
            patchDetail = 'save rejected (HTTP ' + res.status + '): ' + txt.slice(0, 120);
            try { rd.updateStatus('Preview save failed: ' + txt.slice(0, 80), { error: true }); } catch (_eS) {}
          }
        } catch (ePatch) {
          try { console.warn('[VariantGen] PATCH error: ' + (ePatch && ePatch.message || ePatch)); } catch (_e) {}
          try { rd.updateStatus('Preview upload failed', { error: true }); } catch (_eS) {}
        }
      } else {
        try { rd.updateStatus('Preview generation produced no images', { error: true }); } catch (_eS) {}
      }
      /* ⚠️ Per-combo reasons FIRST. patchDetail's "nothing to save" is a consequence,
         not a cause — when every combo fails, the specific error from each is the only
         thing worth reading, and ordering this the other way round masked exactly that
         on the first real failure it saw. */
      /* Eight combos failing for ONE reason produce eight identical strings, which
         buries the reason in repetition. Collapse by reason, keep the keys. */
      const byReason = new Map();
      for (const f of failures) {
        const i = f.indexOf(': ');
        const key = i > 0 ? f.slice(0, i) : '?';
        const reason = i > 0 ? f.slice(i + 2) : f;
        if (!byReason.has(reason)) byReason.set(reason, []);
        byReason.get(reason).push(key);
      }
      const grouped = [];
      byReason.forEach((keys, reason) => {
        grouped.push((keys.length === comboCount ? 'all ' + keys.length + ' combos' : keys.join(', ')) + ': ' + reason);
      });
      const failDetail = (grouped.length ? grouped.join(' | ') : '') || patchDetail;
      try { if (!patchOk) console.warn('[VariantGen] INCOMPLETE — ' + (failDetail || 'no reason captured')); } catch (_e) {}
      this._finishVariantGenBanner(banner, patchOk, Object.keys(variants).length, comboCount, failDetail);
      this._embedReport({ ok: !!patchOk, reason: patchOk ? '' : (failDetail || 'previews-incomplete') });   // CUSTOM_QUOTELAYOUTSWAP_BY_CLAUDE
    } finally {
      this._embedReport({ ok: false, reason: 'previews-error' });   // only if nothing reported above
      // Always restore toggles + repaint so the user's view stays consistent
      rd.showMeasurements = orig.showMeasurements;
      rd.hideOverage = orig.hideOverage;
      this._variantGenInFlight = false;
      try { window.removeEventListener('beforeunload', beforeUnloadHandler); } catch (_eBU2) {}
      try { if (rd.render) rd.render(); } catch (_eR) {}
      try { console.log('[VariantGen] done in ' + ((Date.now() - t0) / 1000).toFixed(1) + 's, variants=' + Object.keys(variants).join(',')); } catch (_e) {}
    }
  }

  // CUSTOM_PROJECTASSEMBLY_BY_CLAUDE — fixed top banner so the rep clearly sees variant
  // gen is running and knows to keep the tab open. Paired with a beforeunload guard
  // armed in _kickoffVariantGeneration.
  _showVariantGenBanner() {
    try {
      let el = document.getElementById('rd-variant-gen-banner');
      if (el) el.remove();
      el = document.createElement('div');
      el.id = 'rd-variant-gen-banner';
      el.style.cssText = [
        'position:fixed', 'top:0', 'left:0', 'right:0',
        'background:#006bcc', 'color:#fff',
        'padding:10px 16px',
        'font:600 14px Arial,sans-serif',
        'text-align:center',
        'z-index:99999',
        'box-shadow:0 2px 8px rgba(0,0,0,0.25)',
        'transition:background 0.2s, opacity 0.4s',
      ].join(';');
      el.innerHTML =
        '<span style="display:inline-block;width:14px;height:14px;border:2px solid rgba(255,255,255,0.4);border-top-color:#fff;border-radius:50%;animation:rd-vg-spin 0.8s linear infinite;vertical-align:-3px;margin-right:8px;"></span>' +
        '<span id="rd-variant-gen-text">Generating proposal previews — keep this tab open</span>';
      // Spinner keyframes (idempotent)
      if (!document.getElementById('rd-variant-gen-style')) {
        const s = document.createElement('style');
        s.id = 'rd-variant-gen-style';
        s.textContent = '@keyframes rd-vg-spin { to { transform: rotate(360deg); } }';
        document.head.appendChild(s);
      }
      document.body.appendChild(el);
      return el;
    } catch (_e) { return null; }
  }
  _updateVariantGenBanner(el, n, total) {
    if (!el) return;
    try {
      const t = el.querySelector('#rd-variant-gen-text');
      if (t) t.textContent = 'Generating proposal previews — keep this tab open (' + n + '/' + total + ')';
    } catch (_e) {}
  }
  _finishVariantGenBanner(el, ok, count, total, detail) {
    if (!el) return;
    try {
      // Replace spinner with check / x and switch color
      el.style.background = ok ? '#2a7a2a' : '#b03a3a';
      el.innerHTML = ok
        ? '<span style="margin-right:6px;">✓</span>Proposal previews ready (' + count + '/' + (total || count) + ') — safe to navigate'
        : '<span style="margin-right:6px;">!</span>Previews incomplete (' + count + '/' + (total || '?') + ')' +
          (detail ? ' — ' + String(detail).slice(0, 110) : '') + ' — resave to retry';
      // Full text on hover: the banner has room for one reason, a run can have eight.
      if (!ok && detail) { try { el.title = String(detail); } catch (_eT) {} }
      // Auto-dismiss success after a short visible window; keep failure until manual close
      if (ok) {
        setTimeout(function () {
          try { el.style.opacity = '0'; } catch (_eF) {}
          setTimeout(function () { try { el.remove(); } catch (_eR) {} }, 450);
        }, 2500);
      } else {
        // Add a small dismiss × so failures don't stick forever
        const x = document.createElement('span');
        x.textContent = '×';
        x.style.cssText = 'position:absolute;right:14px;top:50%;transform:translateY(-50%);font-size:20px;cursor:pointer;line-height:1;';
        x.onclick = function () { try { el.remove(); } catch (_e) {} };
        el.style.position = 'fixed';
        el.appendChild(x);
      }
    } catch (_e) {
      try { el.remove(); } catch (_eR2) {}
    }
  }
  // END CUSTOM_PROJECTASSEMBLY_BY_CLAUDE

  /* CUSTOM_PROJECTASSEMBLY_BY_CLAUDE (Ryan 2026-09-23) — THE PLAN'S ITEMS, saved with the design.
     The Projects hub can add a page under a floor plan listing everything on it (equipment,
     flooring, turf, platforms) with quantities, grouped by brand or by category. Only the
     designer can count those — a placed object is `{type:'equipment', equipmentId}` and its
     name, brand and product page come from the equipment catalogue loaded here; flooring square
     footage is computed from the room geometry — so the count is taken at save time from the
     same aggregator the quote handoff uses (_collectHandoffItems) and stored on the design
     record as `planItems`. The worker never re-derives it; a plan saved before this exists has
     none, and the hub says so rather than printing a partial list.
       Merged ACROSS spaces (the handoff keeps equipment per room for the quote; the list is one
     plan-wide count), keyed so a rep's custom wording can be stored against a line. */
  _buildPlanItems(rd) {
    const overlay = window.roomDesignerOverlay;
    if (!overlay || typeof overlay._collectHandoffItems !== 'function') return null;
    let h;
    try { h = overlay._collectHandoffItems(); } catch (_e) { return null; }
    if (!h) return null;
    const clean = (s) => String(s || '').replace(/\s+/g, ' ').trim();
    const equipment = [];
    const eqBy = new Map();
    for (const it of (h.equipmentItems || [])) {
      const key = 'eq:' + (it.eqId || it.sku || it.title);
      if (!eqBy.has(key)) {
        const info = (it.eqId && rd._equipmentImages) ? rd._equipmentImages[it.eqId] : null;
        eqBy.set(key, { key, title: clean(it.title), vendor: clean(it.vendor), sku: clean(it.sku),
          qty: 0, url: it.url || '', type: clean(it.productType || (info && info.productType) || '') });
        equipment.push(eqBy.get(key));
      }
      eqBy.get(key).qty += Number(it.quantity) || 0;
    }
    const flooring = [];
    const flBy = new Map();
    for (const it of (h.flooringItems || [])) {
      const key = 'fl:' + clean(it.title) + '|' + clean(it.variantTitle);
      if (!flBy.has(key)) {
        flBy.set(key, { key, title: clean(it.title), color: clean(it.variantTitle), vendor: clean(it._vendor),
          sku: clean(it._sku), qty: 0, unit: it.qtyUnit || 'sq ft', url: it.url || '', category: clean(it._category) });
        flooring.push(flBy.get(key));
      }
      flBy.get(key).qty += Number(it.quantity) || 0;
    }
    const turf = [];
    const tfBy = new Map();
    for (const it of (h.turfItems || [])) {
      const key = 'tf:' + clean(it.title) + '|' + clean(it.variantTitle);
      if (!tfBy.has(key)) {
        tfBy.set(key, { key, title: clean(it.title), color: clean(it.variantTitle), vendor: clean(it.vendor),
          qty: 0, sqft: 0, url: it.url || '' });
        turf.push(tfBy.get(key));
      }
      tfBy.get(key).qty += Number(it.quantity) || 0;
      tfBy.get(key).sqft += Number(it._totalAreaSqFt) || 0;
    }
    const platforms = (h.platformItems || []).map((it) => ({
      key: 'pl:' + clean(it.variantTitle || it.platformDescription),
      title: clean(it.title), variant: clean(it.variantTitle), qty: Number(it.quantity) || 0,
      description: clean(it.platformDescription), vendor: clean(it.vendor),
    }));
    return { v: 1, at: new Date().toISOString(), equipment, flooring, turf, platforms };
  }

  _buildSpaceSummary(rd) {
    const spaceSummary = [];
    let totalArea = 0;
    let totalMaterialArea = 0;
    let idx = 0;
    const overlay = window.roomDesignerOverlay;
    for (const room of rd.rooms) {
      if (!room.closed || room.type !== 'add') continue;
      idx++;
      const stats = rd._computeSpaceStats(room);
      totalArea += stats.area;
      // Compute material area + seam LF from flooring layout if available
      let materialArea = 0;
      let seamLF = 0;
      if (overlay && room.flooring) {
        const entries = overlay._getFlooringEntriesForRoom ? overlay._getFlooringEntriesForRoom(room) : null;
        if (entries) {
          for (const e of entries) materialArea += e.materialArea || 0;
        } else if (room._flooringLayout && room._flooringLayout.strips) {
          for (const strip of room._flooringLayout.strips) {
            for (const roll of strip.rolls) materialArea += strip.width * (roll.naturalLength || roll.length);
          }
        }
        // Seam LF: side seams (between adjacent strips) + head seams (between pieces in same strip)
        // Excludes wall boundary seams — only roll-to-roll joints
        const layout = room._flooringLayout;
        if (layout && layout.strips) {
          for (let si = 0; si < layout.strips.length; si++) {
            const strip = layout.strips[si];
            // Side seam: seam between this strip and the next (along the strip length)
            if (si < layout.strips.length - 1) {
              // Side seam length = the overlapping Y range of both strips within the room
              for (const seg of strip.segments) {
                seamLF += seg.segMaxY - seg.segMinY;
              }
            }
            // Head seams: between pieces within each segment
            for (const seg of strip.segments) {
              if (seg.rolls && seg.rolls.length > 1) {
                // Each boundary between consecutive rolls = one head seam across the strip width
                seamLF += (seg.rolls.length - 1) * strip.width;
              }
            }
          }
        }
      }
      totalMaterialArea += materialArea;
      // CUSTOM_ROOMDESIGNER_BY_CLAUDE — surface door counts so the Full Packet PDF can populate its DOORS column
      const doors = room.doors || [];
      const doorBreakdown = { single: 0, double: 0, roller: 0 };
      for (const d of doors) {
        const t = d.type || 'single';
        if (doorBreakdown[t] != null) doorBreakdown[t]++;
      }
      // END CUSTOM_ROOMDESIGNER_BY_CLAUDE
      spaceSummary.push({
        name: room.name || `Space ${idx}`,
        area: Math.round(stats.area),
        materialArea: Math.round(materialArea),
        seamLF: Math.round(seamLF),
        perimeterLF: Math.round(stats.perimeterLF),
        doorCount: doors.length,
        doorBreakdown,
      });
    }
    return { spaceSummary, totalArea: Math.round(totalArea), totalMaterialArea: Math.round(totalMaterialArea) };
  }

  async _buildPresentationUrl(designData, meta) {
    const rd = this.rd;
    const MULTI_VIEW_DEFS = {
      draw:      { id: 'draw',      label: 'Floor Plan',        overrides: { showMeasurements: true, showFloorOverlay: false, showSeamLines: false, showMaterialOverlay: false, seamFaint: false, hideZoneDividers: false, equipmentMode: false, showRoomLabels: true } },
      floor:     { id: 'floor',     label: 'Flooring Layout',   overrides: { showMeasurements: false, showFloorOverlay: true, showSeamLines: true, showMaterialOverlay: true, seamFaint: true, hideZoneDividers: true, equipmentMode: false, showRoomLabels: true } },
      equipment: { id: 'equipment', label: 'Equipment Layout',  overrides: { showMeasurements: false, showFloorOverlay: false, showSeamLines: false, showMaterialOverlay: false, seamFaint: false, hideZoneDividers: false, equipmentMode: true, showRoomLabels: true } }
    };
    const hasFlooring = rd.rooms.some(r => r.closed && r.type === 'add' && r.flooring)
        || rd.objects.some(o => o.type === 'turf');
    const hasEquipment = rd.objects.some(o => o.type === 'equipment');
    const views = [MULTI_VIEW_DEFS.draw];
    if (hasFlooring) views.push(MULTI_VIEW_DEFS.floor);
    if (hasEquipment) views.push(MULTI_VIEW_DEFS.equipment);
    if (hasFlooring && hasEquipment) {
      views.push({ id: 'combined', label: 'Full Layout', overrides: {
        showMeasurements: false, showFloorOverlay: true, showSeamLines: true, showMaterialOverlay: true,
        seamFaint: true, hideZoneDividers: true, equipmentMode: true, showRoomLabels: true
      }});
    }
    const voMeta = {
      senderName: meta.senderName || '',
      receiverName: meta.receiverName || '',
      viewOnly: true,
      sharedAt: new Date().toISOString(),
      multiView: views.length > 1,
      views: views.length > 1 ? views : undefined,
      repId: meta.repId || undefined,
      repEmail: meta.repEmail || undefined,
      repPhone: meta.repPhone || undefined
    };
    if (!voMeta.multiView) voMeta.landingTab = 'draw';
    try {
      return await this._buildShareUrl(designData, voMeta);
    } catch (_e) { return null; }
  }

  async _handleSharePlanPdf(designData, meta) {
    const rd = this.rd;
    const images = await this._uploadSpaceImages(rd);
    if (!images) throw new Error('No images generated');
    const { spaceSummary, totalArea } = this._buildSpaceSummary(rd);

    // Build presentation link for embedding in PDF (non-blocking — skip if design too large for KV)
    let presentationUrl = null;
    try {
      presentationUrl = await this._buildPresentationUrl(designData, meta);
    } catch (_e) { /* design too large for KV — skip presentation link in PDF */ }

    const res = await fetch('https://rfq-worker.ryan-29b.workers.dev/share/pdf', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        images,
        items: [],
        spaceSummary,
        totalArea,
        meta: { senderName: meta.senderName || 'Rep', receiverName: meta.receiverName || '', repName: meta.senderName || '', repEmail: meta.repEmail || '', repPhone: meta.repPhone || '', customerName: meta.receiverName || '', spaceName: meta.spaceName || '', presentationUrl: presentationUrl || undefined },
        type: 'plan',
      }),
    });
    if (!res.ok) throw new Error('PDF generation failed: ' + res.status);
    const data = await res.json();
    return data.url ? ('https://rfq-worker.ryan-29b.workers.dev' + data.url) : null;
  }

  async _handleShareFullPacket(designData, meta) {
    const rd = this.rd;
    const images = await this._uploadSpaceImages(rd);
    if (!images) throw new Error('No images generated');
    const { spaceSummary, totalArea } = this._buildSpaceSummary(rd);

    // Collect flooring + equipment items via overlay helper
    const overlay = window.roomDesignerOverlay;
    let items = [];
    if (overlay && overlay._collectHandoffItems) {
      const collected = overlay._collectHandoffItems();
      items = [...collected.flooringItems, ...collected.equipmentItems, ...(collected.turfItems || []), ...(collected.platformItems || [])];
    }

    // Build per-vendor brand color summary for PDF
    const brandColorSummary = [];
    if (rd._getEquipColorNames && rd.objects) {
      const seenVendors = new Set();
      for (const obj of rd.objects) {
        if (obj.type !== 'equipment') continue;
        const info = rd._equipmentImages && rd._equipmentImages[obj.equipmentId];
        if (!info || !info.vendor || seenVendors.has(info.vendor)) continue;
        seenVendors.add(info.vendor);
        const colors = rd._getEquipColorNames(info.vendor, obj.equipmentId);
        brandColorSummary.push({
          vendor: info.vendor,
          frame: colors.frame || '-',
          pad: colors.pad || null
        });
      }
    }

    // Build presentation link for embedding in PDF (non-blocking — skip if design too large for KV)
    let presentationUrl = null;
    try {
      presentationUrl = await this._buildPresentationUrl(designData, meta);
    } catch (_e) { /* design too large for KV — skip presentation link in PDF */ }

    const res = await fetch('https://rfq-worker.ryan-29b.workers.dev/share/pdf', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        images,
        items,
        spaceSummary,
        totalArea,
        brandColorSummary,
        meta: { senderName: meta.senderName || 'Rep', receiverName: meta.receiverName || '', repName: meta.senderName || '', repEmail: meta.repEmail || '', repPhone: meta.repPhone || '', spaceName: meta.spaceName || '', presentationUrl: presentationUrl || undefined },
        type: 'full',
      }),
    });
    if (!res.ok) throw new Error('PDF generation failed: ' + res.status);
    const data = await res.json();
    return data.url ? ('https://rfq-worker.ryan-29b.workers.dev' + data.url) : null;
  }

  async _handleShareSeamPdf(designData, meta) {
    const rd = this.rd;
    const overlay = window.roomDesignerOverlay;
    if (!overlay) throw new Error('Overlay not loaded');

    // Collect seam data + generate diagram canvases for each room with roll flooring
    const seamEntries = [];
    const uploadedImages = [];

    // Every space's seam data first (roll sharing packs a material ACROSS spaces, so a
    // space's piece letters are only final once all spaces are computed), then draw.
    for (const { room, spaceIdx, seamData } of overlay._computeSeamPlanAll()) {
      const dataUrl = overlay._generateSeamPlanCanvas(room, seamData);
      if (!dataUrl) continue;

      // Upload diagram image to R2
      const blob = await (await fetch(dataUrl)).blob();
      const form = new FormData();
      form.append('file', blob, `seam-${spaceIdx}.jpg`);
      const res = await fetch('https://rfq-worker.ryan-29b.workers.dev/upload', { method: 'POST', body: form });
      if (!res.ok) throw new Error('Seam image upload failed');
      const data = await res.json();

      uploadedImages.push({ url: data.url, spaceName: room.name || `Space ${spaceIdx}`, spaceIndex: spaceIdx });
      seamEntries.push({
        spaceName: room.name || `Space ${spaceIdx}`,
        spaceIndex: spaceIdx,
        purchaseRolls: seamData.purchaseRolls,
        product: seamData.product,
        productGroups: seamData.productGroups,
      });
    }

    if (seamEntries.length === 0) throw new Error('No rooms with roll flooring found');

    // Force re-render to recover any equipment canvases that may have been
    // evicted from GPU memory by the offscreen canvas allocation
    rd.render();

    const res = await fetch('https://rfq-worker.ryan-29b.workers.dev/share/pdf', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        images: uploadedImages,
        seamData: seamEntries,
        meta: { senderName: meta.senderName || 'Rep', receiverName: meta.receiverName || '', repName: meta.senderName || '', repEmail: meta.repEmail || '', repPhone: meta.repPhone || '', spaceName: meta.spaceName || '' },
        type: 'seam',
      }),
    });
    if (!res.ok) throw new Error('Seam PDF generation failed: ' + res.status);
    const data = await res.json();
    return data.url ? ('https://rfq-worker.ryan-29b.workers.dev' + data.url) : null;
  }

  // ── Designs Panel UI ──────────────────────

  _updateDesignNameUI(name) {
    const input = document.getElementById('designNameInput');
    if (input) {
      input.value = name || '';
      // Trigger input event so save button state updates
      input.dispatchEvent(new Event('input', { bubbles: true }));
    }
    const label = document.getElementById('designsToggleLabel');
    if (label) label.textContent = name || 'Designs';
  }

  // ── Migration (one-time for existing localStorage users) ──

  _checkMigration() {
    if (localStorage.getItem('rd_migration_v2')) return;
    let index;
    try { index = JSON.parse(localStorage.getItem('rd_designs') || '[]'); } catch (e) { return; }
    if (!index || index.length === 0) return;

    const overlay = document.getElementById('rdMigrationOverlay');
    if (!overlay) {
      // No migration overlay in DOM — mark as done
      localStorage.setItem('rd_migration_v2', '1');
      return;
    }

    const list = overlay.querySelector('.rd-migration-list');
    const dlAllBtn = document.getElementById('rdMigrationDownloadAll');
    const dismissBtn = document.getElementById('rdMigrationDismiss');

    // Build list of designs with download buttons
    list.innerHTML = '';
    index.forEach(entry => {
      const row = document.createElement('div');
      row.className = 'rd-migration-item';
      const name = document.createElement('span');
      name.className = 'rd-migration-name';
      name.textContent = entry.name || 'Untitled';
      const btn = document.createElement('button');
      btn.className = 'rd-migration-dl-btn';
      btn.textContent = 'Download';
      btn.addEventListener('click', () => {
        this._downloadMigrationDesign(entry);
        btn.textContent = 'Downloaded';
        btn.disabled = true;
      });
      row.appendChild(name);
      row.appendChild(btn);
      list.appendChild(row);
    });

    overlay.classList.add('visible');

    const downloadAll = () => {
      index.forEach((entry, i) => {
        setTimeout(() => this._downloadMigrationDesign(entry), i * 300);
      });
      list.querySelectorAll('.rd-migration-dl-btn').forEach(b => { b.textContent = 'Downloaded'; b.disabled = true; });
      if (dlAllBtn) { dlAllBtn.textContent = 'All Downloaded'; dlAllBtn.disabled = true; }
    };

    const dismiss = () => {
      overlay.classList.remove('visible');
      localStorage.setItem('rd_migration_v2', '1');
    };

    if (dlAllBtn) dlAllBtn.addEventListener('click', downloadAll);
    if (dismissBtn) dismissBtn.addEventListener('click', dismiss);
  }

  _downloadMigrationDesign(entry) {
    try {
      const raw = localStorage.getItem('rd_design_' + entry.id);
      if (!raw) return;
      const data = JSON.parse(raw);
      const exportObj = { version: 1, name: entry.name || 'Untitled', exportedAt: new Date().toISOString(), data };
      const blob = new Blob([JSON.stringify(exportObj, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = (entry.name || 'Untitled').replace(/[^a-z0-9_\- ]/gi, '').replace(/\s+/g, '_') + '.json';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) { /* skip corrupt entries */ }
  }

  _showConfirm(message, okText, onOk, opts) {
    const overlay = document.getElementById('rdConfirmOverlay');
    const msgEl = document.getElementById('rdConfirmMessage');
    const okBtn = document.getElementById('rdConfirmOk');
    const cancelBtn = document.getElementById('rdConfirmCancel');
    const altBtn = document.getElementById('rdConfirmAlt');
    if (message.indexOf('<') !== -1) msgEl.innerHTML = message;
    else msgEl.textContent = message;
    okBtn.textContent = okText || 'OK';
    cancelBtn.textContent = (opts && opts.cancelText) || 'Cancel';
    // Alt button (optional third action)
    const actions = altBtn.parentElement;
    if (opts && opts.altText) {
      altBtn.textContent = opts.altText;
      altBtn.style.display = '';
      actions.classList.add('three-btn');
    } else {
      altBtn.style.display = 'none';
      actions.classList.remove('three-btn');
    }
    // Blue OK for non-destructive prompts
    if (opts && opts.blueOk) actions.classList.add('blue-ok');
    else actions.classList.remove('blue-ok');
    overlay.classList.add('visible');
    const cleanup = () => {
      overlay.classList.remove('visible');
      okBtn.removeEventListener('click', handleOk);
      cancelBtn.removeEventListener('click', handleCancel);
      altBtn.removeEventListener('click', handleAlt);
    };
    const handleOk = () => { cleanup(); if (onOk) onOk(); };
    const handleAlt = () => { cleanup(); if (opts && opts.onAlt) opts.onAlt(); };
    const handleCancel = () => { cleanup(); if (opts && opts.onCancel) opts.onCancel(); };
    okBtn.addEventListener('click', handleOk);
    cancelBtn.addEventListener('click', handleCancel);
    altBtn.addEventListener('click', handleAlt);
  }

  _setupDesignsPanel() {
    const rd = this.rd;
    const toggle = document.getElementById('designsToggle');
    const drawer = document.getElementById('designsDrawer');
    if (!toggle || !drawer) return;

    // Click handler — pin/unpin drawer on both mobile and desktop
    toggle.addEventListener('click', () => {
      if (rd._isMobile) rd._tapFlash(toggle);
      clearTimeout(rd._popoverTimers['designsDrawer']);
      if (rd._pinnedPopover === 'designs') {
        rd._pinnedPopover = null;
        drawer.classList.remove('open');
        toggle.classList.remove('open');
      } else {
        // Close other popovers and drawers
        ['zoomPopover', 'penPopover', 'objectsPopover'].forEach(id => { document.getElementById(id).classList.remove('visible'); clearTimeout(rd._popoverTimers[id]); });
        document.getElementById('settingsDrawer').classList.remove('open');
        document.getElementById('settingsToggle').classList.remove('open');
        clearTimeout(rd._popoverTimers['settingsDrawer']);
        const kd = document.getElementById('shortcutsDrawer'); const kt = document.getElementById('shortcutsToggle');
        if (kd) kd.classList.remove('open'); if (kt) kt.classList.remove('open');
        clearTimeout(rd._popoverTimers['shortcutsDrawer']);
        rd._pinnedPopover = 'designs';
        drawer.classList.add('open');
        toggle.classList.add('open');
      }
      if (rd._syncDrawerPins) rd._syncDrawerPins();
      if (rd._syncPinIcons) rd._syncPinIcons();
      if (rd._isMobileToolbar) rd._updateMobilePopoverState();
    });

    // Desktop hover
    if (!rd._isMobile) {
      const openHover = () => {
        if (rd._selectRotating || rd._selectDragging || rd._selectResizing || rd._wallEndpointDragging || rd._wallCornerDragging || rd._wallGrabDragging) return;
        if (!rd._isDrawerPinned()) {
          clearTimeout(rd._popoverTimers['designsDrawer']);
          // Instantly close settings + shortcuts drawers to prevent stacking
          const sd = document.getElementById('settingsDrawer'); const st = document.getElementById('settingsToggle');
          clearTimeout(rd._popoverTimers['settingsDrawer']);
          if (sd) { sd.style.transition = 'none'; sd.classList.remove('open'); sd.offsetHeight; sd.style.transition = ''; }
          if (st) st.classList.remove('open');
          const kd = document.getElementById('shortcutsDrawer'); const kt = document.getElementById('shortcutsToggle');
          clearTimeout(rd._popoverTimers['shortcutsDrawer']);
          if (kd) { kd.style.transition = 'none'; kd.classList.remove('open'); kd.offsetHeight; kd.style.transition = ''; }
          if (kt) kt.classList.remove('open');
          drawer.classList.add('open');
          toggle.classList.add('open');
        }
      };
      const closeHover = () => {
        if (!rd._isDrawerPinned()) {
          // Don't close if the name input is focused (user is typing)
          const nameInput = document.getElementById('designNameInput');
          if (nameInput && document.activeElement === nameInput) return;
          rd._popoverTimers['designsDrawer'] = setTimeout(() => {
            if (!rd._isDrawerPinned()) {
              const ni = document.getElementById('designNameInput');
              if (ni && document.activeElement === ni) return;
              drawer.classList.remove('open');
              toggle.classList.remove('open');
            }
          }, 250);
        }
      };
      toggle.addEventListener('mouseenter', openHover);
      toggle.addEventListener('mouseleave', closeHover);
      drawer.addEventListener('mouseenter', openHover);
      drawer.addEventListener('mouseleave', closeHover);
    }

    // Auto-pin the drawer while the name input is focused so that no
    // hover-based close path (settings hover, shortcuts hover, mouseleave
    // timer, etc.) can collapse the drawer while the user is typing.
    const nameInputEl = document.getElementById('designNameInput');
    nameInputEl.addEventListener('focus', () => {
      if (rd._pinnedPopover !== 'designs') {
        rd._designsInputAutoPin = true;
        rd._pinnedPopover = 'designs';
      }
    });
    nameInputEl.addEventListener('blur', () => {
      if (rd._designsInputAutoPin) {
        rd._designsInputAutoPin = false;
        rd._pinnedPopover = null;
      }
    });

    // Save button — portal mode saves to server, public mode downloads file
    const saveBtn = document.getElementById('designSaveBtn');
    const saveWarning = document.getElementById('designSaveWarning');
    const isPortalMode = !!rd._portalMode;
    const updateSaveState = () => {
      const hasName = nameInputEl.value.trim().length > 0;
      saveBtn.classList.toggle('disabled', !hasName);
      if (hasName && saveWarning) saveWarning.classList.remove('visible');
    };
    nameInputEl.addEventListener('input', updateSaveState);
    updateSaveState();

    // Portal mode: setup Save dropdown (Save / Save As / Download)
    if (isPortalMode) {
      const helperEl = document.getElementById('designSaveHelper');
      if (helperEl) helperEl.textContent = 'Designs saved to your portal account.';

      const dropdownWrap = document.getElementById('designSaveDropdown');
      const arrowBtn = document.getElementById('designSaveArrow');
      if (dropdownWrap && arrowBtn) {
        arrowBtn.style.display = '';

        // Toggle dropdown on arrow click
        arrowBtn.addEventListener('click', (e) => {
          e.stopPropagation();
          dropdownWrap.classList.toggle('open');
        });

        // Close dropdown on outside click
        document.addEventListener('click', (e) => {
          if (!dropdownWrap.contains(e.target) && e.target !== arrowBtn) {
            dropdownWrap.classList.remove('open');
          }
        });

        // Save As New Version (linked version)
        const saveAsVersionBtn = document.getElementById('designSaveAsVersionBtn');
        if (saveAsVersionBtn) {
          const updateVersionState = () => {
            saveAsVersionBtn.classList.toggle('disabled', !this._portalDesignId);
          };
          updateVersionState();
          arrowBtn.addEventListener('click', updateVersionState);

          saveAsVersionBtn.addEventListener('click', () => {
            if (!this._portalDesignId) return;
            dropdownWrap.classList.remove('open');
            if (!nameInputEl.value.trim()) {
              if (saveWarning) saveWarning.classList.add('visible');
              nameInputEl.focus();
              return;
            }
            this._saveAsVersion();
          });
        }

        // Save As New Design (independent copy)
        const saveAsNewBtn = document.getElementById('designSaveAsNewBtn');
        if (saveAsNewBtn) {
          const updateNewState = () => {
            saveAsNewBtn.classList.toggle('disabled', !this._portalDesignId);
          };
          updateNewState();
          arrowBtn.addEventListener('click', updateNewState);

          saveAsNewBtn.addEventListener('click', () => {
            if (!this._portalDesignId) return;
            dropdownWrap.classList.remove('open');
            this._portalDesignId = null; // force new record
            this._portalDesignRef = null;
            this._portalDesignChainId = null;
            this._updateRefBadge();
            if (!nameInputEl.value.trim()) {
              if (saveWarning) saveWarning.classList.add('visible');
              nameInputEl.focus();
              return;
            }
            this._saveToPortal();
          });
        }

        // CUSTOM_ROOMDESIGNER_BY_CLAUDE — Save + Proposal Previews
        const savePreviewsBtn = document.getElementById('designSavePreviewsBtn');
        if (savePreviewsBtn) {
          savePreviewsBtn.addEventListener('click', () => {
            dropdownWrap.classList.remove('open');
            if (!nameInputEl.value.trim()) {
              if (saveWarning) saveWarning.classList.add('visible');
              nameInputEl.focus();
              return;
            }
            this._saveToPortal({ previews: true });
          });
        }

        // Download
        const downloadBtn = document.getElementById('designDownloadBtn');
        if (downloadBtn) {
          downloadBtn.addEventListener('click', () => {
            dropdownWrap.classList.remove('open');
            if (!nameInputEl.value.trim()) {
              if (saveWarning) saveWarning.classList.add('visible');
              nameInputEl.focus();
              return;
            }
            this._saveToFile();
          });
        }
      }
    }

    saveBtn.addEventListener('click', async () => {
      if (!nameInputEl.value.trim()) {
        if (saveWarning) saveWarning.classList.add('visible');
        nameInputEl.focus();
        return;
      }
      if (isPortalMode) {
        await this._saveToPortal();
      } else {
        this._saveToFile();
      }
      // If there's a pending action (from "Save & Leave" or "Save & Start New"), complete it
      if (this._pendingLeaveDest) {
        const dest = this._pendingLeaveDest;
        this._pendingLeaveDest = null;
        setTimeout(() => { this._isDirty = false; window.location.href = dest; }, 500);
      } else if (this._pendingNewDesign) {
        this._pendingNewDesign = false;
        setTimeout(() => this._newDesign(), 300);
      }
    });

    // Portal mode: load design list when drawer opens + search input
    if (isPortalMode) {
      const listContainer = document.getElementById('portalDesignList');
      if (listContainer) {
        // Load on first open
        const obs = new MutationObserver(() => {
          if (drawer.classList.contains('open')) this._loadDesignList();
        });
        obs.observe(drawer, { attributes: true, attributeFilter: ['class'] });
        if (drawer.classList.contains('open')) this._loadDesignList();
      }

      // Search input — filter client-side
      const searchInput = document.getElementById('portalDesignSearch');
      if (searchInput) {
        searchInput.addEventListener('input', () => {
          this._designFilter = searchInput.value.trim();
          this._designPage = 0;
          this._renderDesignList();
        });
        searchInput.addEventListener('keydown', (e) => e.stopPropagation());
        searchInput.addEventListener('keyup', (e) => e.stopPropagation());
      }
    }

    // New design button
    document.getElementById('designNewBtn').addEventListener('click', () => {
      const hasContent = rd.rooms.length > 0 || rd.objects.length > 0 ||
        (rd.currentRoom && rd.currentRoom.walls && rd.currentRoom.walls.length > 0);
      if (hasContent) {
        this._showConfirm(
          'Save your current design before starting a new one?',
          'Save & Start New',
          () => {
            const ni = document.getElementById('designNameInput');
            if (!ni || !ni.value.trim()) {
              this._pendingNewDesign = true;
              this._openDrawerForName();
              return;
            }
            if (isPortalMode) {
              this._saveToPortal().then(() => setTimeout(() => this._newDesign(), 300));
            } else {
              this._saveToFile(); setTimeout(() => this._newDesign(), 300);
            }
          },
          {
            altText: 'Don\u2019t Save',
            onAlt: () => { this._newDesign(); }
          }
        );
      } else {
        this._newDesign();
      }
    });

    // Share button
    const shareBtn = document.getElementById('designShareBtn');
    if (shareBtn) {
      shareBtn.addEventListener('click', () => { this._shareDesign(); });
    }

    // Open button — opens file picker
    const fileInput = document.getElementById('designImportFile');
    const openBtn = document.getElementById('designOpenBtn');
    if (openBtn) {
      openBtn.addEventListener('click', () => { fileInput.click(); });
    }
    fileInput.addEventListener('change', (e) => {
      if (e.target.files.length > 0) {
        this._openFromFile(e.target.files[0]);
        e.target.value = ''; // reset so same file can be re-opened
      }
    });

    // Standalone share button in step bar
    const stepShareBtn = document.getElementById('rdStepShareBtn');
    if (stepShareBtn) {
      stepShareBtn.addEventListener('click', () => { this._shareDesign(); });
    }

    // Portal PDF button
    const portalPdfBtn = document.getElementById('rdPortalPdfBtn');
    if (portalPdfBtn) {
      portalPdfBtn.addEventListener('click', () => { this._openPdfDialog(); });
    }

    // Design name input — stop propagation while typing
    const nameInput = document.getElementById('designNameInput');
    nameInput.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') { e.preventDefault(); nameInput.blur(); }
      e.stopPropagation();
    });
    nameInput.addEventListener('keyup', (e) => e.stopPropagation());

    // Migration prompt for existing localStorage designs
    this._checkMigration();
  }

  // ── View-Only Reply: Confirm Modal + Send ──────────

  _showReplyConfirmModal() {
    const rd = this.rd;
    const overlay = document.getElementById('rdViewOnlyConfirmOverlay');
    if (!overlay) return;

    const diff = window.roomDesignerOverlay ? window.roomDesignerOverlay._diffViewOnlyChanges() : { equipment: [], turf: [] };
    const hasChanges = diff.equipment.length > 0 || diff.turf.length > 0;
    const firstName = rd._viewOnlyRepFirstName || 'your rep';

    const icon = document.getElementById('rdViewOnlyConfirmIcon');
    const title = document.getElementById('rdViewOnlyConfirmTitle');
    const changes = document.getElementById('rdViewOnlyConfirmChanges');
    const note = document.getElementById('rdViewOnlyConfirmNote');
    const send = document.getElementById('rdViewOnlyConfirmSend');
    const cancel = document.getElementById('rdViewOnlyConfirmCancel');

    if (hasChanges) {
      icon.innerHTML = '<svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#006BCC" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>';
      title.textContent = 'Send your changes to ' + firstName + '?';
      let changesHtml = '';
      for (const c of diff.equipment) {
        changesHtml += '<div class="rd-confirm-change-item">' +
          '<span class="rd-confirm-change-vendor">' + c.vendor + '</span> ' +
          '<span class="rd-confirm-change-type">' + c.type + ':</span> ' +
          '<span class="rd-confirm-change-from">' + c.from + '</span>' +
          ' → <span class="rd-confirm-change-to">' + c.to + '</span></div>';
      }
      for (const c of diff.turf) {
        changesHtml += '<div class="rd-confirm-change-item">' +
          '<span class="rd-confirm-change-type">' + c.label + ':</span> ' +
          '<span class="rd-confirm-change-from">' + c.from + '</span>' +
          ' → <span class="rd-confirm-change-to">' + c.to + '</span></div>';
      }
      changes.innerHTML = changesHtml;
      changes.style.display = '';
      send.textContent = 'Send to ' + firstName;
      send.className = 'rd-viewonly-confirm-send changes';
    } else {
      icon.innerHTML = '<svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#2e7d32" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>';
      title.textContent = 'Let ' + firstName + ' know the design looks good?';
      changes.innerHTML = '';
      changes.style.display = 'none';
      send.textContent = 'Looks Good!';
      send.className = 'rd-viewonly-confirm-send approval';
    }

    note.value = '';
    send.disabled = false;
    overlay.classList.add('visible');

    const onSend = () => {
      send.removeEventListener('click', onSend);
      cancel.removeEventListener('click', onCancel);
      this._sendViewOnlyReply(diff, hasChanges, note.value.trim());
    };
    const onCancel = () => {
      send.removeEventListener('click', onSend);
      cancel.removeEventListener('click', onCancel);
      overlay.classList.remove('visible');
    };
    send.addEventListener('click', onSend);
    cancel.addEventListener('click', onCancel);
  }

  async _sendViewOnlyReply(diff, hasChanges, customerMessage) {
    const rd = this.rd;
    const overlay = document.getElementById('rdViewOnlyConfirmOverlay');
    const send = document.getElementById('rdViewOnlyConfirmSend');
    const title = document.getElementById('rdViewOnlyConfirmTitle');
    const changes = document.getElementById('rdViewOnlyConfirmChanges');
    const note = document.getElementById('rdViewOnlyConfirmNote');

    send.disabled = true;
    send.textContent = 'Sending...';

    try {
      const serialized = rd._serializeDesign();
      const payload = {
        originalShareId: rd._viewOnlyShareId || '',
        design: { meta: rd._viewOnlyMeta, design: serialized },
        replyType: hasChanges ? 'changes' : 'approval',
        changes: { equipment: diff.equipment, turf: diff.turf },
        customerMessage: customerMessage || '',
        receiverName: (rd._viewOnlyMeta && rd._viewOnlyMeta.receiverName) || '',
        senderName: (rd._viewOnlyMeta && rd._viewOnlyMeta.senderName) || ''
      };

      const res = await fetch('https://rfq-worker.ryan-29b.workers.dev/share/reply', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (!res.ok) throw new Error('Server error: ' + res.status);
      await res.json();

      // Success state
      rd._viewOnlyReplySent = true;
      rd._viewOnlyLastSentDiff = JSON.stringify({ equipment: diff.equipment, turf: diff.turf });
      const firstName = rd._viewOnlyRepFirstName || 'your rep';

      // Morph modal to success
      const icon = document.getElementById('rdViewOnlyConfirmIcon');
      icon.innerHTML = '<svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#2e7d32" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>';
      title.textContent = 'Sent! ' + firstName + ' will get your preferences.';
      changes.style.display = 'none';
      note.style.display = 'none';
      send.style.display = 'none';
      const cancel = document.getElementById('rdViewOnlyConfirmCancel');
      if (cancel) cancel.style.display = 'none';

      // Update bottom reply button
      if (window.roomDesignerOverlay) window.roomDesignerOverlay._updateViewOnlyReplyBtn();

      setTimeout(() => {
        overlay.classList.remove('visible');
        // Restore hidden elements for next use
        note.style.display = '';
        send.style.display = '';
        send.disabled = false;
        if (cancel) cancel.style.display = '';
      }, 2500);

    } catch (err) {
      console.error('[RD] Reply send error:', err);
      const meta = rd._viewOnlyMeta || {};
      const fallback = meta.repEmail ? (' Try contacting ' + (rd._viewOnlyRepFirstName || 'them') + ' directly at ' + meta.repEmail + '.') : '';
      title.textContent = 'Couldn\u2019t send.' + fallback;
      title.style.color = '#c62828';
      send.textContent = 'Try Again';
      send.disabled = false;
      send.className = 'rd-viewonly-confirm-send changes';
      const retrySend = () => {
        send.removeEventListener('click', retrySend);
        title.style.color = '';
        this._sendViewOnlyReply(diff, hasChanges, customerMessage);
      };
      send.addEventListener('click', retrySend);
    }
  }

  // ── Portal: Saved Designs ────────────────

  async _generateThumbnail() {
    try {
      const ogDataUrl = await this.rd._generateOgImage();
      if (!ogDataUrl) return null;
      // Resize to 300x158 JPEG at quality 0.6
      const img = new Image();
      await new Promise((resolve, reject) => {
        img.onload = resolve;
        img.onerror = reject;
        img.src = ogDataUrl;
      });
      const c = document.createElement('canvas');
      c.width = 300; c.height = 158;
      const ctx = c.getContext('2d');
      ctx.drawImage(img, 0, 0, 300, 158);
      return c.toDataURL('image/jpeg', 0.6);
    } catch (e) {
      return null;
    }
  }

  _getPortalRepCode() {
    try {
      const session = JSON.parse(sessionStorage.getItem('rep_portal_session') || '{}');
      return session.code || '';
    } catch (e) { return ''; }
  }

  /* CUSTOM_PORTALACCESS_BY_CLAUDE (Ryan 2026-10-02) — an external user (freelancer, role 'agency')
     designs, saves and reopens his own plans; sharing to the team and quoting are team-only and
     the worker refuses both for his token, so the buttons are not drawn. */
  _isExternalUser() {
    try {
      return (JSON.parse(sessionStorage.getItem('rep_portal_session') || '{}').role || '') === 'agency';
    } catch (e) { return false; }
  }

  async _getPortalAuthHeaders() {
    try {
      if (window.RepPortal && window.RepPortal.Auth) {
        const token = await window.RepPortal.Auth.getAccessToken();
        return token ? { 'Authorization': 'Bearer ' + token } : {};
      }
      const session = JSON.parse(sessionStorage.getItem('rep_portal_session') || '{}');
      return session.accessToken ? { 'Authorization': 'Bearer ' + session.accessToken } : {};
    } catch (e) { return {}; }
  }

  // CUSTOM_ROOMDESIGNER_BY_CLAUDE — Auth-aware fetch wrapper. On 401, attempts a
  // refresh; if that fails, prompts for password inline so the user re-authenticates
  // without leaving the page (no logout-and-back-in churn that can clobber autosave).
  async _authedFetch(url, options = {}) {
    const opts = { ...options };
    const baseHeaders = options.headers || {};
    opts.headers = { ...baseHeaders, ...(await this._getPortalAuthHeaders()) };
    let res = await fetch(url, opts);
    if (res.status !== 401) return res;

    // Try one direct refresh (in case access token expired between getAccessToken() and the fetch).
    try {
      if (window.RepPortal && window.RepPortal.Auth && window.RepPortal.Auth.refreshTokens) {
        await window.RepPortal.Auth.refreshTokens();
        opts.headers = { ...baseHeaders, ...(await this._getPortalAuthHeaders()) };
        res = await fetch(url, opts);
        if (res.status !== 401) return res;
      }
    } catch (_) { /* refresh failed — fall through to password prompt */ }

    // Refresh exhausted — prompt for password to re-auth in place.
    // Singleton: deduplicate concurrent prompts so two parallel 401s don't open two modals.
    if (!this._reloginPromise) {
      this._reloginPromise = this._promptReloginAndRefresh().finally(() => {
        this._reloginPromise = null;
      });
    }
    const ok = await this._reloginPromise;
    if (!ok) return res; // user cancelled — return the original 401
    opts.headers = { ...baseHeaders, ...(await this._getPortalAuthHeaders()) };
    return await fetch(url, opts);
  }

  // Inline re-authentication modal. Resolves true on successful sign-in (session
  // saved via RepPortal.Auth), false on cancel or unrecoverable failure. Handles
  // both the no-2FA and 2FA flows so admins don't have to detour through the portal home.
  _promptReloginAndRefresh() {
    return new Promise((resolve) => {
      let email = '';
      try {
        const sessRaw = sessionStorage.getItem('rep_portal_session') || localStorage.getItem('rep_portal_session') || '{}';
        const sess = JSON.parse(sessRaw);
        email = sess.email || '';
      } catch (_) {}

      const isDark = !!(this.rd && this.rd.darkMode);
      const bg = isDark ? '#1e2a42' : '#ffffff';
      const fg = isDark ? '#e0e0e0' : '#000F1C';
      const border = isDark ? '#3a4a64' : '#cccccc';
      const muted = isDark ? '#a0aec0' : '#555555';
      const cancelBg = isDark ? '#3a4a64' : '#eeeeee';

      const overlay = document.createElement('div');
      overlay.className = 'rd-relogin-overlay';
      overlay.style.cssText = 'position:fixed;inset:0;background:rgba(0,15,28,0.6);z-index:99999;display:flex;align-items:center;justify-content:center;backdrop-filter:blur(4px);';
      const box = document.createElement('div');
      box.style.cssText = 'background:' + bg + ';color:' + fg + ';padding:28px;border-radius:8px;max-width:420px;width:calc(100% - 40px);box-shadow:0 12px 40px rgba(0,0,0,0.35);font-family:Arial,sans-serif;';
      const safeEmail = String(email).replace(/"/g, '&quot;');

      // Inline color on every text element so theme CSS can't override (dark-on-dark heading bug).
      const headingStyle = 'margin:0 0 8px;font-size:18px;font-weight:700;color:' + fg + ';';
      const paraStyle = 'margin:0 0 16px;font-size:13px;color:' + muted + ';line-height:1.4;';
      const labelStyle = 'display:block;margin:0 0 4px;font-size:12px;font-weight:600;color:' + fg + ';';
      const inputStyle = 'width:100%;padding:8px;border:1px solid ' + border + ';border-radius:4px;margin:0 0 12px;box-sizing:border-box;font-size:14px;background:' + bg + ';color:' + fg + ';';
      const errStyle = 'color:#c00;font-size:12px;margin:0 0 12px;display:none;';
      const cancelStyle = 'padding:8px 16px;background:' + cancelBg + ';color:' + fg + ';border:0;border-radius:4px;cursor:pointer;font-size:14px;';
      const submitStyle = 'padding:8px 16px;background:#006BCC;color:#ffffff;border:0;border-radius:4px;cursor:pointer;font-size:14px;font-weight:600;';
      const linkStyle = 'background:none;border:0;color:#006BCC;cursor:pointer;font-size:12px;padding:0;text-decoration:underline;';

      box.innerHTML = ''
        // Phase 1: password
        + '<div id="rdReloginPhase1">'
        +   '<h3 style="' + headingStyle + '">Session expired</h3>'
        +   '<p style="' + paraStyle + '">Re-enter your password to keep working — your design will not be lost.</p>'
        +   '<label style="' + labelStyle + '">Email</label>'
        +   '<input type="email" id="rdReloginEmail" value="' + safeEmail + '" style="' + inputStyle + '">'
        +   '<label style="' + labelStyle + '">Password</label>'
        +   '<input type="password" id="rdReloginPwd" autocomplete="current-password" style="' + inputStyle.replace('margin:0 0 12px', 'margin:0 0 16px') + '">'
        +   '<div id="rdReloginErr1" style="' + errStyle + '"></div>'
        +   '<div style="display:flex;gap:8px;justify-content:flex-end;">'
        +     '<button id="rdReloginCancel1" type="button" style="' + cancelStyle + '">Cancel</button>'
        +     '<button id="rdReloginSubmit1" type="button" style="' + submitStyle + '">Sign in</button>'
        +   '</div>'
        + '</div>'
        // Phase 2: 2FA (hidden until needed)
        + '<div id="rdReloginPhase2" style="display:none;">'
        +   '<h3 style="' + headingStyle + '">Two-factor authentication</h3>'
        +   '<p id="rdReloginPhase2Desc" style="' + paraStyle + '">Enter the 6-digit code from your authenticator app.</p>'
        +   '<label id="rdReloginPhase2Label" style="' + labelStyle + '">Code</label>'
        +   '<input type="text" id="rdReloginCode" inputmode="numeric" autocomplete="one-time-code" maxlength="10" style="' + inputStyle.replace('margin:0 0 12px', 'margin:0 0 12px;letter-spacing:2px;') + '">'
        +   '<button id="rdReloginToggleRecovery" type="button" style="' + linkStyle + 'margin:0 0 12px;display:block;">Use a recovery code instead</button>'
        +   '<div id="rdReloginErr2" style="' + errStyle + '"></div>'
        +   '<div style="display:flex;gap:8px;justify-content:flex-end;">'
        +     '<button id="rdReloginCancel2" type="button" style="' + cancelStyle + '">Cancel</button>'
        +     '<button id="rdReloginSubmit2" type="button" style="' + submitStyle + '">Verify</button>'
        +   '</div>'
        + '</div>';
      overlay.appendChild(box);
      document.body.appendChild(overlay);

      const phase1 = box.querySelector('#rdReloginPhase1');
      const phase2 = box.querySelector('#rdReloginPhase2');
      const emailInput = box.querySelector('#rdReloginEmail');
      const pwdInput = box.querySelector('#rdReloginPwd');
      const codeInput = box.querySelector('#rdReloginCode');
      const err1 = box.querySelector('#rdReloginErr1');
      const err2 = box.querySelector('#rdReloginErr2');
      const submit1 = box.querySelector('#rdReloginSubmit1');
      const submit2 = box.querySelector('#rdReloginSubmit2');
      const cancel1 = box.querySelector('#rdReloginCancel1');
      const cancel2 = box.querySelector('#rdReloginCancel2');
      const toggleRecovery = box.querySelector('#rdReloginToggleRecovery');
      const phase2Desc = box.querySelector('#rdReloginPhase2Desc');
      const phase2Label = box.querySelector('#rdReloginPhase2Label');

      setTimeout(() => { (email ? pwdInput : emailInput).focus(); }, 50);

      let tempToken = null;
      let useRecoveryCode = false;

      const cleanup = (result) => { try { overlay.remove(); } catch (_) {} resolve(result); };
      cancel1.addEventListener('click', () => cleanup(false));
      cancel2.addEventListener('click', () => cleanup(false));

      const submitPwd = async () => {
        const e = (emailInput.value || '').trim();
        const p = pwdInput.value || '';
        if (!e || !p) { err1.textContent = 'Email and password required.'; err1.style.display = 'block'; return; }
        submit1.disabled = true; submit1.textContent = 'Signing in...';
        err1.style.display = 'none';
        try {
          if (!window.RepPortal || !window.RepPortal.Auth || !window.RepPortal.Auth.validate) {
            throw new Error('Auth module unavailable — reload the page to sign in.');
          }
          const data = await window.RepPortal.Auth.validate(e, p);
          if (data && data.requires2fa) {
            // Swap to 2FA panel
            tempToken = data.tempToken;
            phase1.style.display = 'none';
            phase2.style.display = 'block';
            setTimeout(() => codeInput.focus(), 50);
            return;
          }
          if (!data || !data.accessToken) {
            throw new Error('Sign in failed — please try again.');
          }
          window.RepPortal.Auth.saveSession(data);
          cleanup(true);
        } catch (err) {
          err1.textContent = (err && err.message) || 'Sign in failed.';
          err1.style.display = 'block';
          submit1.disabled = false; submit1.textContent = 'Sign in';
          pwdInput.select();
        }
      };

      const submit2fa = async () => {
        const code = (codeInput.value || '').trim();
        if (!code) { err2.textContent = 'Code required.'; err2.style.display = 'block'; return; }
        if (!tempToken) { err2.textContent = 'Session lost — start over.'; err2.style.display = 'block'; return; }
        submit2.disabled = true; submit2.textContent = 'Verifying...';
        err2.style.display = 'none';
        try {
          const data = await window.RepPortal.Auth.verify2fa(
            tempToken,
            useRecoveryCode ? null : code,
            useRecoveryCode ? code : null
          );
          if (!data || !data.accessToken) throw new Error('Verification failed — try again.');
          window.RepPortal.Auth.saveSession(data);
          cleanup(true);
        } catch (err) {
          err2.textContent = (err && err.message) || 'Verification failed.';
          err2.style.display = 'block';
          submit2.disabled = false; submit2.textContent = 'Verify';
          codeInput.select();
        }
      };

      toggleRecovery.addEventListener('click', () => {
        useRecoveryCode = !useRecoveryCode;
        if (useRecoveryCode) {
          phase2Desc.textContent = 'Enter one of your saved recovery codes.';
          phase2Label.textContent = 'Recovery code';
          codeInput.setAttribute('inputmode', 'text');
          codeInput.removeAttribute('autocomplete');
          codeInput.value = '';
          toggleRecovery.textContent = 'Use authenticator code instead';
        } else {
          phase2Desc.textContent = 'Enter the 6-digit code from your authenticator app.';
          phase2Label.textContent = 'Code';
          codeInput.setAttribute('inputmode', 'numeric');
          codeInput.setAttribute('autocomplete', 'one-time-code');
          codeInput.value = '';
          toggleRecovery.textContent = 'Use a recovery code instead';
        }
        codeInput.focus();
      });

      submit1.addEventListener('click', submitPwd);
      submit2.addEventListener('click', submit2fa);
      pwdInput.addEventListener('keydown', (ev) => { if (ev.key === 'Enter') submitPwd(); });
      emailInput.addEventListener('keydown', (ev) => { if (ev.key === 'Enter') submitPwd(); });
      codeInput.addEventListener('keydown', (ev) => { if (ev.key === 'Enter') submit2fa(); });
      overlay.addEventListener('keydown', (ev) => { if (ev.key === 'Escape') cleanup(false); });
    });
  }
  // END CUSTOM_ROOMDESIGNER_BY_CLAUDE

  /* CUSTOM_ROOMDESIGNER_BY_CLAUDE — opts.previews decides whether this save also
     regenerates the Project Assembly proposal previews (4 renders, or 8 when the
     design has a designer page list). Plain Save no longer does: reps iterate all
     day, and paying a full render pass per save to feed a packet they may never
     build is the wrong default.

     ⚠️ The cost of making it optional is that previews can now be OLDER than the
     design, which the packet cannot see on its own — it would happily build from
     last week's images. That is why the variant PATCH stamps `variantsAt` and the
     portal flags a plan whose design has been saved since. Do not remove one of
     those without the other. */
  async _saveToPortal(opts) {
    const rd = this.rd;
    const nameInput = document.getElementById('designNameInput');
    const name = (nameInput && nameInput.value.trim()) || this._untitledName();
    const repCode = this._getPortalRepCode();
    if (!repCode) { rd.updateStatus('Not signed in.', { error: true }); return; }

    const saveBtn = document.getElementById('designSaveBtn');
    if (saveBtn) { saveBtn.textContent = 'Saving...'; saveBtn.classList.add('disabled'); }

    try {
      const design = rd._serializeDesign();
      const { spaceSummary, totalArea } = this._buildSpaceSummary(rd);
      const summaryText = spaceSummary.length + ' space' + (spaceSummary.length !== 1 ? 's' : '') + ', ' + totalArea.toLocaleString() + ' sq ft';

      const [thumbnailDataUrl, spaceImageUrls] = await Promise.all([
        this._generateThumbnail(),
        this._uploadSpaceImages(rd).catch(() => null),
      ]);

      const body = {
        name,
        design,
        meta: { spaceSummary: summaryText },
      };
      if (thumbnailDataUrl) body.thumbnailDataUrl = thumbnailDataUrl;
      if (spaceImageUrls && spaceImageUrls.length > 0) body.spaceImages = spaceImageUrls;
      // CUSTOM_PROJECTASSEMBLY_BY_CLAUDE (Ryan 2026-09-23) — everything on the plan, with
      // quantities, for the packet's optional equipment-list page. See _buildPlanItems.
      const _planItems = this._buildPlanItems(rd);
      if (_planItems) body.planItems = _planItems;
      if (this._portalDesignId) body.id = this._portalDesignId;

      const res = await this._authedFetch('https://rfq-worker.ryan-29b.workers.dev/design-save', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      const data = await res.json();
      if (!res.ok || !data.success) {
        // If not authorized (stale ID from another rep), clear ID and retry as new
        if (res.status === 403 && this._portalDesignId) {
          this._portalDesignId = null;
          if (saveBtn) { saveBtn.textContent = 'Save'; }
          return this._saveToPortal();
        }
        rd.updateStatus('Save failed: ' + (data.error || 'Unknown error'), { error: true });
        return;
      }

      this._portalDesignId = data.id;
      const isFirstRef = !this._portalDesignRef && data.ref;
      this._portalDesignRef = data.ref || this._portalDesignRef || null;
      this._portalDesignChainId = data.chainId || this._portalDesignChainId || null;
      this._updateRefBadge();
      const _msg = isFirstRef ? 'Saved as ' + data.ref : 'Design saved to portal.';
      const _bg = window.roomDesignerBackground;
      rd.updateStatus(_bg && _bg._bgImages.length > 0 ? _msg + ' Blueprint image excluded.' : _msg);
      this._flashSaveButton();
      this._isDirty = false;
      // Refresh list
      this._loadDesignList();
      // Fire-and-forget PDF generation for versioned designs
      if (this._portalDesignRef) this._generateLayoutPdf(this._portalDesignRef);
      // CUSTOM_PROJECTASSEMBLY_BY_CLAUDE — Path B: variant generation, on request only.
      // See the note on _saveToPortal — plain Save is quick and leaves previews alone.
      /* CUSTOM_QUOTELAYOUTSWAP_BY_CLAUDE — a tab opened by the Workstation's "Fix in layout" (?fixFor=<ref>)
         redraws the PDF pages on EVERY save, so the rep's plain Save is all it takes. */
      const _fixFor = new URLSearchParams(window.location.search).get('fixFor');
      if (data.id && ((opts && opts.previews) || _fixFor)) this._kickoffVariantGeneration(rd, data.id);
      // END CUSTOM_PROJECTASSEMBLY_BY_CLAUDE
    } catch (e) {
      rd.updateStatus('Save failed: network error.', { error: true });
    } finally {
      if (saveBtn) { saveBtn.classList.remove('disabled'); }
    }
  }

  async _saveAsVersion() {
    const rd = this.rd;
    const nameInput = document.getElementById('designNameInput');
    const name = (nameInput && nameInput.value.trim()) || this._untitledName();
    const repCode = this._getPortalRepCode();
    if (!repCode) { rd.updateStatus('Not signed in.', { error: true }); return; }
    if (!this._portalDesignId) { rd.updateStatus('Save the design first before creating a version.', { error: true }); return; }

    const saveBtn = document.getElementById('designSaveBtn');
    if (saveBtn) { saveBtn.textContent = 'Saving...'; saveBtn.classList.add('disabled'); }

    try {
      const design = rd._serializeDesign();
      const { spaceSummary, totalArea } = this._buildSpaceSummary(rd);
      const summaryText = spaceSummary.length + ' space' + (spaceSummary.length !== 1 ? 's' : '') + ', ' + totalArea.toLocaleString() + ' sq ft';

      const [thumbnailDataUrl, spaceImageUrls] = await Promise.all([
        this._generateThumbnail(),
        this._uploadSpaceImages(rd).catch(() => null),
      ]);

      const res = await this._authedFetch('https://rfq-worker.ryan-29b.workers.dev/design-save-version', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sourceDesignId: this._portalDesignId,
          name,
          design,
          meta: { spaceSummary: summaryText },
          thumbnailDataUrl: thumbnailDataUrl || undefined,
          spaceImages: spaceImageUrls && spaceImageUrls.length > 0 ? spaceImageUrls : undefined,
          planItems: this._buildPlanItems(rd) || undefined,
        }),
      });

      const data = await res.json();
      if (!res.ok || !data.success) {
        rd.updateStatus('Version save failed: ' + (data.error || 'Unknown error'), { error: true });
        return;
      }

      // Switch to the new version
      this._portalDesignId = data.id;
      this._portalDesignRef = data.ref;
      this._portalDesignChainId = data.chainId;
      this._updateRefBadge();
      const _vmsg = 'Saved as ' + data.ref;
      const _vbg = window.roomDesignerBackground;
      rd.updateStatus(_vbg && _vbg._bgImages.length > 0 ? _vmsg + ' Blueprint image excluded.' : _vmsg);
      this._flashSaveButton();
      this._isDirty = false;
      this._loadDesignList();
      // Fire-and-forget PDF generation for versioned designs
      if (data.ref) this._generateLayoutPdf(data.ref);
      // CUSTOM_PROJECTASSEMBLY_BY_CLAUDE — Path B: a NEW VERSION always regenerates.
      // A version is the checkpoint a packet points at, so it is the one save that
      // should never need a second trip through the designer to be usable.
      if (data.id) this._kickoffVariantGeneration(rd, data.id);
      // END CUSTOM_PROJECTASSEMBLY_BY_CLAUDE
    } catch (e) {
      rd.updateStatus('Version save failed: network error.', { error: true });
    } finally {
      if (saveBtn) { saveBtn.classList.remove('disabled'); }
    }
  }

  /* CUSTOM_ROOM_DESIGNER_BY_CLAUDE — Auto-generate Layout Only PDF on save */
  _generateLayoutPdf(ref) {
    const rd = this.rd;
    (async () => {
      try {
        const images = await this._uploadSpaceImages(rd);
        if (!images) return;
        const { spaceSummary, totalArea } = this._buildSpaceSummary(rd);

        // Rep info from portal session
        let repName = '', repEmail = '', repPhone = '';
        try {
          const sess = JSON.parse(sessionStorage.getItem('rep_portal_session') || '{}');
          repName = sess.name || sess.fullName || '';
          repEmail = sess.email || '';
          repPhone = sess.phone || '';
        } catch (_e) { /* no session */ }

        await fetch('https://rfq-worker.ryan-29b.workers.dev/share/pdf', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            images,
            items: [],
            spaceSummary,
            totalArea,
            meta: { senderName: repName, repName, repEmail, repPhone, ref },
            type: 'plan',
          }),
        });
      } catch (_e) { /* silent — packet generator has thumbnail fallback */ }
    })();
  }
  /* END CUSTOM_ROOM_DESIGNER_BY_CLAUDE */

  _updateRefBadge() {
    const badge = document.getElementById('designRefBadge');
    if (!badge) return;
    if (this._portalDesignRef) {
      badge.textContent = this._portalDesignRef;
      badge.style.display = '';
    } else {
      badge.style.display = 'none';
      badge.textContent = '';
    }
  }

  async _loadDesignList() {
    const listContainer = document.getElementById('portalDesignList');
    if (!listContainer) return;

    const repCode = this._getPortalRepCode();
    if (!repCode) return;

    listContainer.innerHTML = '<div class="portal-designs-loading">Loading...</div>';

    try {
      const res = await this._authedFetch('https://rfq-worker.ryan-29b.workers.dev/design-list', {});
      const data = await res.json();
      if (!res.ok || !data.success) {
        listContainer.innerHTML = '<div class="portal-designs-empty">Failed to load designs.</div>';
        return;
      }

      this._allDesigns = data.designs || [];
      this._designPage = 0;
      this._designFilter = '';
      this._renderDesignList();
    } catch (e) {
      listContainer.innerHTML = '<div class="portal-designs-empty">Failed to load designs.</div>';
    }
  }

  _getFilteredDesigns() {
    if (!this._allDesigns) return [];
    if (!this._designFilter) return this._allDesigns;
    const q = this._designFilter.toLowerCase();
    return this._allDesigns.filter(d =>
      (d.name || '').toLowerCase().includes(q) ||
      (d.spaceSummary || '').toLowerCase().includes(q)
    );
  }

  _renderDesignList() {
    const PAGE_SIZE = 20;
    const listContainer = document.getElementById('portalDesignList');
    if (!listContainer) return;

    const filtered = this._getFilteredDesigns();

    if (filtered.length === 0) {
      listContainer.innerHTML = '<div class="portal-designs-empty">' +
        (this._designFilter ? 'No designs match your search.' : 'No saved designs yet.') +
        '</div>';
      const countEl = document.getElementById('portalDesignCount');
      if (countEl) countEl.textContent = '';
      return;
    }

    // Group designs by chainId (version chains)
    const groupMap = new Map();
    for (const d of filtered) {
      const key = d.chainId || ('_solo_' + d.id);
      if (!groupMap.has(key)) groupMap.set(key, []);
      groupMap.get(key).push(d);
    }
    // Sort versions within each group by updatedAt desc (latest first)
    for (const versions of groupMap.values()) {
      versions.sort((a, b) => (b.updatedAt || '').localeCompare(a.updatedAt || ''));
    }
    // Sort groups by latest version's updatedAt desc
    const groups = [...groupMap.values()].sort((a, b) =>
      (b[0].updatedAt || '').localeCompare(a[0].updatedAt || '')
    );

    const showCount = Math.min((this._designPage + 1) * PAGE_SIZE, groups.length);
    const visible = groups.slice(0, showCount);

    listContainer.innerHTML = '';
    for (const group of visible) {
      if (group.length === 1) {
        listContainer.appendChild(this._buildDesignCard(group[0]));
      } else {
        listContainer.appendChild(this._buildDesignGroupCard(group));
      }
    }

    // "Load More" button
    if (showCount < groups.length) {
      const more = document.createElement('button');
      more.className = 'portal-designs-load-more';
      more.textContent = 'Show More (' + (groups.length - showCount) + ' remaining)';
      more.addEventListener('click', () => {
        this._designPage++;
        this._renderDesignList();
      });
      listContainer.appendChild(more);
    }

    // Update count
    const countEl = document.getElementById('portalDesignCount');
    if (countEl) {
      countEl.textContent = groups.length + ' design' + (groups.length !== 1 ? 's' : '') +
        (this._designFilter ? ' found' : '');
    }
  }

  _buildDesignCard(entry) {
    const card = document.createElement('div');
    card.className = 'portal-design-card';
    card.dataset.designId = entry.id;

    const dateStr = entry.updatedAt ? new Date(entry.updatedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '';
    const isActive = this._portalDesignId === entry.id;

    const sharedTag = entry.sharedBy ? '<div class="portal-design-card__shared-tag">Shared by ' + this._escapeHtml(entry.sharedBy.name) + '</div>' : '';
    const refTag = entry.ref ? '<span class="portal-design-card__ref">' + this._escapeHtml(entry.ref) + '</span>' : '';

    card.innerHTML =
      '<div class="portal-design-card__info">' +
        '<div class="portal-design-card__name">' + this._escapeHtml(entry.name) + refTag + '</div>' +
        sharedTag +
        '<div class="portal-design-card__meta">' + dateStr + '</div>' +
      '</div>' +
      '<div class="portal-design-card__actions">' +
        '<button class="portal-design-card__btn portal-design-card__btn--open" title="Open" data-action="open" data-id="' + entry.id + '">' +
          '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/></svg>' +
        '</button>' +
        (this._isExternalUser() ? '' :
        '<button class="portal-design-card__btn portal-design-card__btn--share" title="Share internally" data-action="share" data-id="' + entry.id + '" data-name="' + this._escapeHtml(entry.name) + '">' +
          '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8"/><polyline points="16 6 12 2 8 6"/><line x1="12" y1="2" x2="12" y2="15"/></svg>' +
        '</button>') +
        '<button class="portal-design-card__btn portal-design-card__btn--delete" title="Delete" data-action="delete" data-id="' + entry.id + '" data-name="' + this._escapeHtml(entry.name) + '">' +
          '<svg width="14" height="14" viewBox="2 3 20 21" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M3 7h18M8 7V5h8v2"/><path d="M5 7l1 16h12l1-16"/><path d="M10 11v8M14 11v8"/></svg>' +
        '</button>' +
      '</div>';

    if (isActive) card.classList.add('portal-design-card--active');

    card.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-action]');
      if (!btn) return;
      const action = btn.dataset.action;
      const id = btn.dataset.id;
      if (action === 'open') this._loadFromPortal(id);
      else if (action === 'share') {
        const designName = btn.dataset.name || 'this design';
        this._showShareInternalDialog(id, designName);
      }
      else if (action === 'delete') {
        const designName = btn.dataset.name || 'this design';
        this._showConfirm('Delete "' + designName + '"? This cannot be undone.', 'Delete', () => {
          this._deleteFromPortal(id);
        });
      }
    });

    return card;
  }

  _buildDesignGroupCard(group) {
    // group is an array of design entries sorted by updatedAt desc (latest first)
    const latest = group[0];
    const card = document.createElement('div');
    card.className = 'portal-design-group';

    // Extract base ref (e.g., "PLAN-1001" from "PLAN-1001-A")
    const baseRef = latest.ref ? latest.ref.replace(/-[A-Z]$/, '') : '';
    const dateStr = latest.updatedAt ? new Date(latest.updatedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '';
    const isActive = group.some(d => d.id === this._portalDesignId);

    const refTag = baseRef ? '<span class="portal-design-card__ref">' + this._escapeHtml(baseRef) + '</span>' : '';
    const chevronSvg = '<svg class="portal-design-group__chevron" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><polyline points="6 9 12 15 18 9"/></svg>';
    const verCountText = group.length + ' version' + (group.length !== 1 ? 's' : '');

    // Build version rows — clickable rows, no individual buttons (cleaner)
    let versionsHtml = '';
    for (let i = 0; i < group.length; i++) {
      const d = group[i];
      const rev = d.ref ? d.ref.match(/-([A-Z])$/)?.[1] || '' : '';
      const vDate = d.updatedAt ? new Date(d.updatedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '';
      const latestTag = i === 0 ? '<span class="portal-design-group__ver-latest">Latest</span>' : '';
      const activeClass = d.id === this._portalDesignId ? ' portal-design-group__ver-row--active' : '';
      versionsHtml +=
        '<div class="portal-design-group__ver-row' + activeClass + '" data-ver-id="' + d.id + '">' +
          '<span class="portal-design-group__ver-ref">Version ' + (rev || String.fromCharCode(65 + i)) + '</span>' +
          '<span class="portal-design-group__ver-date">' + vDate + '</span>' +
          latestTag +
        '</div>';
    }

    // Header: info + chevron toggle + action buttons (open, share, delete)
    card.innerHTML =
      '<div class="portal-design-group__header">' +
        '<div class="portal-design-card__info">' +
          '<div class="portal-design-card__name">' + this._escapeHtml(latest.name) + refTag + '</div>' +
          '<div class="portal-design-card__meta">' + dateStr + ' \u00B7 ' + verCountText + ' ' + chevronSvg + '</div>' +
        '</div>' +
        '<div class="portal-design-card__actions">' +
          '<button class="portal-design-card__btn portal-design-card__btn--open" title="Open latest" data-action="open" data-id="' + latest.id + '">' +
            '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/></svg>' +
          '</button>' +
          (this._isExternalUser() ? '' :
          '<button class="portal-design-card__btn portal-design-card__btn--share" title="Share internally" data-action="share" data-id="' + latest.id + '" data-name="' + this._escapeHtml(latest.name) + '">' +
            '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8"/><polyline points="16 6 12 2 8 6"/><line x1="12" y1="2" x2="12" y2="15"/></svg>' +
          '</button>') +
          '<button class="portal-design-card__btn portal-design-card__btn--delete" title="Delete" data-action="delete" data-id="' + latest.id + '" data-name="' + this._escapeHtml(latest.name) + '">' +
            '<svg width="14" height="14" viewBox="2 3 20 21" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M3 7h18M8 7V5h8v2"/><path d="M5 7l1 16h12l1-16"/><path d="M10 11v8M14 11v8"/></svg>' +
          '</button>' +
        '</div>' +
      '</div>' +
      '<div class="portal-design-group__versions">' + versionsHtml + '</div>';

    if (isActive) card.classList.add('portal-design-card--active');

    // Toggle expand on header click (only the chevron or empty space, not action buttons)
    const header = card.querySelector('.portal-design-group__header');
    header.addEventListener('click', (e) => {
      if (e.target.closest('[data-action]')) return; // let action buttons handle themselves
      card.classList.toggle('portal-design-group--expanded');
    });

    // Action buttons on header
    card.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-action]');
      if (!btn) return;
      const action = btn.dataset.action;
      const id = btn.dataset.id;
      if (action === 'open') this._loadFromPortal(id);
      else if (action === 'share') {
        const designName = btn.dataset.name || latest.name || 'this design';
        this._showShareInternalDialog(id, designName);
      }
      else if (action === 'delete') {
        const designName = btn.dataset.name || latest.name || 'this design';
        this._showConfirm('Delete "' + designName + '"? This cannot be undone.', 'Delete', () => {
          this._deleteFromPortal(id);
        });
      }
    });

    // Version rows clickable to open
    card.querySelectorAll('.portal-design-group__ver-row').forEach(row => {
      row.addEventListener('click', () => {
        const id = row.dataset.verId;
        if (id) this._loadFromPortal(id);
      });
    });

    return card;
  }

  async _showShareInternalDialog(designId, designName) {
    const rd = this.rd;
    const repCode = this._getPortalRepCode();
    if (!repCode || this._isExternalUser()) return;

    // Fetch rep list (cached for session). `include=gld-external` lists external users with the
    // Gym Layout Designer on for an ADMIN only (a copy for marketing); the worker ignores it for a rep.
    if (!this._repListCache) {
      try {
        const res = await this._authedFetch('https://rfq-worker.ryan-29b.workers.dev/rep-list-basic?include=gld-external', {});
        const data = await res.json();
        this._repListCache = (data.success && data.reps) ? data.reps : [];
      } catch (e) {
        rd.updateStatus('Failed to load rep list.', { error: true });
        return;
      }
    }

    if (this._repListCache.length === 0) {
      rd.updateStatus('No other reps to share with.');
      return;
    }

    // Build options HTML
    const options = this._repListCache.map(r =>
      '<option value="' + this._escapeHtml(r.code) + '">' + this._escapeHtml(r.name) + (r.external ? ' (external, gets a copy)' : '') + '</option>'
    ).join('');

    this._showConfirm(
      'Share "' + this._escapeHtml(designName) + '" with:<br><br>' +
      '<select id="rdShareRepSelect" style="width:100%;padding:8px;border-radius:4px;border:1px solid var(--rd-border,#555);background:var(--rd-bg,#1a1a2e);color:var(--rd-text,#e0e0e0);font-size:14px;">' +
      '<option value="">Select a person...</option>' + options + '</select>' +
      /* CUSTOM_GYMLAYOUT_BY_CLAUDE (Ryan 2026-10-10) — the copy may land under another name
         (a freelancer sees "Weight Room 1", not the customer's). Prefilled with today's name;
         a changed name also blanks the customer name saved inside the copy (worker side). */
      '<br><br><label for="rdShareNameInput" style="display:block;margin-bottom:4px;">Name it as</label>' +
      '<input type="text" id="rdShareNameInput" maxlength="200" value="' + this._escapeHtml(designName === 'this design' ? '' : designName) + '" placeholder="Name they will see" style="width:100%;box-sizing:border-box;padding:8px;border-radius:4px;border:1px solid var(--rd-border,#555);background:var(--rd-bg,#1a1a2e);color:var(--rd-text,#e0e0e0);font-size:14px;">',
      'Share',
      () => {
        const select = document.getElementById('rdShareRepSelect');
        const recipientCode = select ? select.value : '';
        if (!recipientCode) {
          rd.updateStatus('Please select a rep to share with.');
          return;
        }
        const nameInput = document.getElementById('rdShareNameInput');
        const shareName = nameInput ? nameInput.value.trim() : '';
        this._shareDesignInternal(designId, recipientCode, shareName);
      }
    );
  }

  async _shareDesignInternal(designId, recipientCode, shareName) {
    const rd = this.rd;
    const repCode = this._getPortalRepCode();
    if (!repCode) return;

    rd.updateStatus('Sharing design...');

    try {
      const res = await this._authedFetch('https://rfq-worker.ryan-29b.workers.dev/design-share', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(shareName ? { designId, recipientCode, name: shareName } : { designId, recipientCode }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        rd.updateStatus('Share failed: ' + (data.error || 'Unknown error'), { error: true });
        return;
      }

      const recipientName = this._repListCache ?
        (this._repListCache.find(r => r.code === recipientCode) || {}).name || recipientCode :
        recipientCode;
      rd.updateStatus('Design shared with ' + recipientName + (shareName ? ' as "' + shareName + '"' : '') + '.');
    } catch (e) {
      rd.updateStatus('Share failed: network error.', { error: true });
    }
  }

  /* CUSTOM_GYMLAYOUT_BY_CLAUDE (Ryan 2026-10-10) — a design somebody shared with you opens
     the way a presenter wants it, EVERY time: on the Customize tab, the left panel
     contracted, and the "This Layout" cards closed. The view switches (wall dimensions off,
     grid on, ...) are written into the copy once by the worker, so the recipient's own
     toggles stick. A design of your own clears the flag, so nothing else changes. */
  _applySharedOpenView(record) {
    const rd = this.rd;
    const shared = !!(record && record.sharedBy);
    rd._collapseLayoutCards = shared;
    if (!shared) return;
    // Close the "This Layout" cards now (updateMeasurements keeps whatever is open).
    const list = document.getElementById('rdSpaceList');
    if (list) list.querySelectorAll('.rd-space-card.expanded, .rd-total-summary.expanded').forEach(el => el.classList.remove('expanded'));
    if (typeof rd.updateMeasurements === 'function') rd.updateMeasurements();
    const ov = window.roomDesignerOverlay;
    if (!ov || ov._wipeInProgress) return;
    if (typeof ov._syncFinishingTab === 'function') ov._syncFinishingTab();
    const tab = document.querySelector('.rd-step-tab[data-step="finishing"]');
    if (!tab || tab.classList.contains('disabled')) return;
    if (ov.currentStep === 'finishing') {
      if (ov._sidePanelOpen && typeof ov._minimizeSidePanel === 'function') {
        ov._minimizeSidePanel();
        setTimeout(() => { if (!rd._viewOnly) rd.fitToView({ inset: LOAD_FIT_INSET }); }, 400);
      }
      return;
    }
    ov._minimizePanelOnOpen = true;
    ov._switchStep('finishing');
  }

  _escapeHtml(str) {
    const div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }

  async _loadFromPortal(designId) {
    const rd = this.rd;
    const repCode = this._getPortalRepCode();
    if (!repCode) return;

    rd.updateStatus('Loading design...');

    try {
      const res = await this._authedFetch('https://rfq-worker.ryan-29b.workers.dev/design/' + designId, {});
      const data = await res.json();
      if (!res.ok || !data.success || !data.design) {
        rd.updateStatus('Failed to load design.', { error: true });
        return;
      }

      const record = data.design;
      rd._loadDesignData(record.design);
      this._updateDesignNameUI(record.name || '');
      this._portalDesignId = record.id;
      this._portalDesignRef = record.ref || null;
      this._portalDesignChainId = record.chainId || null;
      /* CUSTOM_ROOMDESIGNER_BY_CLAUDE (Ryan 2026-05-21) — Deal backlinks
         from /quote-link-design. Used by the Mode picker to pre-select
         "Append to {ref}" when this floor plan is already linked to a
         deal. */
      this._portalDesignLinkedDeals = Array.isArray(record.linkedDeals) ? record.linkedDeals : [];
      this._updateRefBadge();
      this._applySharedOpenView(record);

      // Update autosave cache (never from the hidden Workstation copy: CUSTOM_QUOTELAYOUTSWAP_BY_CLAUDE)
      const prefix = this._getRepPrefix();
      if (!this._embed) {
        localStorage.setItem(prefix + 'rd_autosave', JSON.stringify(record.design));
        localStorage.setItem(prefix + 'rd_autosave_time', String(Date.now()));
      }

      rd.updateStatus('Design "' + record.name + '" loaded.');
      this._isDirty = false;
      /* CUSTOM_QUOTELAYOUTSWAP_BY_CLAUDE — the Quote Workstation's "Update the layout" link. */
      if (record.id === designId) this._maybeApplyFloorSwap();

      // Update active state in list
      document.querySelectorAll('.portal-design-card').forEach(c => {
        c.classList.toggle('portal-design-card--active', c.dataset.designId === designId);
      });
    } catch (e) {
      rd.updateStatus('Failed to load design: network error.', { error: true });
    }
  }

  /* CUSTOM_QUOTELAYOUTSWAP_BY_CLAUDE (Ryan 2026-10-10, RW-1379-A/-B/-C) — "UPDATE THE LAYOUT TO <colour>".

     A quote version gets its own copy of the gym layout (lib/quote-layout-copy.js on the worker), and
     its last page should show the flooring THAT quote prices. When the Quote Workstation sees the
     quote's flooring no longer on the layout, its button opens this design with
     ?designId=<copy>&swapFrom=<skus on the layout>&swapTo=<skus on the quote>. Here: every room or zone
     whose colour carries a swapFrom SKU takes the colour of the SAME product carrying a swapTo SKU (a
     colour change, the Customize panel's own apply), the layout caches drop, and the design saves
     with proposal previews, so the quote PDF's plan pages are redrawn in the new colour. The params
     are stripped first so a reload never swaps twice. Nothing matches -> it says so and saves nothing. */
  async _maybeApplyFloorSwap() {
    const params = new URLSearchParams(window.location.search);
    const norm = (v) => String(v || '').trim().toUpperCase();
    const list = (k) => (params.get(k) || '').split(',').map(norm).filter(Boolean);
    const from = list('swapFrom');
    const to = list('swapTo');
    const removeEq = list('removeEq');
    const redraw = params.get('redraw') === '1';   // only redraw the PDF pages (they are older than the last save)
    if (!(from.length && to.length) && !removeEq.length && !redraw) return;
    this._embedPending = this._embed;
    try {
      ['swapFrom', 'swapTo', 'swapFor', 'removeEq', 'redraw'].forEach((k) => params.delete(k));
      const qs = params.toString();
      window.history.replaceState(null, '', window.location.pathname + (qs ? '?' + qs : ''));
    } catch (_e) {}
    const rd = this.rd;
    let ov = null;
    for (let i = 0; i < 150; i++) {
      ov = window.roomDesignerOverlay;
      if (ov && Array.isArray(ov.products) && ov.products.length && typeof ov._collectViewOnlyFloorTargets === 'function') break;
      ov = null;
      await new Promise((r) => setTimeout(r, 100));
    }
    if (!ov) {
      rd.updateStatus('Could not update the layout: the flooring list did not load. Reload to try again.', { error: true });
      this._embedReport({ ok: false, reason: 'flooring-list-did-not-load' });
      return;
    }
    try { rd.render(); } catch (_e) {}
    const plan = (from.length && to.length) ? RoomDesignerStorage.planFloorSwap(ov._collectViewOnlyFloorTargets(), from, to) : { changes: [] };
    /* Equipment taken off the quote comes off the layout (every placed piece with that SKU). A count
       lowered but not to zero is NOT handled here: the Workstation flags it for the rep (Ryan's pick). */
    const removed = [];
    if (removeEq.length && Array.isArray(rd.objects)) {
      const imgs = rd._equipmentImages || {};
      for (let i = rd.objects.length - 1; i >= 0; i--) {
        const o = rd.objects[i];
        if (!o || o.type !== 'equipment') continue;
        const info = imgs[o.equipmentId];
        if (info && removeEq.indexOf(norm(info.sku)) !== -1) { removed.push(info.title || info.sku); rd.objects.splice(i, 1); }
      }
    }
    if (!plan.changes.length && !removed.length && !redraw) {
      rd.updateStatus('Nothing to update: no room uses the flooring the quote replaced and no equipment on the layout was taken off the quote.', { error: true });
      this._embedReport({ ok: false, reason: 'nothing-matched' });
      return;
    }
    plan.changes.forEach((c) => c.apply());
    try { if (typeof rd.selectedObject !== 'undefined') rd.selectedObject = null; } catch (_e) {}
    try { rd.render(); if (typeof rd.updateMeasurements === 'function') rd.updateMeasurements(); } catch (_e) {}
    this._isDirty = true;
    const words = Array.from(new Set(plan.changes.map((c) => c.fromName + ' to ' + c.toName)))
      .concat(removed.length ? ['removed ' + removed.length + ' piece' + (removed.length === 1 ? '' : 's') + ' of equipment'] : []).join(', ');
    this._embedSummary = { colours: plan.changes.map((c) => c.toName), removed: removed };
    rd.updateStatus((words ? 'Layout updated (' + words + '). ' : '') + 'Saving and redrawing the proposal pages...');
    await this._saveToPortal({ previews: true });
    /* A save that failed leaves the design dirty and never starts the previews: say so now. */
    if (this._isDirty) this._embedReport({ ok: false, reason: 'save-failed' });
  }

  /* CUSTOM_QUOTELAYOUTSWAP_BY_CLAUDE — tell the Workstation that ran this page hidden how it went. Once. */
  _embedReport(msg) {
    if (!this._embedPending) return;
    this._embedPending = false;
    try {
      if (window.parent && window.parent !== window) {
        window.parent.postMessage(Object.assign({ type: 'bsfs-layout-update', designId: this._portalDesignId || null }, this._embedSummary || {}, msg), window.location.origin);
      }
    } catch (_e) {}
  }

  async _deleteFromPortal(designId) {
    const rd = this.rd;
    const repCode = this._getPortalRepCode();
    if (!repCode) return;

    try {
      const res = await this._authedFetch('https://rfq-worker.ryan-29b.workers.dev/design-delete', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: designId }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        rd.updateStatus('Delete failed: ' + (data.error || 'Unknown error'), { error: true });
        return;
      }

      // If we deleted the currently-loaded design, clear portal ID and ref
      if (this._portalDesignId === designId) {
        this._portalDesignId = null;
        this._portalDesignRef = null;
        this._portalDesignChainId = null;
        this._updateRefBadge();
      }

      rd.updateStatus('Design deleted.');
      this._loadDesignList();
    } catch (e) {
      rd.updateStatus('Delete failed: network error.', { error: true });
    }
  }
}

/* CUSTOM_QUOTELAYOUTSWAP_BY_CLAUDE — the decision half of _maybeApplyFloorSwap, pure so a test can run it.
   groups: _collectViewOnlyFloorTargets() ([{targets:[{product:{name, colors:[{id,name,sku}]}, colorId, apply}]}]).
   A target changes when its CURRENT colour's SKU is in `from` and its product has a colour whose SKU is
   in `to`. Returns { changes: [{fromName, toName, product, apply}], unmatched } */
RoomDesignerStorage.planFloorSwap = function (groups, from, to) {
  const norm = (v) => String(v || '').trim().toUpperCase();
  const fromSet = new Set((from || []).map(norm));
  const toSet = new Set((to || []).map(norm));
  const changes = [];
  let unmatched = 0;
  (groups || []).forEach((g) => (g && g.targets || []).forEach((t) => {
    const colors = (t && t.product && Array.isArray(t.product.colors)) ? t.product.colors : [];
    const cur = colors.find((c) => c.id === t.colorId);
    if (!cur || !fromSet.has(norm(cur.sku))) return;
    const next = colors.find((c) => toSet.has(norm(c.sku)) && c.id !== cur.id);
    if (!next) { unmatched++; return; }
    changes.push({ fromName: cur.name, toName: next.name, product: t.product.name, apply: () => t.apply(next.id) });
  }));
  return { changes, unmatched };
};

// Expose for the main RoomDesigner to instantiate
window.RoomDesignerStorage = RoomDesignerStorage;
window.RoomDesignerStorage.LOAD_FIT_INSET = LOAD_FIT_INSET;

